import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { DateInput } from '@/components/ui/date-input';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, Truck, CheckCircle, XCircle, Clock, AlertCircle, Hammer, 
  User, MapPin, Phone, Mail, Camera, Search,
  Filter, RefreshCw, TrendingUp, Calendar, Timer, Award, DollarSign, Image,
  Tag, CreditCard, Percent, MessageSquare, RotateCcw, History, UserCog,
  FileText, ShoppingCart, CloudUpload, Loader2, Building2, Plus, 
  Printer, Download, Users, ChevronDown, X, AlertTriangle, Send, Hash, Store, Zap,
  PackageCheck, ChevronsUpDown, Check, MapPinned, MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { ChannelSyncDialog } from '@/components/ChannelSyncDialog';
import { useSettings } from '@/context/SettingsContext';
import { ChannelPill } from '@/lib/channels';
import { COUNTRY_CODES, getDialByCode } from '@/lib/countryDialCodes';
import ImageLightbox from '@/components/ImageLightbox';
import OrderDetailPanel from '@/components/OrderDetailPanel';
import PullToRefresh from '@/components/PullToRefresh';
import { 
  ReopenOrderDialog, 
  ReturnReplacementDialog, 
  RefundDialog, 
  AuditLogDialog 
} from '@/components/OrderManagementDialogs';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// EXPRESS badge with live countdown — turns red near breach, pulses when overdue
const ExpressBadge = ({ order }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const exp = order.express;
  if (!exp?.enabled || ['delivered', 'cancelled', 'refunded', 'failed'].includes(order.fulfillment_status)) return null;
  const dl = new Date(exp.deadline_at);
  const ms = dl - Date.now();
  const overdue = ms <= 0;
  const totalMs = (exp.sla_hours || 1) * 3600000;
  const critical = !overdue && ms < Math.min(totalMs * 0.25, 2 * 3600000);
  const absMs = Math.abs(ms);
  const h = Math.floor(absMs / 3600000);
  const m = Math.floor((absMs % 3600000) / 60000);
  const label = overdue ? `OVERDUE ${h}h ${m}m` : `${h}h ${m}m left`;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${
        overdue ? 'bg-red-600 text-white border-red-600 animate-pulse'
        : critical ? 'bg-red-500/15 text-red-400 border-red-500/40'
        : 'bg-amber-500/15 text-amber-400 border-amber-500/40'}`}
      data-testid={`express-badge-${order.id}`}
      title={`Express order — deliver by ${dl.toLocaleString()}`}
    >
      <Zap className="h-3 w-3" />
      EXPRESS · {label}
    </span>
  );
};

// Order workflow tabs configuration
const orderTabs = [
  { id: 'new', label: 'New', icon: ShoppingCart, description: 'Unprocessed orders' },
  { id: 'backorder', label: 'Backorder', icon: AlertCircle, description: 'Awaiting procurement (virtual stock)' },
  { id: 'packed', label: 'Packed', icon: Package, description: 'Packing slip generated' },
  { id: 'in_production', label: 'In Production', icon: Hammer, description: 'Made-to-order items being manufactured' },
  { id: 'ready_for_dispatch', label: 'Ready For Dispatch', icon: Truck, description: 'Ready to ship' },
  { id: 'fulfilled', label: 'Fulfilled', icon: CheckCircle, description: 'Tracking/driver assigned' },
  { id: 'intransit', label: 'In Transit', icon: Truck, description: 'Picked up by courier/driver' },
  { id: 'delivered', label: 'Delivered', icon: CheckCircle, description: 'Successfully delivered' },
  { id: 'cancelled', label: 'Cancelled', icon: XCircle, description: 'Cancelled orders' },
  { id: 'delivery_failed', label: 'Delivery Failed', icon: AlertCircle, description: 'Failed deliveries' },
  { id: 'all', label: 'All', icon: Package, description: 'All orders' },
];

const statusConfig = {
  pending: { label: 'Pending', color: 'text-yellow-400', bg: 'bg-yellow-500/10', icon: Clock },
  new: { label: 'New', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: ShoppingCart },
  packed: { label: 'Packed', color: 'text-indigo-400', bg: 'bg-indigo-500/10', icon: Package },
  ready_for_dispatch: { label: 'Ready For Dispatch', color: 'text-cyan-400', bg: 'bg-cyan-500/10', icon: Package },
  in_production: { label: 'In Production', color: 'text-amber-400', bg: 'bg-amber-500/10', icon: Hammer },
  accepted: { label: 'Accepted', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: CheckCircle },
  picked: { label: 'Picked', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: Package },
  on_the_way: { label: 'On The Way', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: Truck },
  courier_assigned: { label: 'Courier Assigned', color: 'text-teal-400', bg: 'bg-teal-500/10', icon: Truck },
  booked: { label: 'Booked', color: 'text-teal-400', bg: 'bg-teal-500/10', icon: Truck },
  manifested: { label: 'Manifested', color: 'text-teal-400', bg: 'bg-teal-500/10', icon: Truck },
  picked_up: { label: 'Picked Up', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: Truck },
  in_transit: { label: 'In Transit', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: Truck },
  courier_picked_up: { label: 'In Transit', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: Truck },
  courier_in_transit: { label: 'In Transit', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: Truck },
  out_for_delivery: { label: 'Out for Delivery', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: Truck },
  courier_out_for_delivery: { label: 'Out for Delivery', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: Truck },
  delivered: { label: 'Delivered', color: 'text-green-400', bg: 'bg-green-500/10', icon: CheckCircle },
  fulfilled: { label: 'Fulfilled', color: 'text-green-400', bg: 'bg-green-500/10', icon: CheckCircle },
  failed: { label: 'Failed', color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle },
  delivery_failed: { label: 'Delivery Failed', color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle },
  rto_initiated: { label: 'RTO Initiated', color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle },
  rto_in_transit: { label: 'RTO In Transit', color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle },
  rto_delivered: { label: 'RTO Delivered', color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle },
  reopened: { label: 'Reopened', color: 'text-amber-400', bg: 'bg-amber-500/10', icon: RotateCcw },
  delivery_rescheduled: { label: 'Rescheduled', color: 'text-amber-400', bg: 'bg-amber-500/10', icon: RotateCcw },
  cancelled: { label: 'Cancelled', color: 'text-gray-400', bg: 'bg-gray-500/10', icon: XCircle },
  rto_initiated: { label: 'RTO Initiated', color: 'text-red-400', bg: 'bg-red-500/10', icon: RotateCcw },
  split: { label: 'Split', color: 'text-indigo-400', bg: 'bg-indigo-500/10', icon: Package },
};

const prodDaysIn = (startedAt) => startedAt ? Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 86400000)) : 0;
const prodOverdue = (eta) => !!eta && new Date(`${eta}T23:59:59`) < new Date();

const failureReasons = [
  'Customer not available',
  'Wrong address',
  'Customer refused delivery',
  'Product damaged',
  'Payment issue',
  'Other'
];

const OrdersPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [channelSyncOpen, setChannelSyncOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState('active'); // Default to active for drivers
  const [channelFilter, setChannelFilter] = useState('all'); // Sales-channel account filter
  const [salesChannels, setSalesChannels] = useState([]); // Connected sales channels (Amazon/Shopify/Flipkart/etc)
  const [activeTab, setActiveTab] = useState(() => {
    // honor ?tab= deep links (e.g. Dashboard At-Risk card → /orders?tab=in_production)
    const t = new URLSearchParams(window.location.search).get('tab');
    return t && orderTabs.some(x => x.id === t) ? t : 'new';
  }); // Order workflow tab
  // Per-tab counts returned by the backend (total across ALL orders, not
  // just the paginated batch currently loaded). Used for the numeric
  // badges on each tab pill so they reflect reality.
  const [serverTabCounts, setServerTabCounts] = useState(null);
  const [showDeliveredOrders, setShowDeliveredOrders] = useState(false);
  
  // Bulk selection state
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [acceptingIds, setAcceptingIds] = useState(new Set());
  const [showBulkDriverDialog, setShowBulkDriverDialog] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showBulkCourierDialog, setShowBulkCourierDialog] = useState(false);
  const [bulkOverrideDialog, setBulkOverrideDialog] = useState({ open: false, status: '', notes: '', failureReason: '', saving: false });
  const [bulkSyncing, setBulkSyncing] = useState(false);

  const handleBulkSyncShopify = async () => {
    const ids = [...selectedOrderIds];
    if (ids.length > 100) { toast.error('Maximum 100 orders per bulk sync'); return; }
    setBulkSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/orders/bulk-sync-shopify`, { order_ids: ids }, { headers: { Authorization: `Bearer ${token}` } });
      const { updated_count, skipped_count, failed_count } = res.data;
      if (updated_count > 0) toast.success(`Synced ${updated_count} order(s) from Shopify${skipped_count ? ` · ${skipped_count} skipped (not Shopify)` : ''}`);
      else if (skipped_count && !failed_count) toast.info(`No Shopify orders in selection — ${skipped_count} skipped`);
      if (failed_count > 0) toast.error(`${failed_count} order(s) failed: ${res.data.failed?.[0]?.error || ''}`);
      clearSelection();
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk Shopify sync failed');
    } finally {
      setBulkSyncing(false);
    }
  };
  
  // Pagination state
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalOrders, setTotalOrders] = useState(0);
  const PAGE_SIZE = 50;
  
  // User permissions
  const [userPermissions, setUserPermissions] = useState([]);
  
  // Dialog states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [podDialogOpen, setPodDialogOpen] = useState(false);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [cashDialogOpen, setCashDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  // New dialog states for order management
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [returnReplacementDialogOpen, setReturnReplacementDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [auditLogDialogOpen, setAuditLogDialogOpen] = useState(false);
  
  // Zoho & PO dialog states
  const [createPODialogOpen, setCreatePODialogOpen] = useState(false);
  const [poMultiOrders, setPoMultiOrders] = useState([]);
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(null);
  const [creatingPO, setCreatingPO] = useState(false);
  const [zohoStatus, setZohoStatus] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [linkedPOs, setLinkedPOs] = useState([]);
  const [vendorOpenPOs, setVendorOpenPOs] = useState([]); // Open POs for selected vendor
  const [addToExistingPO, setAddToExistingPO] = useState(false); // Toggle for adding to existing PO
  const [selectedExistingPO, setSelectedExistingPO] = useState(null); // Selected existing PO
  // Vendor cost comparison for Create-PO-from-Order dialog.
  // Shape: { [sku]: [ { vendor_id, vendor_name, cost_price, is_primary, lead_time_days, moq, last_cost, last_po_number, last_seen_at }, ... ] }
  const [vendorOptionsBySku, setVendorOptionsBySku] = useState({});
  const [stockBySku, setStockBySku] = useState({});
  const [expandedVendorRows, setExpandedVendorRows] = useState({}); // { sku: bool }
  const [loadingVendorOptions, setLoadingVendorOptions] = useState(false);
  
  // Courier booking dialog states
  const [courierDialogOpen, setCourierDialogOpen] = useState(false);
  const [bookingCourier, setBookingCourier] = useState(false);
  const [selectedCourierType, setSelectedCourierType] = useState('');
  const [courierPickupDate, setCourierPickupDate] = useState('');
  const [manualAwb, setManualAwb] = useState(''); // Manual AWB for custom couriers
  const [customCourierAwbCount, setCustomCourierAwbCount] = useState(null); // Available AWB count for selected custom courier
  const [packageDimensions, setPackageDimensions] = useState({
    length: '',
    width: '',
    height: '',
    weight: ''
  });
  const [showDimensionWarning, setShowDimensionWarning] = useState(false);
  const [packageInfo, setPackageInfo] = useState(null); // Package calculation result
  const [loadingPackageInfo, setLoadingPackageInfo] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [courierIntegrations, setCourierIntegrations] = useState([]); // Active courier integrations
  
  // Booking results dialog
  const [bookingResultsOpen, setBookingResultsOpen] = useState(false);
  const [bookingResults, setBookingResults] = useState([]);
  const [downloadingLabel, setDownloadingLabel] = useState(null);
  
  // Product search for Create Order
  const [productSearch, setProductSearch] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [activeProductIndex, setActiveProductIndex] = useState(null); // Which item is being searched
  
  // Create Order form state — structured to match Shopify's order
  // schema so the optional "Push to Shopify" toggle can ship the
  // order verbatim. Old single-string `shipping_address` /
  // `customer_name` are still derived on submit (`first_name + " " +
  // last_name`, joined address) so downstream features (driver
  // assignment, label printing, courier booking, journeys) keep
  // working without changes.
  // Create-order customer autocomplete: suggest existing customers as the
  // operator types a name / phone / email, and prefill on selection.
  const [custSuggestions, setCustSuggestions] = useState([]);
  const [custAnchor, setCustAnchor] = useState('first_name');
  const custSearchTimer = useRef(null);
  const custSearchSeq = useRef(0);

  const dismissCustSuggestions = () => {
    custSearchSeq.current += 1; // invalidate pending debounce + in-flight responses
    if (custSearchTimer.current) clearTimeout(custSearchTimer.current);
    setCustSuggestions([]);
  };

  const searchExistingCustomers = (q, anchor = 'first_name') => {
    if (custSearchTimer.current) clearTimeout(custSearchTimer.current);
    const query = (q || '').trim();
    if (query.length < 3) { setCustSuggestions([]); return; }
    setCustAnchor(anchor);
    const seq = ++custSearchSeq.current;
    custSearchTimer.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/customers/search`, {
          params: { q: query, limit: 6 },
          headers: { Authorization: `Bearer ${token}` }
        });
        if (seq !== custSearchSeq.current) return; // dismissed/superseded meanwhile
        setCustSuggestions(Array.isArray(res.data) ? res.data : []);
      } catch { if (seq === custSearchSeq.current) setCustSuggestions([]); }
    }, 300);
  };

  // Dismiss customer suggestions on Escape or any click outside the dropdown
  useEffect(() => {
    if (!custSuggestions.length) return;
    const onKey = (e) => { if (e.key === 'Escape') dismissCustSuggestions(); };
    const onClick = (e) => {
      if (!e.target.closest('[data-testid="customer-suggestions"]')) dismissCustSuggestions();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onClick);
    };
  }, [custSuggestions.length]);

  const renderCustSuggestions = (anchor) => (
    custSuggestions.length > 0 && custAnchor === anchor && (
      <div className="absolute z-50 top-full left-0 mt-1 min-w-full w-max max-w-[420px] bg-wt-paper border border-wt-border rounded-lg shadow-xl overflow-hidden" data-testid="customer-suggestions">
        <div className="flex items-center justify-between bg-wt-bg pr-1">
          <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-wt-text-muted">Existing customers</p>
          <button
            type="button"
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); dismissCustSuggestions(); }}
            className="p-1 rounded hover:bg-wt-subtle text-wt-text-muted"
            aria-label="Close suggestions"
            data-testid="customer-suggestions-close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        {custSuggestions.map((c) => (
          <button
            key={c.id}
            type="button"
            onPointerDown={(e) => { e.preventDefault(); applyExistingCustomer(c); }}
            className="w-full text-left px-3 py-2 hover:bg-wt-subtle border-t border-wt-border/50 cursor-pointer"
            data-testid={`customer-suggestion-${c.id}`}
          >
            <p className="text-sm text-wt-text-primary font-medium truncate">
              {(c.full_name || `${c.first_name || ''} ${c.last_name || ''}`).trim() || c.email || c.phone}
              {c.orders_count > 0 && <span className="ml-2 text-xs text-wt-gold">{c.orders_count} orders</span>}
            </p>
            <p className="text-xs text-wt-text-muted truncate">{[c.phone, c.email].filter(Boolean).join('  ·  ')}</p>
          </button>
        ))}
      </div>
    )
  );

  const applyExistingCustomer = (c) => {
    const full = (c.full_name || `${c.first_name || ''} ${c.last_name || ''}`).trim();
    const parts = full.split(' ');
    let phoneLocal = String(c.phone || '').replace(/[^0-9]/g, '');
    let cc = newOrderData.phone_country_code;
    const dialMatch = COUNTRY_CODES
      .slice()
      .sort((a, b) => b.dial.length - a.dial.length)
      .find(x => phoneLocal.startsWith(x.dial.replace('+', '')));
    if (dialMatch) {
      cc = dialMatch.code;
      phoneLocal = phoneLocal.slice(dialMatch.dial.replace('+', '').length);
    }
    const addr = (c.addresses || [])[0] || {};
    setNewOrderData(prev => ({
      ...prev,
      first_name: c.first_name || parts[0] || '',
      last_name: c.last_name || parts.slice(1).join(' ') || '',
      customer_email: c.email || prev.customer_email,
      phone_country_code: cc,
      phone_number: phoneLocal || prev.phone_number,
      address1: addr.address1 || prev.address1,
      address2: addr.address2 || prev.address2,
      city: addr.city || prev.city,
      state: addr.province || prev.state,
      country: addr.country || prev.country,
      country_code: (addr.country_code || prev.country_code || 'AE').toUpperCase(),
      zip: addr.zip || prev.zip,
    }));
    setCustSuggestions([]);
    toast.success(`Filled from existing customer: ${full || c.email || c.phone}`);
  };

  const [newOrderData, setNewOrderData] = useState({
    first_name: '',
    last_name: '',
    customer_email: '',
    phone_country_code: 'AE',  // ISO alpha-2; mapped to dial via COUNTRY_CODES
    phone_number: '',           // local digits without dial
    address1: '',
    address2: '',
    city: '',
    state: '',
    country: 'United Arab Emirates',
    country_code: 'AE',         // ISO alpha-2 for Shopify
    zip: '',
    payment_status: 'pending',
    payment_timing: 'received',
    payment_method_id: '',
    discount_amount: '',
    shipping_title: '',
    shipping_cost: '',
    tax: '',
    tax_inclusive: false,
    notes: '',
    push_to_shopify: false,
    order_purpose: 'new_sale',
    created_from: null,
    conversation_id: null,
    lead_id: null,
    items: [{ sku: '', title: '', quantity: 1, price: 0 }]
  });

  // Inbox → Create Order handoff: InboxPage stores a prefill payload in
  // sessionStorage and navigates here; we open the SAME create-order
  // dialog pre-filled with the conversation's customer details.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('inbox_create_order');
      if (!raw) return;
      sessionStorage.removeItem('inbox_create_order');
      const p = JSON.parse(raw);
      setNewOrderData(prev => ({
        ...prev,
        first_name: p.first_name || '',
        last_name: p.last_name || '',
        customer_email: p.customer_email || '',
        phone_country_code: p.phone_country_code || prev.phone_country_code,
        phone_number: p.phone_number || '',
        created_from: 'inbox',
        conversation_id: p.conversation_id || null,
        lead_id: p.lead_id || null,
        push_to_shopify: true,
        order_purpose: 'new_sale',
      }));
      setCreateOrderDialogOpen(true);
      toast.info('Creating order from WhatsApp conversation — you will be the Sales Owner');
    } catch (e) { /* ignore malformed payloads */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Paid-order verification & payment drafts (Jun 2026)
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [proofFile, setProofFile] = useState(null);
  const [draftLinkInfo, setDraftLinkInfo] = useState(null);
  const [draftsDialogOpen, setDraftsDialogOpen] = useState(false);
  const [orderDrafts, setOrderDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [checkingDraftId, setCheckingDraftId] = useState(null);

  useEffect(() => {
    if (!createOrderDialogOpen) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE_URL}/payment-methods`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPaymentMethods(res.data.methods || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrderDialogOpen]);

  const loadOrderDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/order-drafts`, { headers: { Authorization: `Bearer ${token}` } });
      setOrderDrafts(res.data.drafts || []);
    } catch (e) {
      toast.error('Failed to load drafts');
    } finally {
      setLoadingDrafts(false);
    }
  };

  const cancelOrderDraft = async (draftId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/order-drafts/${draftId}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Draft cancelled');
      loadOrderDrafts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel draft');
    }
  };

  const checkDraftPayment = async (draftId) => {
    setCheckingDraftId(draftId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/order-drafts/${draftId}/check-payment`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const { paid, finalized, message } = res.data;
      if (finalized) {
        toast.success(message || 'Payment received — order created');
        setSelectedDraft(null);
        loadOrderDrafts();
        loadOrders();
      } else if (paid) {
        toast.success(message || 'Payment received');
        loadOrderDrafts();
      } else {
        toast.info(message || 'Payment not received yet');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment check failed');
    } finally {
      setCheckingDraftId(null);
    }
  };
  
  // Create PO form state
  const [poFormData, setPOFormData] = useState({
    vendor_id: '',
    selected_items: [],
    po_type: 'made_to_order',
    expected_delivery_date: '',
    vendor_notes: '',
    fulfillment_method: 'vendor_delivers'
  });
  
  // Form states
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [selectedShipmentNumber, setSelectedShipmentNumber] = useState(null);  // For shipment-level operations
  const [newStatus, setNewStatus] = useState('');
  const [failureReason, setFailureReason] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [podFile, setPodFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cashCollected, setCashCollected] = useState(false);
  const [cashAmount, setCashAmount] = useState('');
  const [newTag, setNewTag] = useState('');
  const [orderTags, setOrderTags] = useState([]);
  const [statusOverrideMode, setStatusOverrideMode] = useState(false); // Manager override for status
  // Courier-status override (separate toggle nested inside the
  // override dialog). Lets a manager push the courier into RTO when
  // the Delhivery API returns no data despite the package physically
  // returning — real production case Feb 2026. When set + RTO state,
  // backend also auto-creates a return doc.
  const [overrideCourierStatus, setOverrideCourierStatus] = useState(false);
  const [overrideCourierStatusValue, setOverrideCourierStatusValue] = useState('rto_initiated');

  // Check if user is admin (legacy roles check OR is_admin flag)
  const isAdmin = user?.is_admin || user?.roles?.includes('admin');
  const isDriver = user?.roles?.includes('driver') && !isAdmin;
  
  // Permission checks - must use userPermissions directly for reactivity
  const hasPermission = useCallback((perm) => {
    if (isAdmin) return true;
    return userPermissions.includes(perm);
  }, [isAdmin, userPermissions]);
  
  // Derived permission flags - recalculate when userPermissions changes
  const canOverrideStatus = isAdmin || userPermissions?.includes('orders.override_status');
  const canCreateInvoice = isAdmin || userPermissions?.includes('orders.create_invoice');
  const canCreateOrder = isAdmin || userPermissions?.includes('orders.create');
  const canCreatePO = isAdmin || userPermissions?.includes('po.create') || userPermissions?.includes('purchase_orders.create');
  const canAssignDriver = isAdmin || userPermissions?.includes('orders.assign_driver');
  const canReassignDriver = isAdmin || userPermissions?.includes('orders.reassign_driver');
  const canUpdateStatus = isAdmin || userPermissions?.includes('orders.update_status');
  const canManageReturns = isAdmin || userPermissions?.includes('orders.manage_returns');
  const canManageRefunds = isAdmin || userPermissions?.includes('orders.manage_refunds');
  const canSyncShopify = isAdmin || userPermissions?.includes('orders.sync_shopify');
  const canCreatePaymentLink = isAdmin || userPermissions?.includes('mamopay.create_link');
  const canRequestIncentive = isAdmin || userPermissions?.includes('incentives.request_order');
  const canMarkReadyForDispatch = isAdmin || userPermissions?.includes('orders.mark_ready_for_dispatch');
  const canBookCourier = isAdmin || userPermissions?.includes('orders.book_courier') || userPermissions?.includes('orders.assign_driver');
  const canEditOrder = isAdmin || userPermissions?.includes('orders.edit_details');
  const canCancelOrder = isAdmin || userPermissions?.includes('orders.cancel');

  // Fetch user permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.id || isAdmin) return;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/users/${user.id}/permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserPermissions(response.data.permissions || []);
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
      }
    };
    fetchPermissions();
  }, [user?.id, isAdmin]);

  // Load orders on mount
  useEffect(() => {
    // Initial load will be triggered by the searchTerm useEffect
    checkZohoStatus();
    loadVendors();
    loadCourierIntegrations();
    // Set default filter based on role
    if (isDriver) {
      setStatusFilter('active');
    } else {
      setStatusFilter('all');
    }
  }, [isDriver]);

  // Load drivers when user has permission (separate effect to handle async permission loading)
  useEffect(() => {
    if (isAdmin || canAssignDriver) {
      loadDrivers();
    }
  }, [isAdmin, canAssignDriver, userPermissions]);

  // Load connected sales channels (Shopify stores, Amazon seller accounts,
  // Flipkart accounts, etc.) so the user can filter the orders list by
  // specific channel account (e.g. "Amazon India - Wooden Twist" vs
  // "Amazon India - Onlineshoppee" vs "Shopify - primary store").
  useEffect(() => {
    if (isDriver) return;
    const loadSalesChannels = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API_BASE_URL}/sales-channels`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSalesChannels(r.data?.channels || []);
      } catch (err) {
        // Non-fatal — the filter just won't show.
        console.warn('Failed to load sales channels for filter:', err);
      }
    };
    loadSalesChannels();
  }, [isDriver]);

  // Debounce search term - only update debouncedSearchTerm after user stops typing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms debounce for better typing experience

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Search orders when debouncedSearchTerm changes
  useEffect(() => {
    // Only trigger API call when debounced value changes
    if (debouncedSearchTerm !== undefined) {
      loadOrders(false, false, debouncedSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  // Reload from page 0 when the user flips tabs — the backend returns
  // orders for the selected tab, so we can't just client-filter the
  // first 50 newest (a store with lots of pending orders at the top
  // would show an empty "Delivered" tab despite having 100+ delivered
  // orders further down the timeline).
  useEffect(() => {
    loadOrders(false, false, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Partially-paid quick filter
  const [partiallyPaidOnly, setPartiallyPaidOnly] = useState(false);
  const partiallyPaidOnlyRef = useRef(false);
  const [slipFilter, setSlipFilter] = useState('all'); // all | printed | not_printed
  const [cityFilter, setCityFilter] = useState('all');
  const [expressFilter, setExpressFilter] = useState('all'); // all | express
  const [cityOptions, setCityOptions] = useState([]);
  const [cityOpen, setCityOpen] = useState(false);
  const [agentFilter, setAgentFilter] = useState('all'); // 'all' | 'driver:<name>' | 'courier:<name>'
  const [agentOptions, setAgentOptions] = useState({ drivers: [], couriers: [] });
  const [agentOpen, setAgentOpen] = useState(false);
  const slipFilterRef = useRef('all');
  const cityFilterRef = useRef('all');
  const expressFilterRef = useRef('all');
  const agentFilterRef = useRef('all');
  useEffect(() => {
    partiallyPaidOnlyRef.current = partiallyPaidOnly;
    loadOrders(false, false, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partiallyPaidOnly]);

  // Handle openOrder query parameter (from "View in Orders" links)
  useEffect(() => {
    const openOrderId = searchParams.get('openOrder');
    const openOrderNumber = searchParams.get('orderNumber');
    
    if ((openOrderId || openOrderNumber) && !loading) {
      // First check if order is already in loaded orders
      let orderToOpen = null;
      
      if (openOrderId) {
        orderToOpen = orders.find(o => o.id === openOrderId);
      } else if (openOrderNumber) {
        orderToOpen = orders.find(o => o.order_number === openOrderNumber);
      }
      
      if (orderToOpen) {
        setSelectedOrder(orderToOpen);
        setDetailPanelOpen(true);
        setStatusFilter('all');
        setSearchParams({});
      } else {
        // Order not in current page - fetch it directly
        const fetchOrder = async () => {
          try {
            const token = localStorage.getItem('token');
            let response;
            
            if (openOrderId) {
              response = await axios.get(`${API_BASE_URL}/orders/${openOrderId}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
            } else if (openOrderNumber) {
              // Search by order number
              response = await axios.get(`${API_BASE_URL}/orders?search=${encodeURIComponent(openOrderNumber)}&limit=1`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              // Extract order from search results
              if (response.data?.orders?.length > 0) {
                response.data = response.data.orders[0];
              } else if (response.data?.items?.length > 0) {
                response.data = response.data.items[0];
              } else {
                throw new Error('Order not found');
              }
            }
            
            if (response.data) {
              setSelectedOrder(response.data);
              setDetailPanelOpen(true);
              setStatusFilter('all');
              setSearchParams({});
            }
          } catch (error) {
            console.error('Failed to fetch order:', error);
            toast.error('Order not found');
            setSearchParams({});
          }
        };
        fetchOrder();
      }
    }
  }, [orders, loading, searchParams, setSearchParams]);

  const checkZohoStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/zoho/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZohoStatus(response.data);
    } catch (error) {
      console.error('Failed to check Zoho status');
    }
  };

  const loadVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/vendors?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Failed to load vendors');
    }
  };

  // Load active courier integrations
  const loadCourierIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/couriers/integrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only active integrations with valid IDs
      const activeIntegrations = (response.data.integrations || [])
        .filter(i => i.is_active && i.id && i.id.trim() !== '');
      setCourierIntegrations(activeIntegrations);
      
      // Set default selected courier to the first integration ID if not set
      if (activeIntegrations.length > 0 && !selectedCourierType) {
        setSelectedCourierType(activeIntegrations[0].id);
      }
    } catch (error) {
      console.error('Failed to load courier integrations');
    }
  };
  
  // Check AWB availability when selecting a custom courier
  useEffect(() => {
    const checkCustomCourierAwb = async () => {
      if (!selectedCourierType) {
        setCustomCourierAwbCount(null);
        return;
      }
      
      const selectedIntegration = courierIntegrations.find(i => i.id === selectedCourierType);
      if (selectedIntegration?.courier_type !== 'custom') {
        setCustomCourierAwbCount(null);
        return;
      }
      
      // Check AWB count for custom courier
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/couriers/custom-couriers/${selectedCourierType}/awb`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCustomCourierAwbCount(response.data.total_count || 0);
      } catch (error) {
        setCustomCourierAwbCount(0);
      }
    };
    
    checkCustomCourierAwb();
  }, [selectedCourierType, courierIntegrations]);

  // Create Custom Order — builds a structured payload that matches
  // Shopify's order shape so the optional "Push to Shopify" toggle
  // can hand the order off verbatim. Backend also derives the
  // legacy `customer_name` / single-string `shipping_address` for
  // downstream consumers (driver assignment, courier booking) that
  // still read those fields.
  const handleCreateOrder = async () => {
    const fullName = `${newOrderData.first_name} ${newOrderData.last_name}`.trim();
    if (!newOrderData.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!newOrderData.address1.trim() || !newOrderData.city.trim() || !newOrderData.zip.trim()) {
      toast.error('Address line 1, city, and pincode/ZIP are required');
      return;
    }
    if (!newOrderData.phone_number.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (!newOrderData.items.some(item => item.sku && item.quantity > 0)) {
      toast.error('At least one item with SKU is required');
      return;
    }

    const dial = getDialByCode(newOrderData.phone_country_code) || '+971';
    // Strip any leading + or 0 from the local digits so the result
    // is always a single, well-formed E.164 number.
    const localDigits = String(newOrderData.phone_number)
      .replace(/[^\d]/g, '')
      .replace(/^0+/, '');
    const fullPhone = `${dial}${localDigits}`;

    const shippingAddress = {
      first_name: newOrderData.first_name,
      last_name: newOrderData.last_name,
      name: fullName,
      phone: fullPhone,
      address1: newOrderData.address1,
      address2: newOrderData.address2,
      city: newOrderData.city,
      province: newOrderData.state,
      country: newOrderData.country,
      country_code: newOrderData.country_code,
      zip: newOrderData.zip,
    };

    const isPaidReceived = newOrderData.payment_status === 'paid' && newOrderData.payment_timing === 'received';
    if (isPaidReceived) {
      if (!newOrderData.payment_method_id) { toast.error('Select a payment method'); return; }
      if (!proofFile) { toast.error('Payment proof screenshot is required'); return; }
    }

    setCreatingOrder(true);
    try {
      const token = localStorage.getItem('token');
      let proofUrl = null;
      if (isPaidReceived) {
        const fd = new FormData();
        fd.append('file', proofFile);
        const up = await axios.post(`${API_BASE_URL}/orders/payment-proof/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
        proofUrl = up.data.proof_url;
      }
      const response = await axios.post(
        `${API_BASE_URL}/orders`,
        {
          customer_name: fullName,
          customer_email: newOrderData.customer_email,
          customer_phone: fullPhone,
          shipping_address: shippingAddress,
          billing_address: shippingAddress,  // same by default; parity with Shopify
          payment_status: newOrderData.payment_status,
          ...(newOrderData.payment_status === 'paid' ? { payment_timing: newOrderData.payment_timing } : {}),
          ...(proofUrl ? { payment_proof_url: proofUrl, payment_method_id: newOrderData.payment_method_id } : {}),
          discount_amount: parseFloat(newOrderData.discount_amount) || 0,
          shipping_cost: parseFloat(newOrderData.shipping_cost) || 0,
          tax: parseFloat(newOrderData.tax) || 0,
          tax_inclusive: !!newOrderData.tax_inclusive,
          ...(newOrderData.shipping_title?.trim() ? { shipping_title: newOrderData.shipping_title.trim() } : {}),
          notes: newOrderData.notes,
          push_to_shopify: newOrderData.push_to_shopify,
          order_purpose: newOrderData.order_purpose,
          ...(newOrderData.created_from ? { created_from: newOrderData.created_from } : {}),
          ...(newOrderData.conversation_id ? { conversation_id: newOrderData.conversation_id } : {}),
          ...(newOrderData.lead_id ? { lead_id: newOrderData.lead_id } : {}),
          items: newOrderData.items.filter(item => item.sku && item.quantity > 0),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.draft) {
        setDraftLinkInfo({
          order_number: response.data.order_number,
          payment_url: response.data.payment_url,
          expires_at: response.data.expires_at,
        });
        toast.success(`Draft ${response.data.order_number} saved — share the payment link with the customer`);
      } else {
        const created = response.data.order;
        const shopifyMsg = response.data.shopify
          ? response.data.shopify.success
            ? ' (also pushed to Shopify)'
            : ` (Shopify push failed: ${response.data.shopify.error || 'unknown'})`
          : '';
        toast.success(`Order ${created.order_number} created${shopifyMsg}`);
      }
      setCreateOrderDialogOpen(false);
      setNewOrderData({
        first_name: '',
        last_name: '',
        customer_email: '',
        phone_country_code: 'AE',
        phone_number: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        country: 'United Arab Emirates',
        country_code: 'AE',
        zip: '',
        payment_status: 'pending',
        payment_timing: 'received',
        payment_method_id: '',
        discount_amount: '',
        shipping_title: '',
        shipping_cost: '',
        notes: '',
        push_to_shopify: false,
        order_purpose: 'new_sale',
        created_from: null,
        conversation_id: null,
        lead_id: null,
        items: [{ sku: '', title: '', quantity: 1, price: 0 }]
      });
      setProofFile(null);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setCreatingOrder(false);
    }
  };

  const addOrderItem = () => {
    setNewOrderData(prev => ({
      ...prev,
      items: [...prev.items, { sku: '', title: '', quantity: 1, price: 0 }]
    }));
  };

  const removeOrderItem = (index) => {
    setNewOrderData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateOrderItem = (index, field, value) => {
    setNewOrderData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };
  
  // Search products from inventory
  const searchProducts = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setProductSearchResults([]);
      return;
    }
    
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/enterprise/inventory?search=${encodeURIComponent(searchTerm)}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProductSearchResults(response.data.items || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setProductSearchResults([]);
    } finally {
      setLoadingProducts(false);
    }
  };
  
  // Select a product from search results
  const selectProduct = (product, index) => {
    setNewOrderData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { 
          ...item, 
          sku: product.sku, 
          title: product.product_title || product.name,
          price: product.selling_price || 0,
          // Persist the product image so the Order Detail panel's
          // "Items" tab renders a thumbnail for custom orders. Without
          // this, custom-order line items had no `image_url` and fell
          // back to the generic Package icon.
          image_url: product.image_url || product.image || ''
        } : item
      )
    }));
    setProductSearchResults([]);
    setProductSearch('');
    setActiveProductIndex(null);
  };

  // Download shipping label for a booked shipment
  const downloadShippingLabel = async (result) => {
    if (!result?.shipment_id) {
      toast.error('Shipment ID not available');
      return;
    }
    
    setDownloadingLabel(result.order_id);
    try {
      const token = localStorage.getItem('token');
      
      // Try to download Opswarex custom label first (most reliable after booking)
      try {
        const opswarexLabelResponse = await fetch(
          `${API_BASE_URL}/couriers/shipments/${result.shipment_id}/opswarex-label`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (opswarexLabelResponse.ok) {
          const blob = await opswarexLabelResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `shipping-label-${result.order_number || result.awb || 'order'}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success('Label downloaded successfully');
          return;
        }
      } catch (opswarexError) {
        console.log('Opswarex label not available, trying other methods...');
      }
      
      // Fallback: Get shipment info and try original label
      const shipmentResponse = await axios.get(
        `${API_BASE_URL}/v1/couriers/shipments/${result.shipment_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const shipment = shipmentResponse.data;
      const labelUrl = shipment?.label_url || shipment?.pdf_url || shipment?.courier_response?.packages?.[0]?.pdf_download_url;
      
      if (labelUrl) {
        // Open label URL in new tab
        window.open(labelUrl, '_blank');
        toast.success('Label opened in new tab');
      } else {
        // Try to generate label via API
        const labelResponse = await axios.get(
          `${API_BASE_URL}/v1/couriers/shipments/${result.shipment_id}/label`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (labelResponse.data?.label_url) {
          window.open(labelResponse.data.label_url, '_blank');
          if (labelResponse.data?.portal_login_required) {
            toast.info(labelResponse.data.message || 'Please log into the courier portal to download the label');
          } else {
            toast.success('Label opened in new tab');
          }
        } else if (labelResponse.data?.message) {
          toast.info(labelResponse.data.message);
        } else {
          toast.error('Label not available yet. Please try again in a few moments.');
        }
      }
    } catch (error) {
      console.error('Label download error:', error);
      toast.error('Failed to download label. The label may still be generating.');
    } finally {
      setDownloadingLabel(null);
    }
  };

  // Download all labels from booking results
  const downloadAllLabels = async () => {
    const successfulResults = bookingResults.filter(r => r.success && r.shipment_id);
    for (const result of successfulResults) {
      await downloadShippingLabel(result);
    }
  };

  const handleCreateInvoice = async (order) => {
    setCreatingInvoice(order.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/enterprise/orders/${order.id}/create-zoho-invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invoice ${response.data.zoho_invoice_number} created in Zoho`);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(null);
    }
  };

  const openCreatePODialog = async (order) => {
    setSelectedOrder(order);
    setPoMultiOrders([]);
    // Initialize form with order items - include image_url for display
    const items = order.items?.map(item => ({
      sku: item.sku || '',
      product_title: item.title || item.name || '',
      variant_title: item.variant_title || '',
      quantity: item.quantity || 1,
      unit_cost: 0, // Will be pre-filled below from inventory
      selected: true,
      image_url: item.image_url || item.image || '' // Include image URL
    })) || [];

    // Prefill unit_cost from inventory (product-level cost_price). The
    // vendor-specific cost will refine further once the user picks a vendor.
    // Done best-effort — if the lookup fails we just leave 0 and the user
    // can enter a cost manually.
    const skus = items.map(i => i.sku).filter(Boolean);
    // Reset vendor comparison state up-front for a clean dialog open.
    setVendorOptionsBySku({});
    setStockBySku({});
    setExpandedVendorRows({});
    if (skus.length > 0) {
      try {
        const token = localStorage.getItem('token');
        const costResp = await axios.post(
          `${API_BASE_URL}/enterprise/inventory/costs-for-skus`,
          { skus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const costs = costResp.data?.costs || {};
        items.forEach(i => {
          if (i.sku && costs[i.sku]) i.unit_cost = costs[i.sku];
        });
      } catch (err) {
        console.warn('[PO] cost prefill failed:', err?.response?.data || err?.message);
      }
      // Fetch per-vendor cost matrix so admins can compare suppliers
      // inline before locking in a vendor for the PO.
      setLoadingVendorOptions(true);
      try {
        const token = localStorage.getItem('token');
        const vResp = await axios.post(
          `${API_BASE_URL}/enterprise/inventory/vendors-for-skus`,
          { skus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setVendorOptionsBySku(vResp.data?.vendors_by_sku || {});
        setStockBySku(vResp.data?.stock_by_sku || {});
      } catch (err) {
        console.warn('[PO] vendor matrix fetch failed:', err?.response?.data || err?.message);
        setVendorOptionsBySku({});
        setStockBySku({});
      } finally {
        setLoadingVendorOptions(false);
      }
    }

    setPOFormData({
      vendor_id: '',
      selected_items: items,
      po_type: 'made_to_order',
      expected_delivery_date: '',
      vendor_notes: `Order: ${order.order_number}`,
      tax_inclusive: false,
      tax_rate: 5
    });
    // Load linked POs for this order
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/orders/${order.id}/linked-pos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinkedPOs(response.data.linked_pos || []);
    } catch (error) {
      setLinkedPOs([]);
    }
    setCreatePODialogOpen(true);
  };

  // Create ONE PO from multiple selected orders (aggregates items by SKU+variant)
  const openCreatePOForOrders = async (ordersList) => {
    if (!ordersList.length) return;
    if (ordersList.length === 1) return openCreatePODialog(ordersList[0]);
    setSelectedOrder(ordersList[0]);
    setPoMultiOrders(ordersList);
    const agg = new Map();
    ordersList.forEach(o => (o.items || []).forEach(item => {
      const key = `${item.sku || ''}|${item.title || item.name || ''}|${item.variant_title || ''}`;
      const ex = agg.get(key);
      if (ex) ex.quantity += (item.quantity || 1);
      else agg.set(key, {
        sku: item.sku || '',
        product_title: item.title || item.name || '',
        variant_title: item.variant_title || '',
        quantity: item.quantity || 1,
        unit_cost: 0,
        selected: true,
        image_url: item.image_url || item.image || ''
      });
    }));
    const items = [...agg.values()];
    const skus = items.map(i => i.sku).filter(Boolean);
    setVendorOptionsBySku({});
    setStockBySku({});
    setExpandedVendorRows({});
    if (skus.length > 0) {
      try {
        const token = localStorage.getItem('token');
        const costResp = await axios.post(
          `${API_BASE_URL}/enterprise/inventory/costs-for-skus`,
          { skus }, { headers: { Authorization: `Bearer ${token}` } });
        const costs = costResp.data?.costs || {};
        items.forEach(i => { if (i.sku && costs[i.sku]) i.unit_cost = costs[i.sku]; });
      } catch { /* leave 0 — user enters manually */ }
      setLoadingVendorOptions(true);
      try {
        const token = localStorage.getItem('token');
        const vResp = await axios.post(
          `${API_BASE_URL}/enterprise/inventory/vendors-for-skus`,
          { skus }, { headers: { Authorization: `Bearer ${token}` } });
        setVendorOptionsBySku(vResp.data?.vendors_by_sku || {});
        setStockBySku(vResp.data?.stock_by_sku || {});
      } catch {
        setVendorOptionsBySku({}); setStockBySku({});
      } finally { setLoadingVendorOptions(false); }
    }
    setPOFormData({
      vendor_id: '',
      selected_items: items,
      po_type: 'made_to_order',
      expected_delivery_date: '',
      vendor_notes: `Orders: ${ordersList.map(o => o.order_number).join(', ')}`,
      tax_inclusive: false,
      tax_rate: 5
    });
    setLinkedPOs([]);
    setCreatePODialogOpen(true);
  };

  // Fetch open POs for selected vendor
  const fetchVendorOpenPOs = async (vendorId) => {    if (!vendorId) {
      setVendorOpenPOs([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/enterprise/vendors/${vendorId}/open-pos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVendorOpenPOs(response.data.open_pos || []);
    } catch (error) {
      console.error('Failed to fetch vendor open POs:', error);
      setVendorOpenPOs([]);
    }
  };

  // Handle vendor selection change
  const handleVendorChange = async (vendorId) => {
    // Find the selected vendor to get their fulfillment settings
    const selectedVendor = vendors.find(v => v.id === vendorId);
    
    // Determine fulfillment method from vendor settings
    let fulfillmentMethod = 'vendor_delivers';
    if (selectedVendor) {
      if (selectedVendor.pickup_from_vendor && !selectedVendor.ships_to_warehouse) {
        fulfillmentMethod = 'wooden_twist_pickup';
      } else if (selectedVendor.ships_to_warehouse) {
        fulfillmentMethod = 'vendor_delivers';
      }
    }
    
    setPOFormData({
      ...poFormData, 
      vendor_id: vendorId,
      fulfillment_method: fulfillmentMethod
    });
    setAddToExistingPO(false);
    setSelectedExistingPO(null);
    fetchVendorOpenPOs(vendorId);

    // Re-resolve unit costs for this vendor — vendor-specific mapping may
    // override the product-level cost that was pre-filled when the dialog
    // opened.
    const skus = (poFormData.selected_items || []).map(i => i.sku).filter(Boolean);
    if (skus.length > 0) {
      try {
        const token = localStorage.getItem('token');
        const costResp = await axios.post(
          `${API_BASE_URL}/enterprise/inventory/costs-for-skus`,
          { skus, vendor_id: vendorId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const costs = costResp.data?.costs || {};
        setPOFormData(prev => ({
          ...prev,
          selected_items: (prev.selected_items || []).map(i => (
            i.sku && costs[i.sku] ? { ...i, unit_cost: costs[i.sku] } : i
          )),
        }));
      } catch (err) {
        console.warn('[PO] vendor cost refresh failed:', err?.response?.data || err?.message);
      }
    }
  };

  const handleCreatePO = async () => {
    if (!poFormData.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    
    const selectedItems = poFormData.selected_items.filter(item => item.selected);
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    // Check if adding to existing PO
    if (addToExistingPO && selectedExistingPO) {
      await handleAddToExistingPO(selectedItems);
      return;
    }
    
    setCreatingPO(true);
    try {
      const token = localStorage.getItem('token');
      const isMulti = poMultiOrders.length > 1;
      const payload = {
        vendor_id: poFormData.vendor_id,
        selected_items: selectedItems.map(item => ({
          sku: item.sku,
          product_title: item.product_title,
          variant_title: item.variant_title,
          quantity: item.quantity,
          unit_cost: parseFloat(item.unit_cost) || 0,
          image_url: item.image_url || ''
        })),
        po_type: poFormData.po_type,
        expected_delivery_date: poFormData.expected_delivery_date || null,
        vendor_notes: poFormData.vendor_notes,
        fulfillment_method: poFormData.fulfillment_method || 'vendor_delivers',
        tax_inclusive: poFormData.tax_inclusive || false,
        tax_rate: poFormData.tax_rate || 5
      };
      const url = isMulti
        ? `${API_BASE_URL}/enterprise/orders/create-po-multi`
        : `${API_BASE_URL}/enterprise/orders/${selectedOrder.id}/create-po`;
      if (isMulti) payload.order_ids = poMultiOrders.map(o => o.id);
      const response = await axios.post(url, payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isMulti
        ? `PO ${response.data.po_number} created from ${poMultiOrders.length} orders`
        : `PO ${response.data.po_number} created successfully`);
      setCreatePODialogOpen(false);
      setPoMultiOrders([]);
      clearSelection();
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    } finally {
      setCreatingPO(false);
    }
  };

  // Add items to an existing PO
  const handleAddToExistingPO = async (selectedItems) => {
    setCreatingPO(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/enterprise/purchase-orders/${selectedExistingPO.id}/add-items`,
        {
          order_id: selectedOrder.id,
          items: selectedItems.map(item => ({
            sku: item.sku,
            product_title: item.product_title,
            variant_title: item.variant_title,
            quantity: item.quantity,
            unit_cost: parseFloat(item.unit_cost) || 0,
            image_url: item.image_url || ''
          }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Items added to ${selectedExistingPO.po_number} successfully`);
      setCreatePODialogOpen(false);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add items to PO');
    } finally {
      setCreatingPO(false);
    }
  };

  const toggleItemSelection = (index) => {
    const newItems = [...poFormData.selected_items];
    newItems[index].selected = !newItems[index].selected;
    setPOFormData({ ...poFormData, selected_items: newItems });
  };

  const updateItemQuantity = (index, quantity) => {
    const newItems = [...poFormData.selected_items];
    newItems[index].quantity = parseInt(quantity) || 1;
    setPOFormData({ ...poFormData, selected_items: newItems });
  };

  const updateItemCost = (index, cost) => {
    const newItems = [...poFormData.selected_items];
    newItems[index].unit_cost = cost;
    setPOFormData({ ...poFormData, selected_items: newItems });
  };

  const loadOrders = async (showToast = false, append = false, searchQuery = '') => {
    try {
      if (showToast) setRefreshing(true);
      if (!append) setLoading(true);
      else setLoadingMore(true);
      
      const token = localStorage.getItem('token');
      const skip = append ? orders.length : 0;
      
      // Build params with search if provided
      const params = { limit: PAGE_SIZE, skip };
      if (searchQuery) {
        params.search = searchQuery;
      }
      // Send active tab to the backend so pagination returns orders of
      // that tab (instead of only the first 50 newest regardless of
      // status — which made tabs like "Delivered" show 0 for stores
      // with any tail of older delivered orders).
      if (activeTab && activeTab !== 'all') {
        params.tab = activeTab;
      }
      if (partiallyPaidOnlyRef.current) {
        params.payment = 'partially_paid';
      }
      if (slipFilterRef.current !== 'all') {
        params.slip_printed = slipFilterRef.current === 'printed' ? 'true' : 'false';
      }
      if (cityFilterRef.current !== 'all') {
        params.city = cityFilterRef.current;
      }
      if (expressFilterRef.current === 'express') {
        params.express = 'true';
      }
      if (agentFilterRef.current !== 'all') {
        const [atype, ...aname] = agentFilterRef.current.split(':');
        if (atype === 'driver') params.driver = aname.join(':');
        else if (atype === 'courier') params.courier = aname.join(':');
      }
      
      const response = await axios.get(`${API_BASE_URL}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      const newOrders = response.data.orders || [];
      
      if (append) {
        // Deduplicate orders by id when appending (prevents UI duplicates from race conditions)
        setOrders(prev => {
          const existingIds = new Set(prev.map(o => o.id));
          const uniqueNewOrders = newOrders.filter(o => !existingIds.has(o.id));
          return [...prev, ...uniqueNewOrders];
        });
      } else {
        setOrders(newOrders);
      }
      
      setTotalOrders(response.data.total || 0);
      setHasMore(response.data.has_more || false);
      if (response.data.counts) {
        setServerTabCounts(response.data.counts);
      }
      
      if (showToast) toast.success(`Loaded ${response.data.total || 0} orders`);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  };

  // Load more orders when scrolling to bottom
  // Reload from backend when slip/city filters change (skip initial mount)
  const filtersMountedRef = useRef(false);
  useEffect(() => {
    slipFilterRef.current = slipFilter;
    cityFilterRef.current = cityFilter;
    agentFilterRef.current = agentFilter;
    expressFilterRef.current = expressFilter;
    if (filtersMountedRef.current) loadOrders(false, false, debouncedSearchTerm);
    else filtersMountedRef.current = true;
  }, [slipFilter, cityFilter, agentFilter, expressFilter]);

  // Load distinct shipping cities for the city filter
  useEffect(() => {
    if (isDriver) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE_URL}/orders/cities`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setCityOptions(r.data.cities || []))
      .catch(() => {});
    axios.get(`${API_BASE_URL}/orders/delivery-agents`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAgentOptions({ drivers: r.data.drivers || [], couriers: r.data.couriers || [] }))
      .catch(() => {});
  }, []);

  const handleBulkMarkPacked = async () => {
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_BASE_URL}/orders/bulk/mark-packed`,
        { order_ids: Array.from(selectedOrderIds) },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message || `${r.data.updated_count} order(s) marked Packed`);
      clearSelection();
      loadOrders(false, false, debouncedSearchTerm);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark packed');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const loadMoreOrders = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadOrders(false, true, debouncedSearchTerm);
    }
  }, [loadingMore, hasMore, debouncedSearchTerm]);

  // Ref for the sentinel element at the bottom of the list
  const observerTarget = useRef(null);

  // Set up IntersectionObserver for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreOrders();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadMoreOrders]);

  // Pull to refresh handler - defined before any early returns
  const handlePullRefresh = useCallback(async () => {
    await loadOrders(true);
  }, []);

  const loadDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/drivers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDrivers(response.data.drivers || []);
    } catch (error) {
      console.error('Failed to load drivers');
    }
  };

  // Calculate driver stats
  const driverStats = useMemo(() => {
    if (!isDriver) return null;
    
    const now = new Date();
    const oneDayAgo = new Date(now - 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
    
    const deliveredOrders = orders.filter(o => o.fulfillment_status === 'delivered');
    const failedOrders = orders.filter(o => o.fulfillment_status === 'failed');
    const cancelledOrders = orders.filter(o => o.fulfillment_status === 'cancelled');
    
    // Count deliveries by time period
    const deliveries24h = deliveredOrders.filter(o => {
      const deliveredAt = new Date(o.delivered_at);
      return deliveredAt >= oneDayAgo;
    }).length;
    
    const deliveries7d = deliveredOrders.filter(o => {
      const deliveredAt = new Date(o.delivered_at);
      return deliveredAt >= sevenDaysAgo;
    }).length;
    
    const deliveries30d = deliveredOrders.filter(o => {
      const deliveredAt = new Date(o.delivered_at);
      return deliveredAt >= thirtyDaysAgo;
    }).length;
    
    // Calculate average delivery time (picked to delivered)
    let avgDeliveryTime = 0;
    const ordersWithTimes = deliveredOrders.filter(o => o.picked_at && o.delivered_at);
    if (ordersWithTimes.length > 0) {
      const totalMinutes = ordersWithTimes.reduce((sum, o) => {
        const pickedAt = new Date(o.picked_at);
        const deliveredAt = new Date(o.delivered_at);
        return sum + (deliveredAt - pickedAt) / (1000 * 60);
      }, 0);
      avgDeliveryTime = Math.round(totalMinutes / ordersWithTimes.length);
    }
    
    // Active orders (not delivered/failed)
    const activeOrders = orders.filter(o => 
      !['delivered', 'failed', 'cancelled'].includes(o.fulfillment_status)
    ).length;
    
    return {
      deliveries24h,
      deliveries7d,
      deliveries30d,
      avgDeliveryTime,
      activeOrders,
      totalDelivered: deliveredOrders.length,
      totalFailed: failedOrders.length,
      successRate: deliveredOrders.length > 0 
        ? Math.round((deliveredOrders.length / (deliveredOrders.length + failedOrders.length)) * 100)
        : 100
    };
  }, [orders, isDriver]);

  // ====== Bulk Selection Handlers ======
  const toggleSelectOrder = (orderId) => {
    setSelectedOrderIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const selectAllOrders = () => {
    if (selectedOrderIds.size === filteredOrders.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(filteredOrders.map(o => o.id)));
    }
  };

  const clearSelection = () => {
    setSelectedOrderIds(new Set());
  };

  const selectedOrders = useMemo(() => {
    return orders.filter(o => selectedOrderIds.has(o.id));
  }, [orders, selectedOrderIds]);

  // ── Marketplace order acceptance (Carrefour / Danube) ──
  const orderNeedsAccept = (o) =>
    (o.source === 'carrefour' && o.carrefour?.status === 'new') ||
    (o.source === 'danube' && o.danube?.danube_status === 0);

  const acceptMarketplaceOrder = async (order) => {
    const token = localStorage.getItem('token');
    const url = order.source === 'carrefour'
      ? `${API_BASE_URL}/carrefour/orders/${order.id}/pack`
      : `${API_BASE_URL}/danube/orders/${order.id}/accept`;
    await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
  };

  const handleAcceptOrder = async (order) => {
    setAcceptingIds(prev => new Set(prev).add(order.id));
    try {
      await acceptMarketplaceOrder(order);
      toast.success(`Order ${order.order_number} accepted on ${order.source === 'carrefour' ? 'Carrefour' : 'Danube'}`);
      loadOrders(false, false, debouncedSearchTerm);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept order');
    } finally {
      setAcceptingIds(prev => { const n = new Set(prev); n.delete(order.id); return n; });
    }
  };

  const handleBulkAccept = async () => {
    const targets = selectedOrders.filter(orderNeedsAccept);
    if (!targets.length) return;
    setBulkActionLoading(true);
    let ok = 0;
    const fails = [];
    for (const o of targets) {
      try { await acceptMarketplaceOrder(o); ok++; }
      catch (e) { fails.push(`${o.order_number}: ${e.response?.data?.detail || 'failed'}`); }
    }
    if (ok) toast.success(`${ok} order(s) accepted on marketplace`);
    if (fails.length) toast.error(`${fails.length} failed — ${fails[0]}`);
    clearSelection();
    loadOrders(false, false, debouncedSearchTerm);
    setBulkActionLoading(false);
  };

  // Bulk toolbar applicability — buttons auto-hide when no selected order can use them
  const bulkApplicability = useMemo(() => {
    const sel = selectedOrders;
    const DONE = ['delivered', 'cancelled', 'refunded', 'failed'];
    const PACKABLE = ['pending', 'unfulfilled', 'processing', 'in_production', 'on_hold', null, undefined, ''];
    const DISPATCHABLE = ['pending', 'unfulfilled', 'packed', 'in_production', null, undefined, ''];
    return {
      acceptCount: sel.filter(orderNeedsAccept).length,
      anyNotDone: sel.some(o => !DONE.includes(o.fulfillment_status) && o.status !== 'cancelled'),
      anyPackable: sel.some(o => PACKABLE.includes(o.fulfillment_status)),
      anyDispatchable: sel.some(o => DISPATCHABLE.includes(o.fulfillment_status)),
      anyReadyForDispatch: sel.some(o => o.fulfillment_status === 'ready_for_dispatch'),
      anyShopify: sel.some(o => o.shopify_order_id || o.source === 'shopify'),
    };
  }, [selectedOrders]);

  // Bulk Assign Driver
  const handleBulkAssignDriver = async () => {
    if (!selectedDriverId) {
      toast.error('Please select a driver');
      return;
    }

    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const results = await Promise.allSettled(
        [...selectedOrderIds].map(orderId => 
          axios.post(
            `${API_BASE_URL}/orders/${orderId}/assign`,
            { driver_id: selectedDriverId },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      if (failed > 0) {
        toast.warning(`Assigned ${succeeded} orders, ${failed} failed`);
      } else {
        toast.success(`Assigned ${succeeded} orders to driver`);
      }
      
      setShowBulkDriverDialog(false);
      setSelectedDriverId('');
      clearSelection();
      loadOrders();
    } catch (error) {
      toast.error('Failed to assign orders');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk Print Packing Slips
  const handleBulkPackingSlips = async () => {
    if (selectedOrderIds.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/bulk/packing-slips`,
        { order_ids: [...selectedOrderIds] },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `packing-slips-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded packing slips for ${selectedOrderIds.size} orders`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate packing slips');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk Print Picking Summary
  const handleBulkPickingSummary = async () => {
    if (selectedOrderIds.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/bulk/picking-summary`,
        { order_ids: [...selectedOrderIds] },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `picking-summary-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded picking summary for ${selectedOrderIds.size} orders`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate picking summary');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk Generate Invoices
  const handleBulkInvoices = async () => {
    if (selectedOrderIds.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/bulk/invoices`,
        { order_ids: [...selectedOrderIds] },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded invoices for ${selectedOrderIds.size} orders`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoices');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk Mark Ready for Dispatch
  const handleBulkStatusOverride = async () => {
    const d = bulkOverrideDialog;
    if (!d.status) { toast.error('Select a status'); return; }
    if (!d.notes.trim()) { toast.error('Notes are required for status override'); return; }
    if (d.status === 'failed' && !d.failureReason) { toast.error('Failure reason is required'); return; }
    setBulkOverrideDialog(p => ({ ...p, saving: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/orders/bulk-status-override`, {
        order_ids: [...selectedOrderIds],
        status: d.status,
        delivery_notes: d.notes.trim(),
        failure_reason: d.status === 'failed' ? d.failureReason : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const { updated_count, failed_count } = res.data;
      if (updated_count > 0) toast.success(`Overrode status on ${updated_count} order(s)`);
      if (failed_count > 0) toast.error(`${failed_count} order(s) failed: ${res.data.failed?.[0]?.error || ''}`);
      setBulkOverrideDialog({ open: false, status: '', notes: '', failureReason: '', saving: false });
      clearSelection();
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk override failed');
      setBulkOverrideDialog(p => ({ ...p, saving: false }));
    }
  };

  const handleBulkMarkReadyForDispatch = async () => {
    if (selectedOrderIds.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      let errorCount = 0;
      
      // Update each order
      for (const orderId of selectedOrderIds) {
        try {
          await axios.put(
            `${API_BASE_URL}/orders/${orderId}/status`,
            { status: 'ready_for_dispatch' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Failed to update order ${orderId}:`, error);
        }
      }
      
      if (successCount > 0) {
        toast.success(`Marked ${successCount} order(s) as Ready for Dispatch`);
        clearSelection();
        loadOrders();
      }
      if (errorCount > 0) {
        toast.error(`Failed to update ${errorCount} order(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update orders');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk Print Shipping Labels (Opswarex branded).
  // Amazon Easy-Ship orders take a different path: we schedule the
  // pickup on Amazon first (using the seller account's default slot
  // preference) and download the Amazon-native label PDF. Non-Amazon-
  // Easy-Ship orders go through the courier `opswarex-labels` path.
  const handleBulkPrintLabels = async () => {
    if (selectedOrderIds.size === 0) return;

    const selectedList = orders.filter((o) => selectedOrderIds.has(o.id));
    const easyShipOrders = selectedList.filter(
      (o) => o.source === 'amazon' && o.source_channel_type === 'amazon_in' && (
        o.is_easyship === true ||
        (o.amazon_easyship && o.amazon_easyship.label_base64)
      )
    );
    const otherOrders = selectedList.filter((o) => !easyShipOrders.includes(o));

    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const pdfBlobs = [];
      let successCount = 0;
      const failureMessages = [];

      // --- Amazon Easy-Ship path ---
      if (easyShipOrders.length > 0) {
        try {
          const r = await axios.post(
            `${API_BASE_URL}/integrations/amazon/easyship/orders/bulk-schedule`,
            { order_ids: easyShipOrders.map((o) => o.id) },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const summary = r.data?.summary || {};
          successCount += summary.ok || 0;
          if (summary.failed) {
            const errs = (r.data?.results || [])
              .filter((x) => !x.ok)
              .slice(0, 3)
              .map((x) => `${x.order_id.slice(0, 8)}: ${x.error}`);
            failureMessages.push(
              `${summary.failed} Amazon Easy-Ship failed — ${errs.join('; ')}`
            );
          }
          const mergedB64 = r.data?.merged_labels_pdf_base64;
          if (mergedB64) {
            const byteChars = atob(mergedB64);
            const byteNumbers = new Array(byteChars.length);
            for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
            pdfBlobs.push(new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' }));
          }
        } catch (error) {
          failureMessages.push(
            `Easy-Ship bulk failed: ${error.response?.data?.detail || error.message}`
          );
        }
      }

      // --- Non-Amazon-Easy-Ship path (regular courier labels) ---
      if (otherOrders.length > 0) {
        try {
          const response = await axios.post(
            `${API_BASE_URL}/couriers/shipments/bulk/opswarex-labels`,
            { order_ids: otherOrders.map((o) => o.id), labels_per_page: 1 },
            { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
          );
          if (response.data.type === 'application/json') {
            const text = await response.data.text();
            const errorData = JSON.parse(text);
            failureMessages.push(errorData.detail || 'Failed to generate courier labels');
          } else {
            pdfBlobs.push(new Blob([response.data], { type: 'application/pdf' }));
            successCount += otherOrders.length;
          }
        } catch (error) {
          let errorMsg = 'Failed to generate shipping labels';
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorMsg = errorData.detail || errorMsg;
            } catch (e) { /* keep default */ }
          } else if (error.message) {
            errorMsg = error.message;
          }
          failureMessages.push(errorMsg);
        }
      }

      // Download whatever PDFs we collected. If >1 PDF, just download
      // them as separate files (browser concatenation via pypdf
      // happens server-side for the Easy-Ship batch, but the second
      // path is always a single already-merged PDF).
      pdfBlobs.forEach((blob, idx) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download =
          pdfBlobs.length === 1
            ? `shipping-labels-${new Date().toISOString().slice(0, 10)}.pdf`
            : `shipping-labels-${idx === 0 ? 'amazon-easyship' : 'couriers'}-${new Date()
                .toISOString()
                .slice(0, 10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      });

      if (successCount > 0) {
        toast.success(`Downloaded shipping labels for ${successCount} orders`);
      }
      failureMessages.forEach((m) => toast.error(m));
      if (successCount > 0) {
        clearSelection();
        loadOrders();
      }
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedDriverId) {
      toast.error('Please select a driver');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Check if this is a shipment-level assignment
      if (selectedShipmentNumber !== null && selectedOrder?.is_split) {
        // Get driver name from drivers list
        const driver = drivers.find(d => d.id === selectedDriverId);
        const driverName = driver?.name || driver?.email || 'Driver';
        
        await axios.post(
          `${API_BASE_URL}/orders/${selectedOrder.id}/shipments/${selectedShipmentNumber}/assign-driver`,
          { 
            driver_id: selectedDriverId,
            driver_name: driverName
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Shipment ${selectedShipmentNumber} assigned to ${driverName}`);
        // Refresh the detail panel so the updated shipment info is visible.
        try {
          const refreshed = await axios.get(
            `${API_BASE_URL}/orders/${selectedOrder.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (refreshed.data) setSelectedOrder(refreshed.data);
        } catch (_) { /* non-fatal */ }
      } else {
        // Regular order-level assignment
        const assignRes = await axios.post(
          `${API_BASE_URL}/orders/${selectedOrder.id}/assign`,
          { driver_id: selectedDriverId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Order assigned to driver');
        // Update the detail panel immediately with fresh server data so the
        // Fulfillment card shows the newly assigned driver without a reload.
        if (assignRes.data?.order) {
          setSelectedOrder(assignRes.data.order);
        }
      }
      
      setAssignDialogOpen(false);
      setSelectedDriverId('');
      setSelectedShipmentNumber(null);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign order');
    }
  };

  const handleReassignDriver = async () => {
    if (!selectedDriverId) {
      toast.error('Please select a driver');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${selectedOrder.id}/reassign`,
        { driver_id: selectedDriverId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Order reassigned successfully');
      if (response.data?.order) {
        setSelectedOrder(response.data.order);
      }
      setReassignDialogOpen(false);
      setSelectedDriverId('');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign order');
    }
  };

  // Unassign driver from order
  const markOrderReadyForDispatch = async (order) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/orders/${order.id}/status`,
        { status: 'ready_for_dispatch' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${order.order_number} marked Ready for Dispatch`);
      loadOrders(false, false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order status');
    }
  };

  const handleUnassignDriver = async (order) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/unassign`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Driver unassigned successfully');
      if (selectedOrder?.id === order.id && response.data?.order) {
        setSelectedOrder(response.data.order);
      }
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unassign driver');
    }
  };

  // Calculate package dimensions from order line items
  const calculatePackageDimensions = (order) => {
    if (!order || !order.line_items || order.line_items.length === 0) {
      return { length: '10', width: '10', height: '10', weight: '0.5', isMultiProduct: false };
    }
    
    const lineItems = order.line_items;
    const isMultiProduct = lineItems.length > 1 || lineItems.some(item => (item.quantity || 1) > 1);
    
    // Try to get dimensions from assigned packages
    let totalLength = 0;
    let totalWidth = 0;
    let totalHeight = 0;
    let totalWeight = 0;
    let hasPackageInfo = false;
    
    for (const item of lineItems) {
      const qty = item.quantity || 1;
      if (item.package_dimensions) {
        hasPackageInfo = true;
        totalLength = Math.max(totalLength, item.package_dimensions.length || 10);
        totalWidth = Math.max(totalWidth, item.package_dimensions.width || 10);
        totalHeight += (item.package_dimensions.height || 10) * qty;
        totalWeight += (item.package_weight?.value || 0.5) * qty;
      } else if (item.weight) {
        totalWeight += item.weight * qty;
      }
    }
    
    // If no package info, use defaults
    if (!hasPackageInfo) {
      totalLength = 10;
      totalWidth = 10;
      totalHeight = 10;
      totalWeight = 0.5;
    }
    
    return {
      length: totalLength.toString(),
      width: totalWidth.toString(),
      height: totalHeight.toString(),
      weight: totalWeight.toString(),
      isMultiProduct,
      hasPackageInfo
    };
  };

  // Open courier dialog with calculated dimensions from API
  const openCourierDialog = async (order) => {
    try {
      setSelectedOrder(order);
      setLoadingPackageInfo(true);
      setPackageInfo(null);
      setSelectedPackageId('');
      // Set defaults first to prevent blank fields
      setPackageDimensions({ length: '10', width: '10', height: '10', weight: '0.5' });
      setCourierDialogOpen(true);
      
      const token = localStorage.getItem('token');
      
      // Fetch package dimensions from API with error handling for each
      const [dimsResponse, packagesResponse] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/couriers/orders/${order.id}/package-dimensions`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(err => {
          console.error('Error fetching dimensions:', err);
          return { data: { dimensions: { length: 10, width: 10, height: 10, weight: 0.5 }, is_using_defaults: true } };
        }),
        axios.get(
          `${API_BASE_URL}/couriers/packages`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(err => {
          console.error('Error fetching packages:', err);
          return { data: { packages: [] } };
        })
      ]);
      
      const pkgInfo = dimsResponse.data || {};
      setPackageInfo(pkgInfo);
      setAvailablePackages(packagesResponse.data?.packages || []);
      
      // Safely access dimensions with fallbacks
      const dims = pkgInfo?.dimensions || {};
      setPackageDimensions({
        length: (dims.length || 10).toString(),
        width: (dims.width || 10).toString(),
        height: (dims.height || 10).toString(),
        weight: (dims.weight || 0.5).toString()
      });
      
      // Auto-select package if all products use the same package
      const productPackages = pkgInfo?.product_packages || [];
      if (productPackages.length > 0) {
        const uniquePackageIds = [...new Set(productPackages.map(p => p.package_id))];
        if (uniquePackageIds.length === 1 && uniquePackageIds[0]) {
          // All products use the same package - auto-select it
          setSelectedPackageId(uniquePackageIds[0]);
        }
      }
      
      setShowDimensionWarning(
        pkgInfo?.is_using_defaults || 
        (pkgInfo?.items_without_package?.length || 0) > 0 ||
        (pkgInfo?.total_items || 0) > 1
      );
    } catch (error) {
      console.error('Failed to fetch package info:', error);
      toast.error('Failed to load package information');
      // Fall back to defaults
      setPackageDimensions({ length: '10', width: '10', height: '10', weight: '0.5' });
      setShowDimensionWarning(true);
    } finally {
      setLoadingPackageInfo(false);
    }
  };

  // Apply selected package dimensions
  const applyPackageDimensions = (packageId) => {
    setSelectedPackageId(packageId);
    if (!packageId) return;
    
    const pkg = availablePackages.find(p => p.id === packageId);
    if (pkg) {
      // Convert weight to kg if stored in grams
      let weightInKg = parseFloat(pkg.weight) || 0.5;
      if (pkg.weight_unit === 'gm' || pkg.weight_unit === 'g') {
        weightInKg = weightInKg / 1000;
      }
      
      setPackageDimensions({
        length: pkg.length?.toString() || '10',
        width: pkg.width?.toString() || '10',
        height: pkg.height?.toString() || '10',
        weight: weightInKg.toString()
      });
      setShowDimensionWarning(false);
    }
  };

  // Book courier for an order
  const handleBookCourier = async () => {
    if (!selectedOrder) return;
    if (!selectedCourierType) {
      toast.error('Please select a courier service');
      return;
    }
    
    // Get courier_type from the selected integration
    const selectedIntegration = courierIntegrations.find(i => i.id === selectedCourierType);
    const courierType = selectedIntegration?.courier_type || selectedCourierType;
    
    // For custom couriers without AWBs, require manual AWB entry
    if (courierType === 'custom' && customCourierAwbCount === 0 && !manualAwb.trim()) {
      toast.error('Please enter an AWB number for this custom courier');
      return;
    }
    
    setBookingCourier(true);
    try {
      const token = localStorage.getItem('token');
      
      // Check if this is a shipment-level booking
      if (selectedShipmentNumber !== null && selectedOrder?.is_split) {
        const response = await axios.post(
          `${API_BASE_URL}/couriers/shipments/book-for-shipment`,
          {
            order_id: selectedOrder.id,
            shipment_number: selectedShipmentNumber,
            courier_type: courierType,
            integration_id: selectedCourierType, // Specific integration ID — split-order bookings honour the dropdown selection
            pickup_date: courierPickupDate || undefined,
            manual_awb: courierType === 'custom' && customCourierAwbCount === 0 ? manualAwb.trim() : undefined,
            package_dimensions: {
              length: parseFloat(packageDimensions.length) || 10,
              width: parseFloat(packageDimensions.width) || 10,
              height: parseFloat(packageDimensions.height) || 10,
              weight: parseFloat(packageDimensions.weight) || 0.5
            }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(`Shipment ${selectedShipmentNumber} booked! AWB: ${response.data.awb}`);
      } else {
        // Regular order-level booking
        const response = await axios.post(
          `${API_BASE_URL}/v1/couriers/shipments/book`,
          {
            order_ids: [selectedOrder.id],
            courier_type: courierType,
            integration_id: selectedCourierType, // Pass specific integration ID
            pickup_date: courierPickupDate || undefined,
            is_cod: selectedOrder.payment_status !== 'paid',
            cod_amount: selectedOrder.payment_status !== 'paid' ? (selectedOrder.total_amount || selectedOrder.total_price || 0) : 0,
            manual_awb: courierType === 'custom' && customCourierAwbCount === 0 ? manualAwb.trim() : undefined,
            package_dimensions: {
              length: parseFloat(packageDimensions.length) || 10,
              width: parseFloat(packageDimensions.width) || 10,
              height: parseFloat(packageDimensions.height) || 10,
              weight: parseFloat(packageDimensions.weight) || 0.5
            }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Check for partial failures in the results
        const results = response.data.results || [];
        const failedResults = results.filter(r => !r.success);
        const successResults = results.filter(r => r.success);
        
        if (failedResults.length > 0 && successResults.length === 0) {
          // All failed - show error
          toast.error(`Booking failed: ${failedResults[0]?.error}`, { duration: 10000 });
        } else if (successResults.length > 0) {
          // Some or all succeeded - show results dialog
          setBookingResults(results);
          setBookingResultsOpen(true);
        }
      }
      
      setCourierDialogOpen(false);
      setSelectedCourierType('');
      setCourierPickupDate('');
      setManualAwb(''); // Clear manual AWB
      setSelectedShipmentNumber(null);
      loadOrders();
    } catch (error) {
      const errorData = error.response?.data;
      const errorDetail = errorData?.detail;
      let errorMessage = 'Failed to book courier';
      
      // Check for results with failure details
      if (errorData?.results && Array.isArray(errorData.results)) {
        const failedResult = errorData.results.find(r => !r.success);
        if (failedResult?.error) {
          errorMessage = failedResult.error;
        }
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation error format
        errorMessage = errorDetail.map(e => {
          const field = e.loc ? e.loc.join('.') : '';
          const msg = e.msg || e.message || '';
          return field ? `${field}: ${msg}` : msg;
        }).join('\n');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      // Show error in toast with longer duration for readability
      toast.error(errorMessage, { 
        duration: 10000,
        style: { maxWidth: '500px', whiteSpace: 'pre-wrap' }
      });
      
      // Also log full error for debugging
      console.error('Courier booking error:', error.response?.data);
    } finally {
      setBookingCourier(false);
    }
  };

  // Bulk book courier for multiple orders
  const handleBulkBookCourier = async () => {
    if (selectedOrderIds.size === 0) return;
    if (!selectedCourierType) {
      toast.error('Please select a courier service');
      return;
    }
    
    // Get courier_type from the selected integration
    const selectedIntegration = courierIntegrations.find(i => i.id === selectedCourierType);
    const courierType = selectedIntegration?.courier_type || selectedCourierType;
    
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/couriers/shipments/book-bulk`,
        {
          order_ids: Array.from(selectedOrderIds),
          courier_type: courierType,
          integration_id: selectedCourierType, // Pass specific integration ID
          pickup_date: courierPickupDate || undefined,
          package_dimensions: {
            length: parseFloat(packageDimensions.length) || 10,
            width: parseFloat(packageDimensions.width) || 10,
            height: parseFloat(packageDimensions.height) || 10,
            weight: parseFloat(packageDimensions.weight) || 0.5
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { summary, success, skipped, failed } = response.data;
      
      // Build result message
      let message = `Bulk booking complete: ${summary.booked} booked`;
      if (summary.skipped > 0) message += `, ${summary.skipped} skipped`;
      if (summary.failed > 0) message += `, ${summary.failed} failed`;
      
      if (summary.booked > 0 && summary.failed === 0) {
        toast.success(message);
      } else if (summary.failed > 0) {
        // Show detailed failure reasons
        toast.error(message, { duration: 5000 });
        
        // Show each failed order with its reason
        failed?.forEach(item => {
          const orderNum = item.order_number || item.order_id;
          const reason = item.error || 'Unknown error';
          toast.error(`Order ${orderNum}: ${reason}`, { duration: 8000 });
        });
        
        // Also show skipped reasons if any
        if (skipped?.length > 0) {
          skipped.forEach(item => {
            const orderNum = item.order_number || item.order_id;
            const reason = item.reason || 'Unknown reason';
            toast.info(`Order ${orderNum}: Skipped - ${reason}`, { duration: 5000 });
          });
        }
      } else if (summary.skipped > 0) {
        toast.info(message);
        skipped?.forEach(item => {
          const orderNum = item.order_number || item.order_id;
          const reason = item.reason || 'Unknown reason';
          toast.info(`Order ${orderNum}: ${reason}`, { duration: 5000 });
        });
      }
      
      // Show details in console for debugging
      console.log('Bulk booking results:', response.data);
      
      setShowBulkCourierDialog(false);
      clearSelection();
      loadOrders();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to book courier';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      toast.error(errorMessage, { duration: 8000 });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Unassign courier from an order
  const handleUnassignCourier = async (order) => {
    if (!window.confirm(`Are you sure you want to unassign courier from order ${order.order_number}?\n\nThis will cancel the shipment and Shopify fulfillment.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/couriers/orders/${order.id}/unassign`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Courier unassigned successfully');
      loadOrders();
      // Close the detail panel if open
      if (selectedOrder?.id === order.id) {
        setSelectedOrder(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unassign courier');
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }

    if (newStatus === 'failed' && !failureReason) {
      toast.error('Please select a failure reason');
      return;
    }

    // Require notes for manager override
    if (statusOverrideMode && !deliveryNotes.trim()) {
      toast.error('Please provide a reason for the status override');
      return;
    }

    // For delivered status, require POD if not already uploaded (skip for override mode going backwards)
    if (newStatus === 'delivered' && !statusOverrideMode && !selectedOrder.proof_of_delivery_url && !podFile) {
      toast.error('Please upload Proof of Delivery before marking as delivered');
      setPodDialogOpen(true);
      setStatusDialogOpen(false);
      return;
    }

    // Check if COD order and show cash dialog (skip for override mode)
    if (newStatus === 'delivered' && !statusOverrideMode && !['paid', 'pending_verification'].includes(selectedOrder.payment_status) && !cashCollected) {
      setCashDialogOpen(true);
      setStatusDialogOpen(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // First upload POD if provided
      if (podFile) {
        const formData = new FormData();
        formData.append('file', podFile);
        await axios.post(
          `${API_BASE_URL}/orders/${selectedOrder.id}/pod/upload`,
          formData,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
      }
      
      const payload = { 
        status: newStatus, 
        failure_reason: newStatus === 'failed' ? failureReason : null,
        delivery_notes: deliveryNotes || null,
        is_override: statusOverrideMode
      };
      
      // Add cash info for COD orders (when not in override mode)
      if (newStatus === 'delivered' && !statusOverrideMode && !['paid', 'pending_verification'].includes(selectedOrder.payment_status)) {
        payload.cash_collected = cashCollected;
        payload.cash_amount = parseFloat(cashAmount) || selectedOrder.total_amount;
      }

      // Courier-status override piggy-backs on the same request when
      // the manager toggled it on inside the Override block. Only sent
      // when both override mode AND the courier toggle are active.
      if (statusOverrideMode && overrideCourierStatus && overrideCourierStatusValue) {
        payload.courier_status = overrideCourierStatusValue;
      }
      
      const response = await axios.put(
        `${API_BASE_URL}/orders/${selectedOrder.id}/status`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check if Shopify sync was attempted and display appropriate message
      const shopifySync = response.data?.shopify_sync;
      if (shopifySync) {
        if (shopifySync.success) {
          toast.success(`Status updated & synced to Shopify: ${shopifySync.message}`);
        } else {
          toast.warning(`Status updated locally. Shopify sync failed: ${shopifySync.message}`);
        }
      } else {
        toast.success(`Order status updated to ${statusConfig[newStatus]?.label}${statusOverrideMode ? ' (Manager Override)' : ''}`);
      }
      
      setStatusDialogOpen(false);
      setCashDialogOpen(false);
      setNewStatus('');
      setFailureReason('');
      setDeliveryNotes('');
      setCashCollected(false);
      setCashAmount('');
      setPodFile(null);
      setStatusOverrideMode(false);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleCashConfirmed = () => {
    setCashCollected(true);
    setCashDialogOpen(false);
    setStatusDialogOpen(true);
    // The handleUpdateStatus will be called again with cashCollected = true
    setTimeout(() => handleUpdateStatus(), 100);
  };

  const handleUploadPOD = async () => {
    if (!podFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', podFile);

      await axios.post(
        `${API_BASE_URL}/orders/${selectedOrder.id}/pod/upload`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      toast.success('Proof of delivery uploaded');
      setPodDialogOpen(false);
      setPodFile(null);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload POD');
    } finally {
      setUploading(false);
    }
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    if (orderTags.includes(newTag.trim())) {
      toast.error('Tag already exists');
      return;
    }
    setOrderTags([...orderTags, newTag.trim()]);
    setNewTag('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setOrderTags(orderTags.filter(tag => tag !== tagToRemove));
  };

  const handleSaveTags = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/orders/${selectedOrder.id}/tags`,
        { tags: orderTags },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tags updated successfully');
      setTagsDialogOpen(false);
      loadOrders();
      // Update the selected order with new tags
      setSelectedOrder({ ...selectedOrder, tags: orderTags });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tags');
    }
  };

  // Filter orders based on status
  // Helper function to determine which tab an order belongs to
  const getOrderTab = useCallback((order) => {
    // Check courier_status first (for courier-shipped orders), then fulfillment_status
    const courierStatus = order.courier_status;
    const status = order.fulfillment_status || order.status || 'pending';
    // NOTE: printing a packing slip no longer implies Packed — only the status does.
    const hasTracking = order.awb_number || order.courier_awb || order.assigned_driver_id;

    // Split parents — bucket from aggregate shipment state. Mirrors the
    // server's `split_tab` $let block in /api/orders so client and server
    // counts stay consistent. Driver-flow splits (per-shipment
    // assigned_driver_id) and courier-flow splits (per-shipment
    // awb_number / courier_awb) both land correctly here because the
    // "all_have_awb_or_driver" check accepts EITHER on each shipment.
    if (order.is_split && Array.isArray(order.shipments) && order.shipments.length > 0) {
      const ships = order.shipments;
      const inTransitStatuses = ['picked_up', 'in_transit', 'out_for_delivery',
        'picked', 'on_the_way', 'courier_picked_up',
        'courier_in_transit', 'courier_out_for_delivery'];
      const failedStatuses = ['failed', 'delivery_failed', 'rto_initiated',
        'rto_in_transit', 'rto_delivered'];

      const allDelivered = ships.every(s => s.status === 'delivered');
      const anyFailed = ships.some(s => failedStatuses.includes(s.status));
      const allCancelled = ships.every(s => s.status === 'cancelled');
      const anyInTransit = ships.some(s => inTransitStatuses.includes(s.status));
      const allHaveAwbOrDriver = ships.every(s =>
        s.awb_number || s.courier_awb || s.assigned_driver_id
      );

      if (allDelivered) return 'delivered';
      if (anyFailed) return 'delivery_failed';
      if (allCancelled) return 'cancelled';
      if (anyInTransit) return 'intransit';
      if (allHaveAwbOrDriver) return 'fulfilled';
      return 'ready_for_dispatch';
    }

    // Cancelled orders
    if (status === 'cancelled' || order.fulfillment_status === 'cancelled') {
      return 'cancelled';
    }

    // Backorder — orders waiting on procurement (virtual stock)
    if (order.is_backorder && order.fulfillment_status === 'pending_procurement') {
      return 'backorder';
    }
    
    // Delivery failed / RTO
    if (status === 'failed' || status === 'delivery_failed' || order.fulfillment_status === 'failed' ||
        courierStatus === 'failed' || courierStatus === 'rto_initiated' || courierStatus === 'rto_in_transit' || courierStatus === 'rto_delivered') {
      return 'delivery_failed';
    }
    
    // Delivered
    if (status === 'delivered' || order.fulfillment_status === 'delivered' || courierStatus === 'delivered') {
      return 'delivered';
    }
    
    // In Transit (picked up by courier/driver) - check courier_status for courier orders
    if (['picked', 'on_the_way', 'courier_picked_up', 'courier_in_transit', 'courier_out_for_delivery', 'delivery_rescheduled'].includes(status) ||
        ['picked_up', 'in_transit', 'out_for_delivery'].includes(courierStatus)) {
      return 'intransit';
    }
    
    // Fulfilled (has tracking number or driver assigned, but not yet in transit).
    // NOTE: 'accepted' counts only for the DRIVER flow (driver assigned) —
    // marketplace-accepted orders (e.g. Danube) still need packing → New tab.
    if (hasTracking || status === 'courier_assigned' || status === 'fulfilled' ||
        (status === 'accepted' && order.assigned_driver_id) ||
        status === 'shipped' ||
        courierStatus === 'booked' || courierStatus === 'manifested') {
      return 'fulfilled';
    }
    
    // In Production — made-to-order items being manufactured
    if (status === 'in_production') {
      return 'in_production';
    }
    
    // Ready for dispatch
    if (status === 'ready_for_dispatch') {
      return 'ready_for_dispatch';
    }
    
    // Packed (packing slip generated OR status is 'packed')
    // Orders with packing slip should go to Packed tab unless they're in a later stage
    if (status === 'packed') {
      return 'packed';
    }
    
    // New (unprocessed)
    return 'new';
  }, []);

  // Calculate order counts per tab. Prefer server-provided counts
  // (total across ALL orders, regardless of pagination); fall back to
  // client-side counting of the currently-loaded batch only if the
  // server hasn't responded yet or is on a pre-patch build.
  const tabCounts = useMemo(() => {
    if (serverTabCounts) {
      return {
        new: serverTabCounts.new || 0,
        packed: serverTabCounts.packed || 0,
        in_production: serverTabCounts.in_production || 0,
        ready_for_dispatch: serverTabCounts.ready_for_dispatch || 0,
        fulfilled: serverTabCounts.fulfilled || 0,
        intransit: serverTabCounts.intransit || 0,
        delivered: serverTabCounts.delivered || 0,
        cancelled: serverTabCounts.cancelled || 0,
        delivery_failed: serverTabCounts.delivery_failed || 0,
        all: serverTabCounts.all || 0,
      };
    }
    const counts = {
      new: 0,
      packed: 0,
      in_production: 0,
      ready_for_dispatch: 0,
      fulfilled: 0,
      intransit: 0,
      delivered: 0,
      cancelled: 0,
      delivery_failed: 0,
      all: orders.length
    };
    
    orders.forEach(order => {
      const tab = getOrderTab(order);
      if (counts[tab] !== undefined) {
        counts[tab]++;
      }
    });
    
    return counts;
  }, [orders, getOrderTab, serverTabCounts]);

  const [productionChip, setProductionChip] = useState('all'); // all | overdue | today | tomorrow | with_po | without_po | po_overdue
  const [etaChip, setEtaChip] = useState('all'); // all | overdue | today | tomorrow | this_week | no_eta (non-production tabs)
  const [poSummary, setPoSummary] = useState({});

  useEffect(() => {
    if (activeTab !== 'in_production') return;
    const ids = orders.filter(o => o.fulfillment_status === 'in_production').map(o => o.id);
    if (!ids.length) { setPoSummary({}); return; }
    const token = localStorage.getItem('token');
    axios.post(`${API_BASE_URL}/enterprise/orders/production/po-summary`, { order_ids: ids }, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPoSummary(res.data.summary || {}))
      .catch(() => {});
  }, [activeTab, orders]);

  // Delivery estimates for the visible order cards
  const [etaSummary, setEtaSummary] = useState({});
  useEffect(() => {
    const ids = orders
      .filter(o => !['delivered', 'cancelled', 'failed'].includes(o.fulfillment_status))
      .slice(0, 100)
      .map(o => o.id);
    if (!ids.length) { setEtaSummary({}); return; }
    const token = localStorage.getItem('token');
    axios.post(`${API_BASE_URL}/delivery/batch-estimates`, { order_ids: ids }, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setEtaSummary(res.data.estimates || {}))
      .catch(() => {});
  }, [orders]);
  const filteredOrders = useMemo(() => {
    let result = orders;
    
    // For drivers, filter out delivered/failed by default unless they toggle
    if (isDriver && statusFilter === 'active') {
      result = result.filter(order => 
        !['delivered', 'failed'].includes(order.fulfillment_status)
      );
    } else if (statusFilter !== 'all' && statusFilter !== 'active') {
      result = result.filter(order => order.fulfillment_status === statusFilter);
    }
    
    // Tab filtering is done **server-side** (we send `?tab=<active>` to
    // the API). Re-running getOrderTab() here used to silently drop
    // orders whenever the JS ladder diverged from the server's $switch
    // ladder by a single field name (e.g. fulfillment_status vs status),
    // producing the classic "Packed 22" pill with an empty list bug.
    // Split parents are now bucketed by aggregate shipment state on
    // BOTH sides, so they show up on real tabs (no more hiding).
    
    // In Production breach chips (Overdue / Due today / Due tomorrow)
    if (activeTab === 'in_production' && productionChip !== 'all') {
      const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const t0 = iso(new Date());
      const tm = new Date(); tm.setDate(tm.getDate() + 1);
      const t1 = iso(tm);
      result = result.filter(o => {
        if (productionChip === 'with_po') return (poSummary[o.id] || []).length > 0;
        if (productionChip === 'without_po') return (poSummary[o.id] || []).length === 0;
        if (productionChip === 'po_overdue') return (poSummary[o.id] || []).some(p =>
          p.expected_delivery_date && p.expected_delivery_date.slice(0, 10) < t0 &&
          !['received', 'completed', 'cancelled'].includes(p.status));
        const d = o.production_estimated_date;
        if (!d) return false;
        if (productionChip === 'overdue') return d < t0;
        if (productionChip === 'today') return d === t0;
        return d === t1;
      });
    }

    // Delivery-ETA chips on all non-production tabs (Overdue / Due today / Due tomorrow / This week / No ETA)
    if (activeTab !== 'in_production' && etaChip !== 'all') {
      const isoD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const today0 = isoD(new Date());
      const wk = new Date(); wk.setDate(wk.getDate() + 7);
      const week0 = isoD(wk);
      result = result.filter(o => {
        if (etaChip === 'rescheduled') return o.fulfillment_status === 'delivery_rescheduled';
        const est = etaSummary[o.id];
        if (etaChip === 'no_eta') return !est;
        if (!est) return false;
        if (etaChip === 'this_week') {
          const d = est.max_date || est.min_date;
          return d && d >= today0 && d <= week0;
        }
        return est.due === etaChip; // overdue | today | tomorrow
      });
    }

    // Channel filter — match against the order's linked sales-channel
    // account. "all" = every channel; "manual" = orders with no channel
    // link (manually-created orders); otherwise the channel id.
    if (channelFilter && channelFilter !== 'all') {
      const MARKETPLACE_TYPES = ['danube', 'noon', 'carrefour'];
      if (channelFilter === 'manual') {
        result = result.filter(order =>
          !order.channel_account_id && !order.source_channel_id && !order.shopify_store_id &&
          !MARKETPLACE_TYPES.includes(order.source || order.channel)
        );
      } else {
        const selectedChannel = salesChannels.find((c) => c.id === channelFilter);
        const chType = selectedChannel?.channel_type;
        result = result.filter(order =>
          order.channel_account_id === channelFilter ||
          order.source_channel_id === channelFilter ||
          order.shopify_store_id === channelFilter ||
          (chType && (order.source === chType || order.channel === chType))
        );
      }
    }

    // Search filter - use debouncedSearchTerm for filtering to avoid lag while typing
    if (slipFilter !== 'all') {
      result = result.filter(order => slipFilter === 'printed' ? !!order.packing_slip_printed : !order.packing_slip_printed);
    }
    if (cityFilter !== 'all') {
      const cf = cityFilter.trim().toLowerCase();
      result = result.filter(order => (order.shipping_address?.city || '').trim().toLowerCase() === cf);
    }
    if (agentFilter !== 'all') {
      const [atype, ...anameParts] = agentFilter.split(':');
      const aname = anameParts.join(':').trim().toLowerCase();
      if (atype === 'driver') {
        result = result.filter(order => (order.assigned_driver_name || '').trim().toLowerCase() === aname);
      } else if (atype === 'courier') {
        result = result.filter(order => (order.courier_type || '').trim().toLowerCase() === aname);
      }
    }

    if (debouncedSearchTerm) {
      const q = debouncedSearchTerm.toLowerCase();
      result = result.filter(order =>
        order.order_number?.toLowerCase().includes(q) ||
        order.customer_name?.toLowerCase().includes(q) ||
        order.customer_phone?.includes(debouncedSearchTerm) ||
        order.shopify_order_name?.toLowerCase().includes(q) ||
        String(order.shopify_order_number || '').toLowerCase().includes(q)
      );
    }
    
    return result;
  }, [orders, statusFilter, debouncedSearchTerm, isDriver, activeTab, channelFilter, salesChannels, slipFilter, cityFilter, agentFilter, productionChip, etaChip, etaSummary]);

  const [pickupSyncing, setPickupSyncing] = useState(false);
  // Production dialog: mode 'start' | 'eta'
  const [productionDialog, setProductionDialog] = useState({ open: false, order: null, mode: 'start', date: '', notes: '', saving: false, items: [], itemDays: {} });

  const openProductionDialog = async (order, mode) => {
    setProductionDialog({
      open: true, order, mode,
      date: mode === 'eta' ? (order.production_estimated_date || '') : '',
      notes: order.production_notes || '',
      saving: false, items: [], itemDays: {}
    });
    if (mode === 'start') {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/orders/${order.id}/production/prep`, { headers: { Authorization: `Bearer ${token}` } });
        setProductionDialog(p => p.order?.id === order.id ? { ...p, items: res.data.items || [] } : p);
      } catch { /* prep is best-effort */ }
    }
  };

  const handleProductionSubmit = async () => {
    const { order, mode, date, notes, itemDays } = productionDialog;
    if (mode === 'eta' && !date) { toast.error('Please pick the expected ready date'); return; }
    setProductionDialog(p => ({ ...p, saving: true }));
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (mode === 'start') {
        const product_days = {};
        Object.entries(itemDays || {}).forEach(([sku, d]) => { if (parseInt(d) > 0) product_days[sku] = parseInt(d); });
        const res = await axios.post(`${API_BASE_URL}/orders/${order.id}/production/start`, { estimated_ready_date: date || null, notes, product_days }, { headers });
        toast.success(`In production — expected ready ${res.data.estimated_ready_date}${res.data.auto_days_used ? ` (auto: ${res.data.auto_days_used} days)` : ''}`);
      } else {
        await axios.put(`${API_BASE_URL}/orders/${order.id}/production/eta`, { estimated_ready_date: date, notes }, { headers });
        toast.success('Expected ready date updated — customer tracking page reflects it');
      }
      setProductionDialog({ open: false, order: null, mode: 'start', date: '', notes: '', saving: false, items: [], itemDays: {} });
      loadOrders(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update production');
      setProductionDialog(p => ({ ...p, saving: false }));
    }
  };

  const handleProductionComplete = async (order) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/orders/${order.id}/production/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${order.order_number} moved to Ready For Dispatch`);
      loadOrders(true);
    } catch (e) {
      if (e.response?.status === 409) {
        // Linked PO still open — ask before forcing
        if (window.confirm(`${e.response.data.detail}\n\nComplete production anyway?`)) {
          try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/orders/${order.id}/production/complete?force=true`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`${order.order_number} moved to Ready For Dispatch (forced)`);
            loadOrders(true);
          } catch (e2) {
            toast.error(e2.response?.data?.detail || 'Failed to complete production');
          }
        }
        return;
      }
      toast.error(e.response?.data?.detail || 'Failed to complete production');
    }
  };

  const handlePickupBackfill = async () => {
    setPickupSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/shopify/pickup-orders/backfill`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data || {};
      if (d.marked_delivered > 0) {
        toast.success(`${d.marked_delivered} picked-up order${d.marked_delivered === 1 ? '' : 's'} moved to Delivered${d.remaining_to_scan ? ` — ${d.remaining_to_scan} left, run again` : ''}`);
      } else if (d.pickup_orders_found > 0) {
        toast.info(`Found ${d.pickup_orders_found} pickup order(s); none marked picked up on Shopify yet`);
      } else {
        toast.info(`No store-pickup orders pending in Fulfilled (${d.scanned} checked)`);
      }
      loadOrders(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pickup sync failed');
    } finally {
      setPickupSyncing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const getNextStatuses = (currentStatus) => {
    const statusFlow = {
      pending: ['ready_for_dispatch'],
      ready_for_dispatch: ['accepted'],
      accepted: ['picked'],
      picked: ['on_the_way'],
      on_the_way: ['delivered', 'failed'],
      delivered: [],
      failed: []
    };
    return statusFlow[currentStatus] || [];
  };

  // Get all available statuses for manager override (excluding current)
  const getAllStatuses = (currentStatus) => {
    const allStatuses = ['pending', 'ready_for_dispatch', 'accepted', 'picked', 'on_the_way', 'delivered', 'failed', 'reopened'];
    return allStatuses.filter(s => s !== currentStatus);
  };

  const formatMinutes = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wt-gold" />
      </div>
    );
  }

  return (
    <>
      {/* Bulk Action Toolbar - Outside PullToRefresh for proper fixed positioning */}
      {/* Hide when dialogs are open to prevent overlap */}
      {selectedOrderIds.size > 0 && !showBulkCourierDialog && !showBulkDriverDialog && !courierDialogOpen && !bulkOverrideDialog.open && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-6 z-40 bg-wt-paper border border-wt-border rounded-xl shadow-2xl p-3 sm:p-4"
          style={{ 
            left: '200px',  /* Sidebar width */
            right: '24px',
            maxWidth: 'calc(100vw - 248px)'  /* Account for sidebar + right margin */
          }}
          data-testid="bulk-action-toolbar"
        >
          <div className="flex items-center justify-center gap-3 sm:gap-4 overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-2 pr-3 sm:pr-4 border-r border-wt-border flex-shrink-0">
              <Badge className="bg-wt-gold text-black font-medium whitespace-nowrap">
                {selectedOrderIds.size} selected
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="text-wt-text-muted hover:text-wt-text-primary h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {bulkApplicability.acceptCount > 0 && (
                <Button
                  size="sm"
                  onClick={handleBulkAccept}
                  disabled={bulkActionLoading}
                  className="bg-green-600 hover:bg-green-500 text-white whitespace-nowrap"
                  data-testid="bulk-accept-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Accept ({bulkApplicability.acceptCount})</span>
                  <span className="sm:hidden">Accept {bulkApplicability.acceptCount}</span>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkPackingSlips}
                disabled={bulkActionLoading}
                className="border-wt-border text-wt-text-primary hover:bg-wt-subtle whitespace-nowrap"
              >
                <Printer className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Packing Slips</span>
                <span className="sm:hidden">Slips</span>
              </Button>
              
              {bulkApplicability.anyPackable && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkMarkPacked}
                disabled={bulkActionLoading}
                className="border-green-500/30 text-green-400 hover:bg-green-500/10 whitespace-nowrap"
                data-testid="bulk-mark-packed-btn"
              >
                <PackageCheck className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Mark Packed</span>
                <span className="sm:hidden">Pack</span>
              </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkPickingSummary}
                disabled={bulkActionLoading}
                className="border-wt-border text-wt-text-primary hover:bg-wt-subtle whitespace-nowrap"
              >
                <FileText className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Picking Summary</span>
                <span className="sm:hidden">Pick</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkInvoices}
                disabled={bulkActionLoading}
                className="border-wt-border text-wt-text-primary hover:bg-wt-subtle whitespace-nowrap"
              >
                <Download className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Invoices</span>
                <span className="sm:hidden">Inv</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkPrintLabels}
                disabled={bulkActionLoading}
                className="border-wt-gold/50 text-wt-gold hover:bg-wt-gold/10 whitespace-nowrap"
                data-testid="bulk-print-labels-btn"
              >
                <Tag className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Print Labels</span>
                <span className="sm:hidden">Labels</span>
              </Button>
              
              {canCreatePO && bulkApplicability.anyNotDone && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openCreatePOForOrders(selectedOrders)}
                  disabled={bulkActionLoading}
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 whitespace-nowrap"
                  data-testid="bulk-create-po-btn"
                >
                  <ShoppingCart className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Create PO</span>
                  <span className="sm:hidden">PO</span>
                </Button>
              )}

              {(isAdmin || canAssignDriver) && (activeTab === 'ready_for_dispatch' || bulkApplicability.anyReadyForDispatch) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkDriverDialog(true)}
                  disabled={bulkActionLoading}
                  className="border-wt-border text-wt-text-primary hover:bg-wt-subtle whitespace-nowrap"
                >
                  <Users className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Assign Driver</span>
                  <span className="sm:hidden">Driver</span>
                </Button>
              )}
              
              {/* Mark Ready for Dispatch - shown when any selected order can be dispatched */}
              {(isAdmin || canMarkReadyForDispatch) && (activeTab === 'packed' || bulkApplicability.anyDispatchable) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkMarkReadyForDispatch}
                  disabled={bulkActionLoading}
                  className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 whitespace-nowrap"
                  data-testid="bulk-mark-ready-btn"
                >
                  <Package className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Ready for Dispatch</span>
                  <span className="sm:hidden">Ready</span>
                </Button>
              )}
              
              {canBookCourier && bulkApplicability.anyNotDone && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkCourierDialog(true)}
                  disabled={bulkActionLoading}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 whitespace-nowrap"
                  data-testid="bulk-book-courier-btn"
                >
                  <Truck className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Book Courier</span>
                  <span className="sm:hidden">Courier</span>
                </Button>
              )}
              
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkOverrideDialog({ open: true, status: '', notes: '', failureReason: '', saving: false })}
                  disabled={bulkActionLoading}
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 whitespace-nowrap"
                  data-testid="bulk-override-status-btn"
                >
                  <UserCog className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Override Status</span>
                  <span className="sm:hidden">Override</span>
                </Button>
              )}
              
              {isAdmin && bulkApplicability.anyShopify && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkSyncShopify}
                  disabled={bulkActionLoading || bulkSyncing}
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 whitespace-nowrap"
                  data-testid="bulk-sync-shopify-btn"
                >
                  {bulkSyncing ? <Loader2 className="h-4 w-4 mr-1 sm:mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1 sm:mr-2" />}
                  <span className="hidden sm:inline">{bulkSyncing ? 'Syncing…' : 'Sync Shopify'}</span>
                  <span className="sm:hidden">Sync</span>
                </Button>
              )}
              
              {bulkActionLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-wt-gold flex-shrink-0" />
              )}
            </div>
          </div>
        </motion.div>
      )}
      
      <PullToRefresh onRefresh={handlePullRefresh} disabled={loading}>
      <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-playfair text-2xl sm:text-4xl font-bold text-wt-text-primary mb-1 sm:mb-2">
            {isDriver ? 'My Deliveries' : 'Orders'}
          </h1>
          <p className="text-sm sm:text-base text-wt-text-secondary">
            {isDriver ? 'Manage your assigned deliveries' : 'Manage and track all orders'}
          </p>
        </div>
        <div className="flex gap-2">
          {!isDriver && activeTab === 'fulfilled' && (
            <Button
              onClick={handlePickupBackfill}
              variant="outline"
              className="border-wt-border text-wt-text-primary hover:bg-wt-subtle text-sm"
              data-testid="pickup-backfill-btn"
              disabled={pickupSyncing}
              title="Check Shopify for store-pickup orders already picked up and move them to Delivered"
            >
              {pickupSyncing ? 'Checking…' : '🏬 Sync Pickups'}
            </Button>
          )}
          {canCreateOrder && (
            <Button
              onClick={() => { setDraftsDialogOpen(true); loadOrderDrafts(); }}
              variant="outline"
              className="border-wt-border text-wt-text-primary hover:bg-wt-subtle text-sm"
              data-testid="payment-drafts-btn"
            >
              Drafts
            </Button>
          )}
          {canCreateOrder && (
            <Button
              onClick={() => setCreateOrderDialogOpen(true)}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black text-sm"
              data-testid="open-create-order-btn"
            >
              <Plus className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Create</span> Order
            </Button>
          )}
          {!isDriver && (
            <Button
              onClick={() => setChannelSyncOpen(true)}
              variant="outline"
              className="border-wt-border text-wt-text-primary hover:bg-wt-subtle text-sm"
              data-testid="orders-sync-channels-btn"
            >
              <RefreshCw className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Sync Channels</span>
            </Button>
          )}
          <Button
            onClick={() => loadOrders(true)}
            variant="outline"
            className="border-wt-border text-wt-text-primary hover:bg-wt-subtle text-sm"
            data-testid="orders-refresh-btn"
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </Button>
        </div>
      </div>

      <ChannelSyncDialog
        open={channelSyncOpen}
        onOpenChange={setChannelSyncOpen}
        onCompleted={(r) => {
          if ((r?.total_ingested ?? 0) > 0) toast.success(`Channel sync done — ${r.total_ingested} order(s) brought in`);
          loadOrders();
        }}
      />

      {/* Driver Stats Dashboard */}
      {isDriver && driverStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Package className="h-5 w-5 text-orange-400" />
              <span className="text-xs text-wt-text-muted">Active</span>
            </div>
            <p className="text-2xl font-bold text-wt-text-primary">{driverStats.activeOrders}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-5 w-5 text-blue-400" />
              <span className="text-xs text-wt-text-muted">Last 24h</span>
            </div>
            <p className="text-2xl font-bold text-wt-text-primary">{driverStats.deliveries24h}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-5 w-5 text-purple-400" />
              <span className="text-xs text-wt-text-muted">Last 7 Days</span>
            </div>
            <p className="text-2xl font-bold text-wt-text-primary">{driverStats.deliveries7d}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-green-400" />
              <span className="text-xs text-wt-text-muted">Last 30 Days</span>
            </div>
            <p className="text-2xl font-bold text-wt-text-primary">{driverStats.deliveries30d}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Timer className="h-5 w-5 text-wt-gold" />
              <span className="text-xs text-wt-text-muted">Avg Time</span>
            </div>
            <p className="text-2xl font-bold text-wt-text-primary">
              {driverStats.avgDeliveryTime > 0 ? formatMinutes(driverStats.avgDeliveryTime) : '-'}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Award className="h-5 w-5 text-emerald-400" />
              <span className="text-xs text-wt-text-muted">Success Rate</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">{driverStats.successRate}%</p>
          </motion.div>
        </div>
      )}

      {/* Sticky header: workflow tabs + search & filters stay visible while scrolling */}
      <div className="sticky top-16 z-30 bg-wt-bg/95 backdrop-blur-md pt-1 pb-3 space-y-4" data-testid="orders-sticky-header">
      {/* Order Workflow Tabs - Only show for non-drivers */}
      {!isDriver && (
        <div className="bg-wt-surface border border-wt-border rounded-xl p-1">
          <div className="flex overflow-x-auto scrollbar-hide gap-1">
            {orderTabs.map((tab) => {
              const TabIcon = tab.icon;
              const count = tabCounts[tab.id] || 0;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSelectedOrderIds(new Set()); // Clear selection on tab change
                    setEtaChip('all');
                    setProductionChip('all');
                  }}
                  className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-wt-gold text-black shadow-md'
                      : 'text-wt-text-secondary hover:bg-wt-bg hover:text-wt-text-primary'
                  }`}
                  data-testid={`order-tab-${tab.id}`}
                >
                  <TabIcon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${isActive ? 'text-black' : ''}`} />
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <span className={`ml-0.5 px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold ${
                      isActive 
                        ? 'bg-black/20 text-black' 
                        : 'bg-wt-border text-wt-text-muted'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Search - Show for both drivers and non-drivers */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
          <Input
            placeholder="Search by order #, customer name, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary"
            data-testid="order-search-input"
          />
        </div>
        {/* Quick filter: orders with an outstanding balance */}
        {!isDriver && (
          <button
            onClick={() => setPartiallyPaidOnly(v => !v)}
            className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors ${partiallyPaidOnly ? 'border-amber-500 bg-amber-500/15 text-amber-400' : 'border-wt-border bg-wt-bg text-wt-text-secondary hover:border-amber-500/50'}`}
            data-testid="partially-paid-filter"
          >
            Partially Paid
          </button>
        )}
        {/* Channel filter — lists every connected sales-channel account
           by name (e.g. "Amazon India · Wooden Twist", "Shopify · WT UAE")
           so operators can drill into one store at a time. */}
        {!isDriver && salesChannels.length > 0 && (
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger
              className="w-64 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="order-channel-filter"
            >
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All channels" />
            </SelectTrigger>
            <SelectContent className="bg-wt-paper border-wt-border text-wt-text-primary max-h-80">
              <SelectItem value="all">All channels</SelectItem>
              <SelectItem value="manual">Manual / in-store orders</SelectItem>
              {Object.entries(
                salesChannels.reduce((acc, c) => {
                  const group = (c.country_label || c.channel_type || 'Other').toString();
                  (acc[group] = acc[group] || []).push(c);
                  return acc;
                }, {})
              ).map(([group, list]) => (
                <React.Fragment key={group}>
                  <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-wt-text-muted">
                    {group}
                  </div>
                  {list.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.display_name || c.name || c.account_name || c.channel_type}
                      {!c.is_active && (
                        <span className="ml-2 text-[10px] text-red-400">(paused)</span>
                      )}
                    </SelectItem>
                  ))}
                </React.Fragment>
              ))}
            </SelectContent>
          </Select>
        )}
        {/* Packing-slip printed filter — independent of tab */}
        {!isDriver && (
          <Select value={slipFilter} onValueChange={setSlipFilter}>
            <SelectTrigger
              className="w-44 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="order-slip-filter"
            >
              <Printer className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Slip: All" />
            </SelectTrigger>
            <SelectContent className="bg-wt-paper border-wt-border text-wt-text-primary">
              <SelectItem value="all">Slip: All</SelectItem>
              <SelectItem value="printed">Slip Printed</SelectItem>
              <SelectItem value="not_printed">Slip Not Printed</SelectItem>
            </SelectContent>
          </Select>
        )}
        {/* Express filter */}
        {!isDriver && (
          <Button
            variant="outline"
            onClick={() => setExpressFilter(expressFilter === 'express' ? 'all' : 'express')}
            className={`whitespace-nowrap ${expressFilter === 'express'
              ? 'bg-amber-500/20 border-amber-500/60 text-amber-400'
              : 'bg-wt-bg border-wt-border text-wt-text-primary'}`}
            data-testid="order-express-filter"
          >
            <Zap className="h-4 w-4 mr-2" />
            Express
          </Button>
        )}
        {/* City filter — distinct shipping cities with counts */}
        {!isDriver && cityOptions.length > 0 && (
          <Popover open={cityOpen} onOpenChange={setCityOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-48 justify-between bg-wt-bg border-wt-border text-wt-text-primary font-normal"
                data-testid="order-city-filter"
              >
                <span className="flex items-center truncate">
                  <MapPinned className="h-4 w-4 mr-2 flex-shrink-0" />
                  {cityFilter === 'all' ? 'All cities' : cityFilter}
                </span>
                <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-56" align="start">
              <Command>
                <CommandInput placeholder="Search city..." data-testid="order-city-search" />
                <CommandList className="max-h-64">
                  <CommandEmpty>No city found.</CommandEmpty>
                  <CommandItem value="all-cities" onSelect={() => { setCityFilter('all'); setCityOpen(false); }}>
                    <Check className={`mr-2 h-4 w-4 ${cityFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                    All cities
                  </CommandItem>
                  {cityOptions.map((c) => (
                    <CommandItem key={c.city} value={c.city}
                      data-testid={`city-option-${c.city}`}
                      onSelect={() => { setCityFilter(c.city); setCityOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 ${cityFilter === c.city ? 'opacity-100' : 'opacity-0'}`} />
                      <span className="flex-1 truncate">{c.city}</span>
                      <span className="text-xs text-wt-text-muted ml-2">{c.count}</span>
                    </CommandItem>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
        {/* Driver / Courier filter */}
        {!isDriver && (agentOptions.drivers.length > 0 || agentOptions.couriers.length > 0) && (
          <Popover open={agentOpen} onOpenChange={setAgentOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-52 justify-between bg-wt-bg border-wt-border text-wt-text-primary font-normal"
                data-testid="order-agent-filter"
              >
                <span className="flex items-center truncate">
                  <Truck className="h-4 w-4 mr-2 flex-shrink-0" />
                  {agentFilter === 'all' ? 'Driver / Courier' : agentFilter.split(':').slice(1).join(':')}
                </span>
                <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-60" align="start">
              <Command>
                <CommandInput placeholder="Search driver or courier..." data-testid="order-agent-search" />
                <CommandList className="max-h-72">
                  <CommandEmpty>No match found.</CommandEmpty>
                  <CommandItem value="all-agents" onSelect={() => { setAgentFilter('all'); setAgentOpen(false); }}>
                    <Check className={`mr-2 h-4 w-4 ${agentFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                    All drivers & couriers
                  </CommandItem>
                  {agentOptions.drivers.length > 0 && (
                    <CommandGroup heading="Drivers">
                      {agentOptions.drivers.map((d) => (
                        <CommandItem key={`drv-${d.name}`} value={`driver ${d.name}`}
                          data-testid={`agent-driver-${d.name}`}
                          onSelect={() => { setAgentFilter(`driver:${d.name}`); setAgentOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${agentFilter === `driver:${d.name}` ? 'opacity-100' : 'opacity-0'}`} />
                          <span className="flex-1 truncate">{d.name}</span>
                          <span className="text-xs text-wt-text-muted ml-2">{d.count}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {agentOptions.couriers.length > 0 && (
                    <CommandGroup heading="Couriers">
                      {agentOptions.couriers.map((c) => (
                        <CommandItem key={`cur-${c.name}`} value={`courier ${c.name}`}
                          data-testid={`agent-courier-${c.name}`}
                          onSelect={() => { setAgentFilter(`courier:${c.name}`); setAgentOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${agentFilter === `courier:${c.name}` ? 'opacity-100' : 'opacity-0'}`} />
                          <span className="flex-1 truncate capitalize">{c.name}</span>
                          <span className="text-xs text-wt-text-muted ml-2">{c.count}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
        {/* Status filter only for drivers */}
        {isDriver && (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-wt-bg border-wt-border text-wt-text-primary">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-wt-paper border-wt-border">
              <SelectItem value="active">Active Orders</SelectItem>
              <SelectItem value="all">All Orders</SelectItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      </div>

      {/* Tab Actions */}
      {!isDriver && activeTab !== 'all' && (
        <div className="flex items-center justify-end mb-4 px-2 min-h-[1.25rem]">
          {activeTab === 'new' && selectedOrderIds.size > 0 && (
            <span className="text-sm text-wt-gold">
              {selectedOrderIds.size} selected - Use bulk actions below
            </span>
          )}
        </div>
      )}

      {/* In Production breach chips */}
      {!isDriver && activeTab === 'in_production' && (
        <div className="flex flex-wrap gap-2 mb-2 px-2" data-testid="production-chips">
          {(() => {
            const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const t0 = iso(new Date());
            const tm = new Date(); tm.setDate(tm.getDate() + 1);
            const t1 = iso(tm);
            const prod = orders.filter(o => o.fulfillment_status === 'in_production' && o.production_estimated_date);
            const cOver = prod.filter(o => o.production_estimated_date < t0).length;
            const cToday = prod.filter(o => o.production_estimated_date === t0).length;
            const cTom = prod.filter(o => o.production_estimated_date === t1).length;
            const allProd = orders.filter(o => o.fulfillment_status === 'in_production');
            const poOverdueOf = (o) => (poSummary[o.id] || []).some(p =>
              p.expected_delivery_date && p.expected_delivery_date.slice(0, 10) < t0 &&
              !['received', 'completed', 'cancelled'].includes(p.status));
            const cWithPo = allProd.filter(o => (poSummary[o.id] || []).length > 0).length;
            const cNoPo = allProd.filter(o => (poSummary[o.id] || []).length === 0).length;
            const cPoOver = allProd.filter(poOverdueOf).length;
            const chips = [
              { id: 'all', label: `All`, cls: 'text-wt-text-secondary' },
              { id: 'overdue', label: `Overdue (${cOver})`, cls: 'text-red-400' },
              { id: 'today', label: `Due today (${cToday})`, cls: 'text-amber-400' },
              { id: 'tomorrow', label: `Due tomorrow (${cTom})`, cls: 'text-yellow-300' },
              { id: 'with_po', label: `With PO (${cWithPo})`, cls: 'text-emerald-400' },
              { id: 'without_po', label: `No PO (${cNoPo})`, cls: 'text-red-400' },
              { id: 'po_overdue', label: `PO overdue (${cPoOver})`, cls: 'text-orange-400' },
            ];
            const vendorBreakdown = productionChip === 'po_overdue' ? Object.entries(
              allProd.flatMap(o => (poSummary[o.id] || []).filter(p =>
                p.expected_delivery_date && p.expected_delivery_date.slice(0, 10) < t0 &&
                !['received', 'completed', 'cancelled'].includes(p.status)))
                .reduce((acc, p) => { const v = p.vendor_name || 'Unknown vendor'; acc[v] = (acc[v] || 0) + 1; return acc; }, {})
            ).sort((a, b) => b[1] - a[1]) : [];
            return (<>
            {chips.map(c => (
              <button
                key={c.id}
                onClick={() => setProductionChip(c.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${productionChip === c.id ? 'border-wt-gold bg-wt-gold/10 text-wt-gold' : `border-wt-border bg-wt-bg ${c.cls}`}`}
                data-testid={`production-chip-${c.id}`}
              >
                {c.label}
              </button>
            ))}
            {vendorBreakdown.length > 0 && (
              <div className="w-full flex flex-wrap items-center gap-2 mt-1 text-xs text-wt-text-muted" data-testid="po-overdue-vendor-breakdown">
                <span className="text-orange-400 font-medium">Late by vendor:</span>
                {vendorBreakdown.map(([v, n]) => (
                  <span key={v} className="px-2 py-0.5 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-300">{v} · {n}</span>
                ))}
              </div>
            )}</>);
          })()}
        </div>
      )}

      {/* Delivery-due chips for all other tabs */}
      {!isDriver && activeTab !== 'in_production' && !['cancelled', 'delivery_failed', 'delivered'].includes(activeTab) && (
        <div className="flex flex-wrap gap-2 mb-2 px-2" data-testid="eta-chips">
          {(() => {
            const isoD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const today0 = isoD(new Date());
            const wk = new Date(); wk.setDate(wk.getDate() + 7);
            const week0 = isoD(wk);
            const withEta = orders.filter(o => etaSummary[o.id]);
            const cOver = withEta.filter(o => etaSummary[o.id].due === 'overdue').length;
            const cToday = withEta.filter(o => etaSummary[o.id].due === 'today').length;
            const cTom = withEta.filter(o => etaSummary[o.id].due === 'tomorrow').length;
            const cWeek = withEta.filter(o => {
              const d = etaSummary[o.id].max_date || etaSummary[o.id].min_date;
              return d && d >= today0 && d <= week0;
            }).length;
            const cNoEta = orders.filter(o => !etaSummary[o.id]).length;
            const cResched = orders.filter(o => o.fulfillment_status === 'delivery_rescheduled').length;
            const chips = [
              { id: 'all', label: 'All', cls: 'text-wt-text-secondary' },
              { id: 'overdue', label: `Overdue (${cOver})`, cls: 'text-red-400' },
              { id: 'today', label: `Due today (${cToday})`, cls: 'text-amber-400' },
              { id: 'tomorrow', label: `Due tomorrow (${cTom})`, cls: 'text-yellow-300' },
              { id: 'this_week', label: `Due this week (${cWeek})`, cls: 'text-emerald-400' },
              { id: 'no_eta', label: `No ETA (${cNoEta})`, cls: 'text-wt-text-muted' },
              ...(cResched > 0 || etaChip === 'rescheduled'
                ? [{ id: 'rescheduled', label: `Rescheduled (${cResched})`, cls: 'text-amber-400' }]
                : []),
            ];
            return chips.map(c => (
              <button
                key={c.id}
                onClick={() => setEtaChip(c.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${etaChip === c.id ? 'border-wt-gold bg-wt-gold/10 text-wt-gold' : `border-wt-border bg-wt-bg ${c.cls}`}`}
                data-testid={`eta-chip-${c.id}`}
              >
                {c.label}
              </button>
            ));
          })()}
        </div>
      )}

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="bg-wt-paper border border-wt-border rounded-xl p-12 text-center">
          {(() => {
            const currentTab = orderTabs.find(t => t.id === activeTab);
            const TabIcon = currentTab?.icon || Package;
            return (
              <>
                <TabIcon className="h-16 w-16 mx-auto mb-4 text-wt-text-muted opacity-30" />
                <h3 className="text-xl font-semibold text-wt-text-primary mb-2">
                  {isDriver && statusFilter === 'active' 
                    ? 'No Active Deliveries' 
                    : `No ${currentTab?.label || ''} Orders`}
                </h3>
                <p className="text-wt-text-secondary">
                  {isDriver && statusFilter === 'active'
                    ? 'All your deliveries are completed! Check "All Orders" to see history.'
                    : debouncedSearchTerm 
                      ? 'Try adjusting your search' 
                      : activeTab === 'new' 
                        ? 'Sync orders from Shopify or create new orders'
                        : activeTab === 'packed'
                          ? 'Generate packing slips for new orders to move them here'
                          : activeTab === 'ready_for_dispatch'
                            ? 'Mark packed orders as ready for dispatch'
                            : activeTab === 'fulfilled'
                              ? 'Assign driver or book courier to move orders here'
                              : activeTab === 'intransit'
                                ? 'Orders will appear here once picked up'
                                : activeTab === 'delivered'
                                  ? 'Delivered orders will appear here'
                                  : 'No orders in this category'}
                </p>
                {isDriver && statusFilter === 'active' && (
                  <Button
                    onClick={() => setStatusFilter('all')}
                    className="mt-4 bg-wt-subtle hover:bg-wt-border text-wt-text-primary"
                  >
                    View All Orders
                  </Button>
                )}
                {!isDriver && activeTab !== 'all' && tabCounts.all > 0 && (
                  <Button
                    onClick={() => { setActiveTab('all'); setEtaChip('all'); setProductionChip('all'); }}
                    className="mt-4 bg-wt-subtle hover:bg-wt-border text-wt-text-primary"
                  >
                    View All Orders ({tabCounts.all})
                  </Button>
                )}
              </>
            );
          })()}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Select All Header */}
          {!isDriver && filteredOrders.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-2 bg-wt-surface border border-wt-border rounded-lg">
              <Checkbox
                id="select-all"
                checked={selectedOrderIds.size === filteredOrders.length && filteredOrders.length > 0}
                onCheckedChange={selectAllOrders}
                className="border-wt-border"
              />
              <label htmlFor="select-all" className="text-sm text-wt-text-secondary cursor-pointer">
                Select all {filteredOrders.length} orders
              </label>
            </div>
          )}
          
          {filteredOrders.map((order, index) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index, 10) * 0.05 }}
              className={`bg-wt-paper border rounded-xl p-4 sm:p-6 hover:border-wt-gold/30 transition-colors ${
                selectedOrderIds.has(order.id) ? 'border-wt-gold' : 'border-wt-border'
              }`}
              data-testid={`order-${order.id}`}
            >
              <div className="flex items-start justify-between gap-3 sm:gap-4 flex-col sm:flex-row">
                {/* Row 1: Checkbox + main content block */}
                <div className="flex items-start gap-3 w-full sm:w-auto sm:flex-1 min-w-0">
                  {/* Checkbox */}
                  {!isDriver && (
                    <Checkbox
                      checked={selectedOrderIds.has(order.id)}
                      onCheckedChange={() => toggleSelectOrder(order.id)}
                      className="mt-1 border-wt-border"
                      data-testid={`order-checkbox-${order.id}`}
                    />
                  )}

                  <div className="flex-1 min-w-0">
                  {/* Order Header */}
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <h3 className="text-lg font-semibold text-wt-text-primary">
                      {order.order_number || `#${order.id?.slice(-8) || 'Unknown'}`}
                    </h3>
                    {/* POS orders: also surface the Shopify order number so staff
                        don't need to open the detail panel to cross-reference */}
                    {order.shopify_order_name && order.shopify_order_name !== order.order_number && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#95BF47]/10 text-[#a8d05e] border border-[#95BF47]/30"
                        data-testid={`shopify-order-name-${order.id}`}
                        title="Shopify order number"
                      >
                        Shopify {order.shopify_order_name}
                      </span>
                    )}
                    {/* Sales channel pill — quickly tells warehouse staff
                        which marketplace SLAs apply (Amazon ship-by,
                        Flipkart pickup window, Shopify webhook hooks, etc.).
                        Theme/colour come from /app/frontend/src/lib/channels.js
                        — single source of truth for channel rendering across
                        the entire app. */}
                    <ChannelPill order={order} />
                    <ExpressBadge order={order} />
                    {order.has_marketplace_vendor_items && (
                      <Badge
                        className="bg-violet-500/20 text-violet-300 border border-violet-500/40 text-xs px-2 py-0.5"
                        data-testid={`order-mp-vendor-pill-${order.id}`}
                        title="This order contains items fulfilled by a marketplace vendor"
                      >
                        <Store className="w-3 h-3 mr-1" />
                        Vendor
                      </Badge>
                    )}
                    {/* Split badge — tells warehouse staff at a glance
                        that this order ships in multiple packages. The
                        tooltip breaks down each shipment's current
                        status so they don't need to open the detail
                        panel for routine "is everything booked?" checks.
                        Driver flow + courier flow both render here
                        because both populate `shipments[*].status`. */}
                    {order.is_split && Array.isArray(order.shipments) && order.shipments.length > 0 && (() => {
                      const ships = order.shipments;
                      const counts = ships.reduce((acc, s) => {
                        const k = s.status || 'pending';
                        acc[k] = (acc[k] || 0) + 1;
                        return acc;
                      }, {});
                      const tooltip = ships
                        .map((s, idx) => {
                          const num = s.shipment_number ?? idx + 1;
                          const stat = (s.status || 'pending').replace(/_/g, ' ');
                          const awb = s.awb_number || s.courier_awb;
                          const drv = s.assigned_driver_name || s.assigned_driver_id;
                          const tail = awb ? ` · AWB ${awb}` : drv ? ` · ${drv}` : '';
                          return `Shipment ${num}: ${stat}${tail}`;
                        })
                        .join('\n');
                      // Tone: green if all delivered, amber if any failed,
                      // indigo otherwise (in-flight / mixed).
                      const allDelivered = ships.every(s => s.status === 'delivered');
                      const anyFailed = ships.some(s => ['failed', 'delivery_failed', 'rto_initiated', 'rto_in_transit', 'rto_delivered'].includes(s.status));
                      const tone = allDelivered
                        ? 'bg-green-500/10 text-green-300 border-green-500/30'
                        : anyFailed
                          ? 'bg-red-500/15 text-red-300 border-red-500/30'
                          : 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30';
                      return (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium border ${tone}`}
                          data-testid="split-shipments-badge"
                          title={tooltip}
                        >
                          Split · {ships.length} shipments
                          {counts.delivered ? ` · ${counts.delivered}/${ships.length} delivered` : ''}
                        </span>
                      );
                    })()}
                    {order.is_store_pickup && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-300 border border-cyan-500/30"
                        data-testid={`pickup-pill-${order.id}`}
                        title={order.fulfillment_status === 'delivered' ? 'Store pickup — collected by customer' : order.pickup_ready ? 'Store pickup — ready, awaiting customer' : 'Store pickup order'}
                      >
                        🏬 Pickup{order.pickup_ready && order.fulfillment_status !== 'delivered' ? ' · Ready' : ''}
                      </span>
                    )}
                    {order.shopify_edit_alert && !order.shopify_edit_alert.acknowledged && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-300 border border-red-500/40"
                        data-testid={`edited-after-packing-badge-${order.id}`}
                        title={(order.shopify_edit_alert.changes || []).join('\n')}
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Edited — recheck items
                      </span>
                    )}
                    {/* Driver-assigned orders were showing "Pending" (backend keeps
                        fulfillment_status=pending until the driver accepts). Show the
                        driver assignment + name instead so staff can identify at a glance. */}
                    {order.assigned_driver_id && !order.courier_status && ['pending', 'accepted', 'assigned'].includes(order.fulfillment_status || 'pending') ? (
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-300 border border-indigo-500/30"
                        data-testid={`driver-assigned-badge-${order.id}`}
                        title={`Assigned to ${order.assigned_driver_name || 'driver'}`}
                      >
                        <Truck className="h-3 w-3" />
                        {order.fulfillment_status === 'accepted' ? 'Accepted' : 'Assigned'} · {order.assigned_driver_name || 'Driver'}
                      </span>
                    ) : (
                      <>
                        {getStatusBadge(order.courier_status || order.fulfillment_status)}
                        {order.assigned_driver_name && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-300"
                            data-testid={`driver-name-pill-${order.id}`}
                            title="Assigned driver"
                          >
                            <Truck className="h-3 w-3" />
                            {order.assigned_driver_name}
                          </span>
                        )}
                      </>
                    )}
                    {order.packing_slip_printed && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 inline-flex items-center gap-1"
                        data-testid={`slip-printed-pill-${order.id}`}
                        title={`Packing slip printed${order.packing_slip_printed_by_name ? ` by ${order.packing_slip_printed_by_name}` : ''}${order.packing_slip_printed_at ? ` on ${new Date(order.packing_slip_printed_at).toLocaleString()}` : ''}`}
                      >
                        <Printer className="h-3 w-3" />
                        Slip Printed
                      </span>
                    )}
                    {order.is_backorder && order.fulfillment_status === 'pending_procurement' && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-300 border border-amber-500/30"
                        data-testid="backorder-pill"
                        title="Awaiting procurement — auto-fulfills when stock arrives"
                      >
                        Backorder
                      </span>
                    )}
                    {order.delivery_location?.lat && order.delivery_location?.lng && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/30 inline-flex items-center gap-1"
                        data-testid={`location-pin-pill-${order.id}`}
                        title={order.delivery_location.source === 'customer_pin' ? 'Location pin shared by customer' : 'Location pin added by team'}
                      >
                        <MapPin className="h-3 w-3" />
                        Pin {order.delivery_location.source === 'customer_pin' ? '· Customer' : '· Team'}
                      </span>
                    )}
                    {order.payment_status === 'paid' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400">
                        Paid
                      </span>
                    )}
                    {order.payment_status === 'pending_verification' && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${order.payment_verification?.status === 'rejected' ? 'bg-red-500/10 text-red-400' : 'bg-purple-500/10 text-purple-400'}`} data-testid={`pending-verification-pill-${order.order_number}`}>
                        {order.payment_verification?.status === 'rejected' ? 'Proof Rejected' : 'Pending Verification'}
                      </span>
                    )}
                    {/* Amazon-only Ship-by commitment — missing this
                        date hurts Late Shipment Rate. Red if overdue,
                        amber if <24h, neutral otherwise. */}
                    {order.source === 'amazon' && order.latest_ship_date && (() => {
                      const shipBy = new Date(order.latest_ship_date);
                      const now = new Date();
                      const hoursLeft = (shipBy - now) / (1000 * 60 * 60);
                      const overdue = hoursLeft < 0;
                      const urgent = !overdue && hoursLeft < 24;
                      const tone = overdue
                        ? 'bg-red-500/15 text-red-300 border-red-500/30'
                        : urgent
                          ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                          : 'bg-[#FF9900]/10 text-[#FFAC31] border-[#FF9900]/30';
                      const label = shipBy.toLocaleDateString('en-GB', {
                        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                        timeZone: 'Asia/Kolkata',
                      });
                      return (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium border ${tone}`}
                          data-testid="amazon-ship-by-badge"
                          title={`Amazon ${overdue ? 'LATE — shipment overdue' : 'Ship-by commitment window'}`}
                        >
                          {overdue ? 'Late · ' : 'Ship by: '}{label} IST
                        </span>
                      );
                    })()}
                  </div>
                  
                  {/* Order Item Images */}
                  {order.items && order.items.length > 0 && (
                    <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1">
                      {order.items.slice(0, 4).map((item, idx) => (
                        <div 
                          key={idx} 
                          className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg border border-wt-border overflow-hidden bg-wt-bg"
                          title={item.title || item.name}
                        >
                          {item.image_url || item.image ? (
                            <img 
                              src={item.image_url || item.image} 
                              alt={item.title || item.name || 'Product'}
                              className="w-full h-full object-cover cursor-zoom-in hover:opacity-80 transition-opacity"
                              data-testid={`order-item-image-${order.id}-${idx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setImagePreview({ url: item.image_url || item.image, title: item.title || item.name || 'Product' });
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={`w-full h-full flex items-center justify-center text-wt-text-muted ${item.image_url || item.image ? 'hidden' : ''}`}>
                            <Image className="w-5 h-5" />
                          </div>
                        </div>
                      ))}
                      {order.items.length > 4 && (
                        <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg border border-wt-border bg-wt-subtle flex items-center justify-center">
                          <span className="text-xs text-wt-text-muted font-medium">+{order.items.length - 4}</span>
                        </div>
                      )}
                      <span className="text-xs text-wt-text-muted ml-1 whitespace-nowrap">
                        {order.items.length} item{order.items.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                  
                  {/* Customer Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-wt-text-secondary">
                      <User className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{order.customer_name || order.customer_email?.split('@')[0] || 'Customer'}</span>
                    </div>
                    {order.customer_phone && (
                      <div className="flex items-center gap-2 text-wt-text-secondary">
                        <Phone className="h-4 w-4 flex-shrink-0" />
                        <span>{order.customer_phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-wt-text-secondary">
                      <MapPin className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">
                        {typeof order.shipping_address === 'object' 
                          ? [order.shipping_address?.city, order.shipping_address?.country].filter(Boolean).join(', ')
                          : order.shipping_address}
                      </span>
                    </div>
                  </div>

                  {/* Order date + delivery estimate */}
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid={`order-dates-${order.id}`}>
                    {order.created_at && (
                      <span className="inline-flex items-center gap-1.5 text-wt-text-muted">
                        <Calendar className="h-3.5 w-3.5" />
                        Ordered {new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    {etaSummary[order.id] && (() => {
                      const est = etaSummary[order.id];
                      const fmt = (s) => new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                      const promised = est.basis === 'rescheduled';
                      const dueBadge = {
                        overdue: { label: 'Overdue', cls: 'border-red-500/50 bg-red-500/10 text-red-400' },
                        today: { label: 'Due today', cls: 'border-amber-500/50 bg-amber-500/10 text-amber-400' },
                        tomorrow: { label: 'Due tomorrow', cls: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300' },
                      }[est.due];
                      return (
                        <>
                          <span className={`inline-flex items-center gap-1.5 ${promised ? 'text-wt-gold font-semibold' : 'text-wt-text-secondary'}`}>
                            <Truck className="h-3.5 w-3.5 text-wt-gold" />
                            {promised
                              ? `Promised ${fmt(est.min_date)}`
                              : `Est. delivery ${fmt(est.min_date)}${est.max_date !== est.min_date ? ` – ${fmt(est.max_date)}` : ''}`}
                          </span>
                          {dueBadge && (
                            <span className={`px-2 py-0.5 rounded-full border font-semibold ${dueBadge.cls}`} data-testid={`due-badge-${order.id}`}>
                              {dueBadge.label}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Driver Info */}
                  {!isDriver && order.assigned_driver_name && (
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <Truck className="h-4 w-4 text-wt-gold" />
                      <span className="text-wt-text-secondary">Driver:</span>
                      <span className="text-wt-text-primary font-medium">{order.assigned_driver_name}</span>
                    </div>
                  )}

                  {/* Production info */}
                  {order.fulfillment_status === 'in_production' && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm" data-testid={`production-info-${order.id}`}>
                      <span className="inline-flex items-center gap-1.5 text-amber-400 font-medium">
                        <Hammer className="h-4 w-4" />
                        In production · {prodDaysIn(order.production_started_at)} day{prodDaysIn(order.production_started_at) === 1 ? '' : 's'}{' '}
                      </span>
                      {order.production_estimated_date && (
                        <span className={prodOverdue(order.production_estimated_date) ? 'text-red-400 font-semibold' : 'text-wt-text-secondary'}>
                          {' '}· Expected ready {order.production_estimated_date}{prodOverdue(order.production_estimated_date) ? ' — OVERDUE' : ''}
                        </span>
                      )}
                      {order.production_notes && (
                        <span className="text-wt-text-muted"> · {order.production_notes}</span>
                      )}
                    </div>
                  )}

                  {/* Linked PO info (production tab) */}
                  {order.fulfillment_status === 'in_production' && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid={`production-po-${order.id}`}>
                      {(poSummary[order.id] || []).length > 0 ? (() => {
                        const pos = poSummary[order.id];
                        const doneCount = pos.filter(p => ['received', 'completed'].includes(p.status)).length;
                        const poStatusCls = (s) => ({
                          draft: 'border-wt-border text-wt-text-muted',
                          sent: 'border-blue-500/40 text-blue-400',
                          confirmed: 'border-cyan-500/40 text-cyan-400',
                          partially_received: 'border-amber-500/40 text-amber-400',
                          received: 'border-emerald-500/40 text-emerald-400',
                          completed: 'border-emerald-500/40 text-emerald-400',
                          cancelled: 'border-red-500/40 text-red-400',
                        }[s] || 'border-wt-border text-wt-text-muted');
                        return (<>
                          {pos.map(po => (
                            <button
                              key={po.id}
                              onClick={(e) => { e.stopPropagation(); window.open(`/purchase-orders?po=${po.id}`, '_blank'); }}
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border bg-wt-bg hover:border-wt-gold/60 transition-colors ${poStatusCls(po.status)}`}
                              data-testid={`po-chip-${po.id}`}
                              title={`Open ${po.po_number}`}
                            >
                              <span className="text-wt-text-secondary font-medium">{po.po_number}</span>
                              <span className="text-wt-text-muted">· {po.vendor_name}</span>
                              <span className="uppercase font-semibold">{(po.status || '').replace('_', ' ')}</span>
                              {po.pickup_status && !['received', 'completed'].includes(po.status) && (
                                <span className="text-wt-text-muted">· pickup: {po.pickup_status.replace(/_/g, ' ')}</span>
                              )}
                              {po.expected_delivery_date && !['received', 'completed', 'cancelled'].includes(po.status) && (
                                <span className={po.expected_delivery_date.slice(0, 10) < new Date().toISOString().slice(0, 10) ? 'text-red-400 font-semibold' : 'text-wt-text-muted'}>
                                  · due {po.expected_delivery_date.slice(0, 10)}
                                </span>
                              )}
                            </button>
                          ))}
                          {pos.length > 1 && (
                            <span className="text-wt-text-muted" data-testid={`po-progress-${order.id}`}>{doneCount}/{pos.length} POs received</span>
                          )}
                        </>);
                      })() : (
                        <>
                          <span className="inline-flex items-center px-2 py-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-400 font-semibold" data-testid={`no-po-chip-${order.id}`}>No PO</span>
                          {canCreatePO && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openCreatePODialog(order); }}
                              className="inline-flex items-center px-2 py-1 rounded-full border border-wt-gold/50 text-wt-gold hover:bg-wt-gold/10 transition-colors font-medium"
                              data-testid={`create-po-from-production-${order.id}`}
                            >
                              + Create PO
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Total Amount */}
                  <div className="mt-3 text-lg font-semibold text-wt-gold">
                    {formatCurrency(order.total_amount)}
                  </div>
                </div>
                </div>

                {/* View Details Button — full-width row on mobile, side column on desktop */}
                <div className="flex-shrink-0 w-full sm:w-auto flex flex-col gap-2">
                  {!isDriver && orderNeedsAccept(order) && (
                    <Button
                      size="sm"
                      onClick={() => handleAcceptOrder(order)}
                      disabled={acceptingIds.has(order.id)}
                      className="w-full sm:w-auto bg-green-600 hover:bg-green-500 text-white"
                      data-testid={`accept-order-${order.id}`}
                    >
                      {acceptingIds.has(order.id)
                        ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        : <CheckCircle className="h-4 w-4 mr-1" />}
                      Accept Order
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedOrder(order);
                      setDetailPanelOpen(true);
                    }}
                    className="w-full sm:w-auto border-wt-border text-wt-text-primary hover:bg-wt-subtle hover:border-wt-gold/50"
                    data-testid={`view-order-${order.id}`}
                  >
                    View Details
                  </Button>
                  {!isDriver && (() => {
                    const fs = order.fulfillment_status;
                    const done = ['delivered', 'cancelled', 'refunded', 'failed'].includes(fs) || order.status === 'cancelled';
                    const hasDriver = !!order.assigned_driver_id;
                    const hasCourier = !!(order.courier_awb || order.awb_number);
                    return (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full sm:w-auto border-wt-border text-wt-text-primary hover:bg-wt-subtle"
                            data-testid={`order-actions-${order.id}`}
                          >
                            <MoreHorizontal className="h-4 w-4 mr-1" />
                            Actions
                            <ChevronDown className="h-3 w-3 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52 bg-wt-paper border-wt-border">
                          {(isAdmin || canMarkReadyForDispatch) && ['pending', 'unfulfilled', 'packed', 'in_production', null, undefined, ''].includes(fs) && (
                            <DropdownMenuItem
                              onClick={() => markOrderReadyForDispatch(order)}
                              className="text-cyan-400 hover:bg-cyan-500/10 cursor-pointer"
                              data-testid={`card-ready-dispatch-${order.id}`}
                            >
                              <Package className="w-4 h-4 mr-2" />
                              Ready for Dispatch
                            </DropdownMenuItem>
                          )}
                          {canAssignDriver && !done && (
                            <DropdownMenuItem
                              onClick={() => { setSelectedOrder(order); setSelectedShipmentNumber(null); setAssignDialogOpen(true); }}
                              className="text-blue-400 hover:bg-blue-500/10 cursor-pointer"
                              data-testid={`card-assign-driver-${order.id}`}
                            >
                              <Truck className="w-4 h-4 mr-2" />
                              {hasDriver ? 'Reassign Driver' : 'Assign Driver'}
                            </DropdownMenuItem>
                          )}
                          {canAssignDriver && !done && hasDriver && (
                            <DropdownMenuItem
                              onClick={() => handleUnassignDriver(order)}
                              className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                              data-testid={`card-unassign-driver-${order.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Unassign Driver
                            </DropdownMenuItem>
                          )}
                          {canBookCourier && !done && !hasCourier && (
                            <DropdownMenuItem
                              onClick={() => openCourierDialog(order)}
                              className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                              data-testid={`card-book-courier-${order.id}`}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Book Courier
                            </DropdownMenuItem>
                          )}
                          {canBookCourier && !done && hasCourier && (
                            <DropdownMenuItem
                              onClick={() => handleUnassignCourier(order)}
                              className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                              data-testid={`card-unassign-courier-${order.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Unassign Courier
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator className="bg-wt-border" />
                          {canCreatePO && (
                            <DropdownMenuItem
                              onClick={() => { setSelectedOrder(order); openCreatePODialog(order); }}
                              className="text-wt-gold hover:bg-wt-gold/10 cursor-pointer"
                              data-testid={`card-create-po-${order.id}`}
                            >
                              <Package className="w-4 h-4 mr-2" />
                              Create PO
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const response = await axios.post(
                                  `${API_BASE_URL}/orders/bulk/packing-slips`,
                                  { order_ids: [order.id] },
                                  { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
                                );
                                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                                window.open(url, '_blank');
                              } catch {
                                toast.error('Failed to generate packing slip');
                              }
                            }}
                            className="text-green-400 hover:bg-green-500/10 cursor-pointer"
                            data-testid={`card-packing-slip-${order.id}`}
                          >
                            <Printer className="w-4 h-4 mr-2" />
                            Packing Slip
                          </DropdownMenuItem>
                          {isAdmin && (
                            <DropdownMenuItem
                              onClick={() => { setSelectedOrder(order); setNewStatus(''); setStatusOverrideMode(true); setStatusDialogOpen(true); }}
                              className="text-amber-400 hover:bg-amber-500/10 cursor-pointer"
                              data-testid={`card-override-status-${order.id}`}
                            >
                              <UserCog className="w-4 h-4 mr-2" />
                              Override Status
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  })()}
                  {!isDriver && !order.is_split && ['new', 'packed'].includes(getOrderTab(order)) && (
                    <Button
                      size="sm"
                      onClick={() => openProductionDialog(order, 'start')}
                      className="w-full sm:w-auto bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 border border-amber-500/30"
                      data-testid={`start-production-${order.id}`}
                    >
                      <Hammer className="h-4 w-4 mr-1" />
                      Start Production
                    </Button>
                  )}
                  {!isDriver && order.fulfillment_status === 'in_production' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleProductionComplete(order)}
                        className="w-full sm:w-auto bg-green-500/15 text-green-400 hover:bg-green-500/25 border border-green-500/30"
                        data-testid={`complete-production-${order.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Production Done
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openProductionDialog(order, 'eta')}
                        className="w-full sm:w-auto border-wt-border text-wt-text-secondary hover:text-wt-gold"
                        data-testid={`update-eta-${order.id}`}
                      >
                        Update ETA
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
          
          {/* Infinite Scroll Observer Target */}
          <div ref={observerTarget} className="h-4" />
          
          {/* Loading Indicator */}
          {loadingMore && (
            <div className="flex justify-center py-6">
              <div className="flex items-center gap-2 text-wt-text-muted">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Loading more orders...</span>
              </div>
            </div>
          )}
          
          {/* Manual Load More Button (fallback) */}
          {hasMore && !loadingMore && (
            <div className="flex justify-center py-4">
              <Button
                onClick={loadMoreOrders}
                variant="outline"
                className="border-wt-border text-wt-text-muted hover:text-wt-text-primary"
                data-testid="load-more-orders"
              >
                Load More ({orders.length} of {totalOrders})
              </Button>
            </div>
          )}
          
          {!hasMore && orders.length > 0 && (
            <div className="text-center py-4 text-wt-text-muted text-sm">
              All {totalOrders} orders loaded
            </div>
          )}
        </div>
      )}

      {/* Payment-draft link dialog */}
      <Dialog open={!!draftLinkInfo} onOpenChange={(open) => !open && setDraftLinkInfo(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="draft-link-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Draft Saved — Payment Link Ready</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-wt-text-muted">
              Draft <span className="text-wt-gold font-medium">{draftLinkInfo?.order_number}</span> saved. Share this link with the customer — the order is created automatically once they pay. Draft expires in 7 days.
            </p>
            <div className="flex items-center gap-2">
              <Input readOnly value={draftLinkInfo?.payment_url || ''} className="bg-wt-bg border-wt-border text-wt-text-primary text-xs" data-testid="draft-payment-link-input" />
              <Button
                className="bg-wt-gold hover:bg-wt-gold-hover text-black shrink-0"
                data-testid="copy-draft-link-btn"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(draftLinkInfo?.payment_url || '');
                    toast.success('Payment link copied');
                  } catch (e) {
                    toast.error('Copy failed — select the link and copy manually');
                  }
                }}
              >
                Copy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment drafts list dialog */}
      <Dialog open={draftsDialogOpen} onOpenChange={setDraftsDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl" data-testid="payment-drafts-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Payment Drafts — Awaiting Payment</DialogTitle>
          </DialogHeader>
          {loadingDrafts ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-wt-text-muted" /></div>
          ) : orderDrafts.length === 0 ? (
            <p className="text-wt-text-muted text-center py-8" data-testid="no-drafts-message">No drafts awaiting payment</p>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {orderDrafts.map((d) => (
                <div key={d.id} className="p-3 bg-wt-bg rounded-lg border border-wt-border hover:border-wt-gold/50 transition-colors" data-testid={`draft-row-${d.order_number}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div
                      className="cursor-pointer flex-1 min-w-[180px]"
                      onClick={() => setSelectedDraft(d)}
                      data-testid={`view-draft-${d.order_number}`}
                    >
                      <p className="text-sm font-medium text-wt-text-primary hover:text-wt-gold transition-colors">{d.order_number} — {d.customer_name}</p>
                      <p className="text-xs text-wt-text-muted">
                        {Number(d.total_amount || 0).toFixed(2)} · expires {d.expires_at ? new Date(d.expires_at).toLocaleDateString() : '—'} · tap to view
                      </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                        data-testid={`check-draft-payment-${d.order_number}`}
                        disabled={checkingDraftId === d.id}
                        onClick={() => checkDraftPayment(d.id)}>
                        {checkingDraftId === d.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                        Check Payment
                      </Button>
                      {d.payment_url && (
                        <Button size="sm" variant="outline" className="border-wt-border text-wt-text-primary"
                          data-testid={`copy-draft-link-${d.order_number}`}
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(d.payment_url);
                              toast.success('Payment link copied');
                            } catch (e) {
                              toast.error('Copy failed — open the link and copy manually');
                            }
                          }}>
                          Copy Link
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                        data-testid={`cancel-draft-${d.order_number}`}
                        onClick={() => cancelOrderDraft(d.id)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Draft detail dialog */}
      <Dialog open={!!selectedDraft} onOpenChange={(o) => { if (!o) setSelectedDraft(null); }}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-xl max-h-[85vh] overflow-y-auto" data-testid="draft-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Draft {selectedDraft?.order_number}</DialogTitle>
          </DialogHeader>
          {selectedDraft && (
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-2">
                <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30">Awaiting Payment</Badge>
                <span className="text-xs text-wt-text-muted">expires {selectedDraft.expires_at ? new Date(selectedDraft.expires_at).toLocaleString() : '—'}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-wt-text-muted">Customer</p>
                  <p className="text-wt-text-primary">{selectedDraft.customer_name || '—'}</p>
                  {selectedDraft.customer_phone && <p className="text-xs text-wt-text-muted">{selectedDraft.customer_phone}</p>}
                  {selectedDraft.customer_email && <p className="text-xs text-wt-text-muted">{selectedDraft.customer_email}</p>}
                </div>
                <div>
                  <p className="text-xs text-wt-text-muted">Shipping Address</p>
                  <p className="text-wt-text-primary text-xs">
                    {typeof selectedDraft.shipping_address === 'object' && selectedDraft.shipping_address
                      ? [selectedDraft.shipping_address.address1, selectedDraft.shipping_address.address2, selectedDraft.shipping_address.city, selectedDraft.shipping_address.country].filter(Boolean).join(', ')
                      : (selectedDraft.shipping_address || '—')}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs text-wt-text-muted mb-1">Items</p>
                <div className="space-y-1" data-testid="draft-items-list">
                  {(selectedDraft.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between gap-2 bg-wt-bg border border-wt-border rounded px-2 py-1.5">
                      <span className="text-wt-text-primary text-xs flex-1">{it.title || it.name}{it.sku ? ` · ${it.sku}` : ''}</span>
                      <span className="text-xs text-wt-text-muted whitespace-nowrap">{it.quantity} × {Number(it.price || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-wt-bg border border-wt-border rounded p-3 space-y-1 text-xs">
                <div className="flex justify-between text-wt-text-muted"><span>Subtotal</span><span>{Number(selectedDraft.subtotal || 0).toFixed(2)}</span></div>
                <div className="flex justify-between text-wt-text-muted"><span>Shipping</span><span>{Number(selectedDraft.shipping_cost || 0).toFixed(2)}</span></div>
                {Number(selectedDraft.discount_amount || 0) > 0 && <div className="flex justify-between text-wt-text-muted"><span>Discount</span><span>-{Number(selectedDraft.discount_amount).toFixed(2)}</span></div>}
                <div className="flex justify-between font-semibold text-wt-text-primary pt-1 border-t border-wt-border"><span>Total</span><span>{Number(selectedDraft.total_amount || 0).toFixed(2)}</span></div>
              </div>
              {selectedDraft.payment_url && (
                <div className="flex items-center gap-2 bg-wt-bg border border-wt-border rounded p-2">
                  <span className="text-xs text-wt-text-muted truncate flex-1">{selectedDraft.payment_url}</span>
                  <Button size="sm" variant="outline" className="border-wt-border text-wt-text-primary h-7 text-xs"
                    data-testid="draft-detail-copy-link-btn"
                    onClick={async () => { try { await navigator.clipboard.writeText(selectedDraft.payment_url); toast.success('Payment link copied'); } catch (e) { toast.error('Copy failed'); } }}>
                    Copy
                  </Button>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                  data-testid="draft-detail-cancel-btn"
                  onClick={() => { cancelOrderDraft(selectedDraft.id); setSelectedDraft(null); }}>
                  Cancel Draft
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="draft-detail-check-payment-btn"
                  disabled={checkingDraftId === selectedDraft.id}
                  onClick={() => checkDraftPayment(selectedDraft.id)}>
                  {checkingDraftId === selectedDraft.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                  Check Payment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Order Dialog */}
      {/* Production start / ETA dialog */}
      <Dialog open={bulkOverrideDialog.open} onOpenChange={(open) => !open && setBulkOverrideDialog(p => ({ ...p, open: false }))}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="bulk-override-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <UserCog className="h-5 w-5 text-amber-400" />
              Bulk Status Override — {selectedOrderIds.size} order(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-amber-400/90 bg-amber-500/10 border border-amber-500/30 rounded-md p-2">Admin override: all selected orders will be forced to the chosen status, regardless of their current state.</p>
            <div>
              <Label className="text-wt-text-primary">New Status *</Label>
              <Select value={bulkOverrideDialog.status} onValueChange={(v) => setBulkOverrideDialog(p => ({ ...p, status: v }))}>
                <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="bulk-override-status-select">
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {['pending', 'ready_for_dispatch', 'accepted', 'picked', 'on_the_way', 'delivered', 'failed', 'reopened'].map(status => (
                    <SelectItem key={status} value={status}>{statusConfig[status]?.label || status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {bulkOverrideDialog.status === 'failed' && (
              <div>
                <Label className="text-wt-text-primary">Failure Reason *</Label>
                <Select value={bulkOverrideDialog.failureReason} onValueChange={(v) => setBulkOverrideDialog(p => ({ ...p, failureReason: v }))}>
                  <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="bulk-override-failure-select">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border">
                    {failureReasons.map(reason => (
                      <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-wt-text-primary">Notes * <span className="text-wt-text-muted font-normal">(required for override)</span></Label>
              <Textarea
                value={bulkOverrideDialog.notes}
                onChange={(e) => setBulkOverrideDialog(p => ({ ...p, notes: e.target.value }))}
                placeholder="Reason for bulk status change..."
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
                data-testid="bulk-override-notes-input"
              />
            </div>
            <Button
              onClick={handleBulkStatusOverride}
              disabled={bulkOverrideDialog.saving}
              className="w-full bg-wt-gold hover:bg-wt-gold-hover text-black"
              data-testid="bulk-override-submit-btn"
            >
              {bulkOverrideDialog.saving ? 'Overriding…' : `Override ${selectedOrderIds.size} order(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={productionDialog.open} onOpenChange={(open) => !open && setProductionDialog(p => ({ ...p, open: false }))}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="production-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Hammer className="h-5 w-5 text-amber-400" />
              {productionDialog.mode === 'start' ? `Start Production — ${productionDialog.order?.order_number || ''}` : `Update Expected Ready Date — ${productionDialog.order?.order_number || ''}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 min-w-0 max-w-full">
            {productionDialog.mode === 'start' && productionDialog.items.length > 0 && (
              <div className="space-y-2 min-w-0" data-testid="production-items-list">
                <Label className="text-wt-text-primary">Production days per product <span className="text-wt-text-muted font-normal">(saved to the product for next time)</span></Label>
                {productionDialog.items.map((it) => (
                  <div key={it.sku} className="flex items-center gap-2">
                    <span className="flex-1 min-w-0 text-sm text-wt-text-secondary truncate block" title={it.title}>{it.title}</span>
                    <span className="shrink-0 text-sm text-wt-text-muted">×{it.quantity}</span>
                    <Input
                      type="number"
                      min="0"
                      className="w-24 shrink-0 bg-wt-bg border-wt-border text-wt-text-primary"
                      placeholder="days"
                      value={productionDialog.itemDays[it.sku] ?? (it.production_days || '')}
                      onChange={(e) => setProductionDialog(p => ({ ...p, itemDays: { ...p.itemDays, [it.sku]: e.target.value } }))}
                      data-testid={`item-days-${it.sku}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <div>
              <Label className="text-wt-text-primary">Expected ready date {productionDialog.mode === 'start' && <span className="text-wt-text-muted font-normal">(leave empty to auto-calculate from product production days)</span>}</Label>
              <DateInput
                value={productionDialog.date}
                onChange={(e) => setProductionDialog(p => ({ ...p, date: e.target.value }))}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                data-testid="production-eta-input"
              />
            </div>
            <div>
              <Label className="text-wt-text-primary">Notes <span className="text-wt-text-muted font-normal">(optional, e.g. "polish stage")</span></Label>
              <Input
                value={productionDialog.notes}
                onChange={(e) => setProductionDialog(p => ({ ...p, notes: e.target.value }))}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                data-testid="production-notes-input"
              />
            </div>
            <Button
              onClick={handleProductionSubmit}
              disabled={productionDialog.saving}
              className="w-full bg-wt-gold hover:bg-wt-gold-hover text-black"
              data-testid="production-submit-btn"
            >
              {productionDialog.saving ? 'Saving…' : productionDialog.mode === 'start' ? 'Start Production' : 'Update Date'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={createOrderDialogOpen} onOpenChange={setCreateOrderDialogOpen}>
        <DialogContent
          className="bg-wt-paper border-wt-border max-w-2xl max-h-[90vh] overflow-y-auto"
          onEscapeKeyDown={(e) => {
            if (custSuggestions.length > 0) { e.preventDefault(); dismissCustSuggestions(); }
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Create Custom Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Customer Information — first_name + last_name (Shopify
                parity). full name is derived on submit so existing
                consumers (label printing, journeys) keep working. */}
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <Label className="text-wt-text-primary">First Name *</Label>
                <Input
                  value={newOrderData.first_name}
                  onChange={(e) => {
                    setNewOrderData(prev => ({ ...prev, first_name: e.target.value }));
                    searchExistingCustomers(e.target.value);
                  }}
                  placeholder="John"
                  data-testid="create-order-first-name"
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
                {renderCustSuggestions('first_name')}
              </div>
              <div>
                <Label className="text-wt-text-primary">Last Name</Label>
                <Input
                  value={newOrderData.last_name}
                  onChange={(e) => setNewOrderData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Doe"
                  data-testid="create-order-last-name"
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>

            <div>
              <Label className="text-wt-text-primary">Phone *</Label>
              {/* Country-code selector + local digits. We strip
                  leading + / 0 on submit so the saved phone is a
                  clean E.164 string (couriers + WhatsApp need this). */}
              <div className="grid grid-cols-[140px,1fr] gap-2 mt-1">
                <Select
                  value={newOrderData.phone_country_code}
                  onValueChange={(v) => {
                    const c = COUNTRY_CODES.find(x => x.code === v);
                    setNewOrderData(prev => ({
                      ...prev,
                      phone_country_code: v,
                      // Mirror the country code into the address
                      // selector if the operator hasn't manually
                      // changed it — most orders ship to the same
                      // country as the phone number.
                      ...(prev.country_code === prev.phone_country_code
                        ? { country_code: v, country: c?.name || prev.country }
                        : {}),
                    }));
                  }}
                >
                  <SelectTrigger
                    data-testid="create-order-phone-cc"
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border max-h-72">
                    {COUNTRY_CODES.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.dial}  {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Input
                    value={newOrderData.phone_number}
                    onChange={(e) => {
                      setNewOrderData(prev => ({ ...prev, phone_number: e.target.value }));
                      searchExistingCustomers(e.target.value, 'phone');
                    }}
                    placeholder="50 123 4567"
                    data-testid="create-order-phone-number"
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                  {renderCustSuggestions('phone')}
                </div>
              </div>
            </div>

            <div className="relative">
              <Label className="text-wt-text-primary">Email</Label>
              <Input
                value={newOrderData.customer_email}
                onChange={(e) => {
                  setNewOrderData(prev => ({ ...prev, customer_email: e.target.value }));
                  searchExistingCustomers(e.target.value, 'email');
                }}
                placeholder="customer@example.com"
                data-testid="create-order-email"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
              {renderCustSuggestions('email')}
            </div>

            {/* Shipping Address — split into Shopify-shaped fields.
                Pincode/ZIP is REQUIRED because every courier
                (Delhivery / DTDC / Bluedart / Aramex) hard-rejects
                bookings without it — that was the original bug. */}
            <div className="space-y-3 p-3 bg-wt-bg/50 rounded-lg border border-wt-border">
              <Label className="text-wt-text-primary text-sm font-semibold">Shipping Address</Label>
              <div>
                <Label className="text-xs text-wt-text-muted">Address Line 1 *</Label>
                <Input
                  value={newOrderData.address1}
                  onChange={(e) => setNewOrderData(prev => ({ ...prev, address1: e.target.value }))}
                  placeholder="Street, building, apartment"
                  data-testid="create-order-address1"
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Address Line 2</Label>
                <Input
                  value={newOrderData.address2}
                  onChange={(e) => setNewOrderData(prev => ({ ...prev, address2: e.target.value }))}
                  placeholder="Landmark, floor (optional)"
                  data-testid="create-order-address2"
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-wt-text-muted">City *</Label>
                  <Input
                    value={newOrderData.city}
                    onChange={(e) => setNewOrderData(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Dubai"
                    data-testid="create-order-city"
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                <div>
                  <Label className="text-xs text-wt-text-muted">State / Emirate / Province</Label>
                  <Input
                    value={newOrderData.state}
                    onChange={(e) => setNewOrderData(prev => ({ ...prev, state: e.target.value }))}
                    placeholder="Dubai"
                    data-testid="create-order-state"
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-wt-text-muted">Country *</Label>
                  <Select
                    value={newOrderData.country_code}
                    onValueChange={(v) => {
                      const c = COUNTRY_CODES.find(x => x.code === v);
                      setNewOrderData(prev => ({
                        ...prev,
                        country_code: v,
                        country: c?.name || prev.country,
                      }));
                    }}
                  >
                    <SelectTrigger
                      data-testid="create-order-country"
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border max-h-72">
                      {COUNTRY_CODES.map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-wt-text-muted">Pincode / ZIP *</Label>
                  <Input
                    value={newOrderData.zip}
                    onChange={(e) => setNewOrderData(prev => ({ ...prev, zip: e.target.value }))}
                    placeholder="00000"
                    data-testid="create-order-zip"
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-wt-text-primary">Payment Status</Label>
                <Select 
                  value={newOrderData.payment_status} 
                  onValueChange={(v) => setNewOrderData(prev => ({ ...prev, payment_status: v }))}
                >
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="create-order-payment-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border">
                    <SelectItem value="pending">Pending (COD)</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-wt-text-primary">Order Purpose</Label>
                <Select 
                  value={newOrderData.order_purpose} 
                  onValueChange={(v) => setNewOrderData(prev => ({ ...prev, order_purpose: v }))}
                >
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="order-purpose-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border">
                    <SelectItem value="new_sale">New Sale</SelectItem>
                    <SelectItem value="replacement">Replacement</SelectItem>
                    <SelectItem value="exchange">Exchange</SelectItem>
                    <SelectItem value="warranty">Warranty</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="correction">Correction</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                  </SelectContent>
                </Select>
                {newOrderData.order_purpose !== 'new_sale' && (
                  <p className="text-xs text-amber-400 mt-1">Not incentive eligible — only New Sale orders can earn incentives</p>
                )}
              </div>
            </div>

            {/* Paid-order options: timing + method + proof (Jun 2026) */}
            {newOrderData.payment_status === 'paid' && (
              <div className="p-3 bg-wt-bg rounded-lg border border-wt-border space-y-3" data-testid="paid-options-section">
                <div>
                  <Label className="text-wt-text-primary">Payment Timing *</Label>
                  <Select
                    value={newOrderData.payment_timing}
                    onValueChange={(v) => setNewOrderData(prev => ({ ...prev, payment_timing: v }))}
                  >
                    <SelectTrigger className="mt-1 bg-wt-surface border-wt-border text-wt-text-primary" data-testid="payment-timing-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border">
                      <SelectItem value="received">Payment already received</SelectItem>
                      <SelectItem value="to_receive">Payment to be received (payment link)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newOrderData.payment_timing === 'received' ? (
                  <>
                    <div>
                      <Label className="text-wt-text-primary">Payment Method *</Label>
                      <Select
                        value={newOrderData.payment_method_id}
                        onValueChange={(v) => setNewOrderData(prev => ({ ...prev, payment_method_id: v }))}
                      >
                        <SelectTrigger className="mt-1 bg-wt-surface border-wt-border text-wt-text-primary" data-testid="payment-method-select">
                          <SelectValue placeholder={paymentMethods.filter(m => m.active !== false).length ? 'Select payment method' : 'No methods yet — add in Settings → Payment Methods'} />
                        </SelectTrigger>
                        <SelectContent className="bg-wt-paper border-wt-border">
                          {paymentMethods.filter(m => m.active !== false).map(m => (
                            <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-wt-text-primary">Payment Proof Screenshot *</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        className="mt-1 bg-wt-surface border-wt-border text-wt-text-primary file:text-wt-text-muted"
                        data-testid="payment-proof-input"
                        onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                      />
                      <p className="text-xs text-wt-text-muted mt-1">
                        Order stays "Pending Verification" (Shopify unpaid) until approved in Cash Management.
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md p-2" data-testid="draft-info-note">
                    A payment link will be generated and this order saved as a draft. The real order is created automatically once payment is received. Unpaid drafts expire after 7 days.
                  </p>
                )}
              </div>
            )}

            {/* Order Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-wt-text-primary">Order Items *</Label>
                <button
                  type="button"
                  className="text-xs text-wt-gold hover:underline mr-auto ml-3"
                  data-testid="create-order-check-availability"
                  onClick={() => {
                    const sku = newOrderData.items.find(i => i.sku)?.sku || '';
                    window.open(`/product-availability?search=${encodeURIComponent(sku)}`, '_blank');
                  }}
                >
                  Check availability →
                </button>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addOrderItem}
                  className="border-wt-border text-wt-text-primary"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>
              
              <div className="space-y-3">
                {newOrderData.items.map((item, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-wt-bg rounded-lg border border-wt-border">
                    <div className="flex-1 space-y-2">
                      {/* Product Search Row */}
                      <div className="relative">
                        <Label className="text-xs text-wt-text-muted">Search Product</Label>
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
                          <Input
                            placeholder="Search by SKU or product name..."
                            className="h-9 pl-8 bg-wt-surface border-wt-border text-wt-text-primary"
                            value={activeProductIndex === index ? productSearch : ''}
                            onChange={(e) => {
                              setProductSearch(e.target.value);
                              setActiveProductIndex(index);
                              searchProducts(e.target.value);
                            }}
                            onFocus={() => setActiveProductIndex(index)}
                          />
                        </div>
                        {/* Search Results Dropdown */}
                        {activeProductIndex === index && productSearchResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-wt-paper border border-wt-border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                            {productSearchResults.map((product) => (
                              <button
                                key={product.id || product.sku}
                                type="button"
                                className="w-full px-3 py-2 text-left hover:bg-wt-subtle flex items-center gap-2"
                                onClick={() => selectProduct(product, index)}
                              >
                                {product.image_url && (
                                  <img src={product.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-wt-text-primary truncate">{product.product_title || product.name}</div>
                                  <div className="text-xs text-wt-text-muted">SKU: {product.sku} • {formatCurrency(product.selling_price || 0)}</div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        {activeProductIndex === index && loadingProducts && (
                          <div className="absolute z-50 w-full mt-1 bg-wt-paper border border-wt-border rounded-lg p-3 text-center">
                            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                            <span className="text-sm text-wt-text-muted">Searching...</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Item Details Row */}
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <Label className="text-xs text-wt-text-muted">SKU *</Label>
                          <Input
                            value={item.sku}
                            onChange={(e) => updateOrderItem(index, 'sku', e.target.value)}
                            placeholder="SKU"
                            className="h-9 bg-wt-surface border-wt-border text-wt-text-primary"
                          />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-xs text-wt-text-muted">Title</Label>
                          <Input
                            value={item.title}
                            onChange={(e) => updateOrderItem(index, 'title', e.target.value)}
                            placeholder="Product title"
                            className="h-9 bg-wt-surface border-wt-border text-wt-text-primary"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-wt-text-muted">Qty *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateOrderItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            className="h-9 bg-wt-surface border-wt-border text-wt-text-primary"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-wt-text-muted">Price</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.price}
                            onChange={(e) => updateOrderItem(index, 'price', parseFloat(e.target.value) || 0)}
                            className="h-9 bg-wt-surface border-wt-border text-wt-text-primary"
                          />
                        </div>
                      </div>
                    </div>
                    {newOrderData.items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOrderItem(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 mt-5"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Order Total */}
              <div className="mt-3 p-3 bg-wt-surface rounded-lg border border-wt-border space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-wt-text-muted">Subtotal</span>
                  <span className="font-semibold text-wt-text-primary">
                    {formatCurrency(newOrderData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0))}
                  </span>
                </div>
                <div className="flex justify-between items-center gap-3">
                  <span className="text-wt-text-muted">Discount</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={newOrderData.discount_amount}
                    onChange={(e) => setNewOrderData(prev => ({ ...prev, discount_amount: e.target.value }))}
                    className="w-32 h-8 text-right bg-wt-bg border-wt-border text-wt-text-primary"
                    data-testid="create-order-discount"
                  />
                </div>
                <div className="flex justify-between items-center gap-3">
                  <span className="text-wt-text-muted whitespace-nowrap">Shipping / Delivery</span>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      placeholder="e.g. Delivery Charges"
                      value={newOrderData.shipping_title}
                      onChange={(e) => setNewOrderData(prev => ({ ...prev, shipping_title: e.target.value }))}
                      className="w-44 h-8 bg-wt-bg border-wt-border text-wt-text-primary"
                      data-testid="create-order-shipping-name"
                    />
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={newOrderData.shipping_cost}
                      onChange={(e) => setNewOrderData(prev => ({ ...prev, shipping_cost: e.target.value }))}
                      className="w-32 h-8 text-right bg-wt-bg border-wt-border text-wt-text-primary"
                      data-testid="create-order-shipping-amount"
                    />
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-wt-text-muted">Tax</span>
                  <div className="flex items-center gap-2">
                    <select
                      value={newOrderData.tax_inclusive ? 'incl' : 'excl'}
                      onChange={(e) => setNewOrderData(prev => ({ ...prev, tax_inclusive: e.target.value === 'incl' }))}
                      className="h-8 bg-wt-bg border border-wt-border rounded-md text-xs text-wt-text-primary px-2"
                      data-testid="create-order-tax-mode">
                      <option value="excl">Excluded — added on top</option>
                      <option value="incl">Included in prices</option>
                    </select>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={newOrderData.tax ?? ''}
                      onChange={(e) => setNewOrderData(prev => ({ ...prev, tax: e.target.value }))}
                      className="w-32 h-8 text-right bg-wt-bg border-wt-border text-wt-text-primary"
                      data-testid="create-order-tax-amount"
                    />
                  </div>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-wt-border">
                  <span className="text-wt-text-primary font-medium">Total</span>
                  <span className="text-lg font-semibold text-wt-gold" data-testid="create-order-total">
                    {formatCurrency(Math.max(0,
                      newOrderData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0) -
                      (parseFloat(newOrderData.discount_amount) || 0) +
                      (parseFloat(newOrderData.shipping_cost) || 0) +
                      (newOrderData.tax_inclusive ? 0 : (parseFloat(newOrderData.tax) || 0))
                    ))}
                  </span>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-wt-text-primary">Notes</Label>
              <Textarea
                value={newOrderData.notes}
                onChange={(e) => setNewOrderData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Order notes (internal)"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>

            {/* Push-to-Shopify toggle. Calls
                `create_shopify_order_from_local()` after the local
                insert succeeds; failure is surfaced as a toast warning
                but does NOT roll back the local order — same
                contract as the bulk-book Shopify push. */}
            <div className="flex items-center justify-between gap-3 p-3 bg-wt-bg/50 rounded-lg border border-wt-border">
              <div className="space-y-1">
                <Label htmlFor="push-to-shopify" className="text-wt-text-primary cursor-pointer">
                  Also create on Shopify
                </Label>
                <p className="text-xs text-wt-text-muted">
                  When on, the order is also created in your connected Shopify store with the same items and address.
                </p>
              </div>
              <Switch
                id="push-to-shopify"
                checked={newOrderData.push_to_shopify}
                onCheckedChange={(v) => setNewOrderData(prev => ({ ...prev, push_to_shopify: v }))}
                data-testid="create-order-push-to-shopify"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="ghost" onClick={() => setCreateOrderDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button 
                onClick={handleCreateOrder} 
                disabled={creatingOrder}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                data-testid="create-order-submit-btn"
              >
                {creatingOrder ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                {newOrderData.payment_status === 'paid' && newOrderData.payment_timing === 'to_receive' ? 'Save Draft & Create Payment Link' : 'Create Order'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Driver Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={(open) => {
        setAssignDialogOpen(open);
        if (!open) setSelectedShipmentNumber(null);
      }}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              {selectedShipmentNumber !== null 
                ? `Assign Driver to Shipment ${selectedShipmentNumber}`
                : 'Assign Driver'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-wt-text-secondary">
              Assign {selectedShipmentNumber !== null 
                ? <span className="font-semibold text-indigo-400">{selectedOrder?.order_number}-{selectedShipmentNumber}</span>
                : <span className="font-semibold text-wt-gold">{selectedOrder?.order_number}</span>
              } to a driver
            </p>
            {selectedShipmentNumber !== null && selectedOrder?.shipments && (
              <div className="bg-wt-surface/50 rounded p-2 text-xs text-wt-text-muted">
                {selectedOrder.shipments.find(s => s.shipment_number === selectedShipmentNumber)?.line_items?.map(item => 
                  `${item.title} x${item.quantity}`
                ).join(', ')}
              </div>
            )}
            <div>
              <Label className="text-wt-text-primary">Select Driver</Label>
              <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
                <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Choose a driver" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {drivers.map(driver => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setAssignDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleAssignDriver} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                Assign Driver
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reassign Driver Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Reassign Driver
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-wt-text-secondary">
              Reassign order <span className="font-semibold text-wt-gold">{selectedOrder?.order_number}</span>
            </p>
            {selectedOrder?.assigned_driver_name && (
              <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <p className="text-sm text-orange-400">
                  Currently assigned to: <span className="font-semibold">{selectedOrder.assigned_driver_name}</span>
                </p>
              </div>
            )}
            <div>
              <Label className="text-wt-text-primary">Select New Driver</Label>
              <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
                <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Choose a driver" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {drivers.filter(d => d.id !== selectedOrder?.assigned_driver_id).map(driver => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setReassignDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleReassignDriver} className="bg-orange-500 hover:bg-orange-600 text-white">
                <UserCog className="h-4 w-4 mr-2" />
                Reassign Driver
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={(open) => {
        setStatusDialogOpen(open);
        if (!open) {
          setStatusOverrideMode(false); // Reset override mode when closing
          setOverrideCourierStatus(false); // and the courier-override sub-toggle
        }
      }}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Update Order Status
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-wt-text-secondary">
              Update status for <span className="font-semibold text-wt-gold">{selectedOrder?.order_number}</span>
              <span className="ml-2 text-wt-text-muted">
                (Current: {statusConfig[selectedOrder?.fulfillment_status]?.label || selectedOrder?.fulfillment_status})
              </span>
            </p>
            
            {/* Manager Override Toggle - Only show for users with override permission */}
            {canOverrideStatus && (
              <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <Checkbox
                  id="override-mode"
                  checked={statusOverrideMode}
                  onCheckedChange={setStatusOverrideMode}
                />
                <label htmlFor="override-mode" className="text-sm text-amber-400 cursor-pointer">
                  <span className="font-semibold">Manager Override</span>
                  <span className="text-wt-text-muted ml-1">- Change to any status (for corrections)</span>
                </label>
              </div>
            )}
            
            <div>
              <Label className="text-wt-text-primary">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {selectedOrder && (statusOverrideMode 
                    ? getAllStatuses(selectedOrder.fulfillment_status)
                    : getNextStatuses(selectedOrder.fulfillment_status)
                  ).map(status => (
                    <SelectItem key={status} value={status}>
                      {statusConfig[status]?.label || status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newStatus === 'failed' && (
              <div>
                <Label className="text-wt-text-primary">Failure Reason *</Label>
                <Select value={failureReason} onValueChange={setFailureReason}>
                  <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border">
                    {failureReasons.map(reason => (
                      <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-wt-text-primary">Notes {statusOverrideMode ? '(Required for override)' : '(Optional)'}</Label>
              <Textarea
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                placeholder={statusOverrideMode ? "Reason for status change..." : "Add any delivery notes..."}
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>

            {/* Courier-status override — only visible when manager
                override is on AND the order is courier-shipped (has
                an AWB or courier_type). Lets ops push the courier
                into the right state when Delhivery's tracking API
                returns no data despite the package physically moving
                (real production case). When set to any RTO state, the
                backend ALSO drops a return doc so the order surfaces
                in the Returns tab automatically. */}
            {statusOverrideMode && selectedOrder && (selectedOrder.awb_number || selectedOrder.courier_awb || selectedOrder.courier_type) && (
              <div className="bg-wt-bg border border-wt-border rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-wt-text-primary text-sm font-semibold flex items-center gap-2">
                      <Truck className="h-4 w-4 text-orange-400" />
                      Also override courier status
                    </Label>
                    <p className="text-xs text-wt-text-muted mt-1">
                      Updates AWB <span className="font-mono">{selectedOrder.awb_number || selectedOrder.courier_awb || '—'}</span>. Use when the courier API is silent but tracking has actually moved.
                    </p>
                  </div>
                  <Switch
                    checked={overrideCourierStatus}
                    onCheckedChange={setOverrideCourierStatus}
                    data-testid="override-courier-status-toggle"
                  />
                </div>
                {overrideCourierStatus && (
                  <Select value={overrideCourierStatusValue} onValueChange={setOverrideCourierStatusValue}>
                    <SelectTrigger
                      className="bg-wt-paper border-wt-border text-wt-text-primary"
                      data-testid="override-courier-status-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border">
                      <SelectItem value="booked">Booked</SelectItem>
                      <SelectItem value="manifested">Manifested</SelectItem>
                      <SelectItem value="picked_up">Picked Up</SelectItem>
                      <SelectItem value="in_transit">In Transit</SelectItem>
                      <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="rto_initiated">RTO Initiated (returning)</SelectItem>
                      <SelectItem value="rto_in_transit">RTO In Transit</SelectItem>
                      <SelectItem value="rto_delivered">RTO Delivered (back at warehouse)</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {overrideCourierStatus && overrideCourierStatusValue.startsWith('rto_') && (
                  <div className="text-xs text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded p-2">
                    A return entry will be auto-created in the Returns tab. The order will also move to "Delivery Failed" tab.
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setStatusDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateStatus} 
                disabled={statusOverrideMode && !deliveryNotes.trim()}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                Update Status
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload/View POD Dialog */}
      <Dialog open={podDialogOpen} onOpenChange={setPodDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              {selectedOrder?.proof_of_delivery_url ? 'Proof of Delivery' : 'Upload Proof of Delivery'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-wt-text-secondary">
              {selectedOrder?.proof_of_delivery_url ? 'Delivery photo for' : 'Upload delivery photo for'} <span className="font-semibold text-wt-gold">{selectedOrder?.order_number}</span>
            </p>
            
            {/* Show existing POD if available */}
            {selectedOrder?.proof_of_delivery_url && (
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Current POD Image</Label>
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}${selectedOrder.proof_of_delivery_url}`}
                  alt="Proof of Delivery"
                  className="w-full max-h-96 object-contain rounded-lg border border-wt-border cursor-pointer"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${selectedOrder.proof_of_delivery_url}`, '_blank')}
                />
              </div>
            )}
            
            <div>
              <Label className="text-wt-text-primary">
                {selectedOrder?.proof_of_delivery_url ? 'Replace with new photo' : 'Photo'}
              </Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setPodFile(e.target.files[0])}
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setPodDialogOpen(false)} className="text-wt-text-secondary">
                Close
              </Button>
              {podFile && (
                <Button 
                  onClick={handleUploadPOD} 
                  disabled={uploading}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  {uploading ? 'Uploading...' : 'Upload Photo'}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Details Panel - Shopify-style slide-over */}
      {/* Image Preview Lightbox */}
      <Dialog open={!!imagePreview} onOpenChange={(open) => !open && setImagePreview(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl p-4" data-testid="image-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary text-base truncate pr-8">{imagePreview?.title}</DialogTitle>
          </DialogHeader>
          {imagePreview && (
            <img
              src={imagePreview.url}
              alt={imagePreview.title}
              className="w-full max-h-[70vh] object-contain rounded-lg bg-wt-bg"
              data-testid="image-preview-large"
            />
          )}
        </DialogContent>
      </Dialog>

      <OrderDetailPanel
        open={detailPanelOpen}
        onClose={() => setDetailPanelOpen(false)}
        order={selectedOrder}
        formatCurrency={formatCurrency}
        onRefresh={(updatedOrder) => {
          setOrders(orders.map(o => o.id === updatedOrder.id ? updatedOrder : o));
          setSelectedOrder(updatedOrder);
        }}
        onAssignDriver={(order) => {
          setAssignDialogOpen(true);
        }}
        onAssignDriverToShipment={(order, shipmentNumber) => {
          setSelectedShipmentNumber(shipmentNumber);
          setAssignDialogOpen(true);
        }}
        onBookCourier={(order) => {
          openCourierDialog(order);
        }}
        onBookCourierForShipment={(order, shipmentNumber) => {
          setSelectedShipmentNumber(shipmentNumber);
          openCourierDialog(order);
        }}
        onUnassignCourier={handleUnassignCourier}
        onUnassignDriver={handleUnassignDriver}
        onReopen={(order) => {
          setReopenDialogOpen(true);
        }}
        onReturnReplacement={(order) => {
          setReturnReplacementDialogOpen(true);
        }}
        onRefund={(order) => {
          setRefundDialogOpen(true);
        }}
        onViewAuditLog={(order) => {
          setAuditLogDialogOpen(true);
        }}
        onCreatePO={(order) => {
          openCreatePODialog(order);
        }}
        onOverrideStatus={(order) => {
          setNewStatus('');
          setStatusOverrideMode(true);
          setStatusDialogOpen(true);
        }}
        onViewPOD={(order) => {
          setPodDialogOpen(true);
        }}
        isAdmin={isAdmin}
        canAssignDriver={isAdmin || canAssignDriver}
        canBookCourier={canBookCourier}
        canManageReturns={canManageReturns}
        canManageRefunds={canManageRefunds}
        canSyncShopify={canSyncShopify}
        canOverrideStatus={canOverrideStatus}
        canCreatePaymentLink={canCreatePaymentLink}
        canRequestIncentive={canRequestIncentive}
        canMarkReadyForDispatch={canMarkReadyForDispatch}
        canCreateInvoice={canCreateInvoice}
        canCreatePO={canCreatePO}
        canEditOrder={canEditOrder}
        canCancelOrder={canCancelOrder}
      />

      {/* Cash Collection Dialog for COD Orders */}
      <Dialog open={cashDialogOpen} onOpenChange={setCashDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-wt-gold" />
              Cash Collection Required
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                <p className="text-wt-text-primary">
                  This is a <span className="font-semibold text-orange-400">Cash on Delivery</span> order. 
                  Please confirm cash collection before marking as delivered.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-wt-text-secondary">Order Amount</Label>
                  <p className="text-2xl font-bold text-wt-gold">{formatCurrency(selectedOrder.total_amount)}</p>
                </div>

                <div>
                  <Label htmlFor="cashAmount" className="text-wt-text-secondary">Cash Collected (can be partial)</Label>
                  <Input
                    id="cashAmount"
                    type="number"
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                    className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                    placeholder={selectedOrder.total_amount?.toString()}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cashConfirm"
                    checked={cashCollected}
                    onCheckedChange={setCashCollected}
                    className="border-wt-gold data-[state=checked]:bg-wt-gold"
                  />
                  <label htmlFor="cashConfirm" className="text-sm text-wt-text-primary cursor-pointer">
                    I confirm that cash has been collected from the customer
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setCashDialogOpen(false);
                    setCashCollected(false);
                  }} 
                  className="text-wt-text-secondary"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCashConfirmed}
                  disabled={!cashCollected}
                  className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                >
                  Confirm & Mark Delivered
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tags Dialog */}
      <Dialog open={tagsDialogOpen} onOpenChange={setTagsDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl flex items-center gap-2">
              <Tag className="h-6 w-6 text-wt-gold" />
              Manage Order Tags
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <p className="text-sm text-wt-text-secondary">
                Add or remove tags for order <span className="font-semibold text-wt-gold">{selectedOrder.order_number}</span>
              </p>

              {/* Current Tags */}
              <div className="flex flex-wrap gap-2 min-h-[32px]">
                {orderTags.map((tag, idx) => (
                  <span 
                    key={idx} 
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-wt-gold/20 text-wt-gold"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-red-400 ml-1"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {orderTags.length === 0 && (
                  <span className="text-xs text-wt-text-muted">No tags added</span>
                )}
              </div>

              {/* Add New Tag */}
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Enter tag name..."
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                />
                <Button
                  onClick={handleAddTag}
                  variant="outline"
                  className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
                >
                  Add
                </Button>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  variant="ghost" 
                  onClick={() => setTagsDialogOpen(false)} 
                  className="text-wt-text-secondary"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveTags}
                  className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                >
                  Save Tags
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reopen Order Dialog */}
      <ReopenOrderDialog
        open={reopenDialogOpen}
        onClose={() => setReopenDialogOpen(false)}
        order={selectedOrder}
        onSuccess={(updatedOrder) => {
          setOrders(orders.map(o => o.id === updatedOrder.id ? updatedOrder : o));
          loadOrders();
        }}
      />

      {/* Return/Replacement Dialog */}
      <ReturnReplacementDialog
        open={returnReplacementDialogOpen}
        onClose={() => setReturnReplacementDialogOpen(false)}
        order={selectedOrder}
        onSuccess={(updatedOrder) => {
          setOrders(orders.map(o => o.id === updatedOrder.id ? updatedOrder : o));
          loadOrders();
        }}
      />

      {/* Refund Dialog */}
      <RefundDialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
        order={selectedOrder}
        formatCurrency={formatCurrency}
        onSuccess={(updatedOrder) => {
          setOrders(orders.map(o => o.id === updatedOrder.id ? updatedOrder : o));
        }}
      />

      {/* Audit Log Dialog */}
      <AuditLogDialog
        open={auditLogDialogOpen}
        onClose={() => setAuditLogDialogOpen(false)}
        order={selectedOrder}
      />

      {/* Bulk Assign Driver Dialog */}
      <Dialog open={showBulkDriverDialog} onOpenChange={setShowBulkDriverDialog}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Users className="h-5 w-5 text-wt-gold" />
              Bulk Assign Driver
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-wt-text-secondary">
              Assign <span className="text-wt-gold font-semibold">{selectedOrderIds.size}</span> orders to a driver
            </p>
            
            <div>
              <Label className="text-wt-text-primary">Select Driver</Label>
              <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
                <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Choose a driver..." />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {drivers.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.full_name || driver.name || driver.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                variant="ghost" 
                onClick={() => {
                  setShowBulkDriverDialog(false);
                  setSelectedDriverId('');
                }}
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleBulkAssignDriver}
                disabled={!selectedDriverId || bulkActionLoading}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                {bulkActionLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Users className="h-4 w-4 mr-2" />
                )}
                Assign to {selectedOrderIds.size} Orders
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Book Courier Dialog */}
      <Dialog open={courierDialogOpen} onOpenChange={(open) => {
        setCourierDialogOpen(open);
        if (!open) setSelectedShipmentNumber(null);
      }}>
        <DialogContent className="bg-wt-paper border-wt-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Truck className="h-5 w-5 text-red-400" />
              {selectedShipmentNumber !== null 
                ? `Book Courier for Shipment ${selectedShipmentNumber}`
                : 'Book Courier'}
            </DialogTitle>
          </DialogHeader>
          
          {!selectedOrder ? (
            <div className="flex flex-col items-center justify-center py-8 text-wt-text-muted">
              <Loader2 className="h-8 w-8 animate-spin mb-4" />
              <p>Loading order details...</p>
            </div>
          ) : (
            <div className="space-y-4 pt-4">
              <div className="bg-wt-surface rounded-lg p-4 border border-wt-border">
                <div className="text-sm text-wt-text-muted mb-1">Order</div>
                <div className="font-semibold text-wt-gold">
                  {selectedShipmentNumber !== null 
                    ? `${selectedOrder.order_number}-${selectedShipmentNumber}`
                    : selectedOrder.order_number}
                </div>
                <div className="text-sm text-wt-text-secondary mt-1">{selectedOrder.customer_name}</div>
                {selectedShipmentNumber !== null && selectedOrder.shipments && (
                  <div className="text-xs text-indigo-400 mt-1">
                    {selectedOrder.shipments.find(s => s.shipment_number === selectedShipmentNumber)?.line_items?.map(item => 
                      `${item.title} x${item.quantity}`
                    ).join(', ')}
                  </div>
                )}
                <div className="text-xs text-wt-text-muted mt-1">
                  {typeof selectedOrder.shipping_address === 'string' 
                    ? selectedOrder.shipping_address 
                    : `${selectedOrder.shipping_address?.city || ''} ${selectedOrder.shipping_address?.zip || ''}`}
                </div>
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Courier Service</Label>
                <Select 
                  value={selectedCourierType || undefined} 
                  onValueChange={setSelectedCourierType}
                >
                  <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue placeholder="Select courier service" />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border z-50">
                    {courierIntegrations.length === 0 ? (
                      <SelectItem value="__none__" disabled>
                        No courier integrations configured
                      </SelectItem>
                    ) : (
                      courierIntegrations.map((integration) => (
                        <SelectItem key={integration.id} value={integration.id}>
                          {integration.name || (integration.courier_type ? `${integration.courier_type.charAt(0).toUpperCase() + integration.courier_type.slice(1)} Account` : 'Courier Account')}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {courierIntegrations.length === 0 && (
                  <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    No courier integrations configured.{' '}
                    <a href="/integrations" className="text-wt-gold hover:underline">Add integration</a>
                  </p>
                )}
              </div>
              
              {/* Manual AWB Input for Custom Couriers with no available AWBs */}
              {(() => {
                const selectedIntegration = courierIntegrations.find(i => i.id === selectedCourierType);
                const isCustomCourier = selectedIntegration?.courier_type === 'custom';
                const noAwbsAvailable = customCourierAwbCount === 0;
                
                if (isCustomCourier) {
                  return (
                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-purple-400 mb-3">
                        <Hash className="h-4 w-4" />
                        <span className="font-medium">Custom Courier AWB</span>
                      </div>
                      
                      {noAwbsAvailable ? (
                        <>
                          <p className="text-xs text-purple-300 mb-3">
                            No pre-loaded AWBs available. Enter AWB number manually:
                          </p>
                          <Input
                            value={manualAwb}
                            onChange={(e) => setManualAwb(e.target.value)}
                            placeholder="Enter AWB number"
                            className="bg-wt-bg border-wt-border text-wt-text-primary font-mono"
                          />
                        </>
                      ) : (
                        <p className="text-xs text-purple-300">
                          <span className="text-purple-400 font-semibold">{customCourierAwbCount}</span> AWBs available. 
                          Next AWB will be auto-assigned.
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
              
              <div>
                <Label className="text-wt-text-primary">Pickup Date (Optional)</Label>
                <DateInput
                  value={courierPickupDate}
                  onChange={(e) => setCourierPickupDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
                />
                <p className="text-xs text-wt-text-muted mt-1">
                  Leave empty for next available pickup slot
                </p>
              </div>
              
              {selectedOrder.payment_status !== 'paid' && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-400">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="font-medium">COD Order</span>
                  </div>
                  <p className="text-xs text-amber-300 mt-1">
                    This order will be marked as Cash on Delivery
                  </p>
                </div>
              )}
              
              {/* Package Dimensions */}
              <div className="border border-wt-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-wt-text-primary flex items-center gap-2">
                    <Package className="h-4 w-4 text-wt-gold" />
                    Package Dimensions
                  </Label>
                  {loadingPackageInfo && (
                    <Loader2 className="h-4 w-4 animate-spin text-wt-gold" />
                  )}
                </div>
                
                {/* Warning for missing packages */}
                {packageInfo && (packageInfo.is_using_defaults || packageInfo.items_without_package?.length > 0) && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-3">
                    <div className="flex items-center gap-2 text-amber-400 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="font-medium">Package dimensions not set for products</span>
                    </div>
                    {packageInfo.items_without_package?.length > 0 && (
                      <ul className="text-xs text-amber-300 mt-2 space-y-1 ml-6">
                        {packageInfo.items_without_package.slice(0, 3).map((item, i) => (
                          <li key={i}>• {item.product_title} {item.sku ? `(${item.sku})` : ''}</li>
                        ))}
                        {packageInfo.items_without_package.length > 3 && (
                          <li>• ... and {packageInfo.items_without_package.length - 3} more</li>
                        )}
                      </ul>
                    )}
                    <p className="text-xs text-amber-300 mt-2">
                      Select a package below or enter dimensions manually.{' '}
                      <a href="/inventory" className="text-wt-gold hover:underline">Assign packages to products</a>
                    </p>
                  </div>
                )}
                
                {/* Package products info */}
                {packageInfo?.product_packages?.length > 0 && (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 mb-3">
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <CheckCircle className="h-4 w-4" />
                      <span className="font-medium">Dimensions from product packages</span>
                    </div>
                    <ul className="text-xs text-green-300 mt-2 space-y-1 ml-6">
                      {packageInfo.product_packages.slice(0, 3).map((item, i) => (
                        <li key={i}>• {item.product_title}: {item.package_name}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Quick package selection */}
                {availablePackages.length > 0 && (
                  <div className="mb-3">
                    <Label className="text-xs text-wt-text-muted mb-1 block">Quick Select Package</Label>
                    <Select value={selectedPackageId || "__manual__"} onValueChange={(val) => val === "__manual__" ? setSelectedPackageId('') : applyPackageDimensions(val)}>
                      <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue placeholder="Choose a predefined package" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border">
                        <SelectItem value="__manual__">-- Manual Entry --</SelectItem>
                        {availablePackages.map((pkg) => (
                          <SelectItem key={pkg.id} value={pkg.id}>
                            {pkg.name} ({pkg.length}×{pkg.width}×{pkg.height} cm, {pkg.weight} {pkg.weight_unit || 'kg'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs text-wt-text-muted">Length (cm)</Label>
                    <Input
                      type="number"
                      value={packageDimensions.length}
                      onChange={(e) => { setPackageDimensions(prev => ({ ...prev, length: e.target.value })); setSelectedPackageId(''); }}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-wt-text-muted">Width (cm)</Label>
                    <Input
                      type="number"
                      value={packageDimensions.width}
                      onChange={(e) => { setPackageDimensions(prev => ({ ...prev, width: e.target.value })); setSelectedPackageId(''); }}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-wt-text-muted">Height (cm)</Label>
                    <Input
                      type="number"
                      value={packageDimensions.height}
                      onChange={(e) => { setPackageDimensions(prev => ({ ...prev, height: e.target.value })); setSelectedPackageId(''); }}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-wt-text-muted">Weight (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={packageDimensions.weight}
                      onChange={(e) => { setPackageDimensions(prev => ({ ...prev, weight: e.target.value })); setSelectedPackageId(''); }}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="ghost"
                  onClick={() => setCourierDialogOpen(false)}
                  className="text-wt-text-secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBookCourier}
                  disabled={bookingCourier}
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  {bookingCourier ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Truck className="h-4 w-4 mr-2" />
                  )}
                  Book Courier
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Book Courier Dialog */}
      <Dialog open={showBulkCourierDialog} onOpenChange={setShowBulkCourierDialog}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md max-h-[90vh] flex flex-col" data-testid="bulk-courier-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Truck className="h-5 w-5 text-red-400" />
              Bulk Book Courier
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4 overflow-y-auto flex-1 pr-2">
            <div className="bg-wt-surface rounded-lg p-4 border border-wt-border">
              <div className="text-sm text-wt-text-muted mb-1">Selected Orders</div>
              <div className="font-semibold text-wt-gold text-lg">{selectedOrderIds.size} orders</div>
              <p className="text-xs text-wt-text-muted mt-1">
                Orders with existing courier assignments will be skipped
              </p>
            </div>
            
            <div>
              <Label className="text-wt-text-primary">Courier Service</Label>
              <Select 
                value={selectedCourierType || undefined} 
                onValueChange={setSelectedCourierType}
              >
                <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Select courier service" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border z-50">
                  {courierIntegrations.length === 0 ? (
                    <SelectItem value="__none__" disabled>
                      No courier integrations configured
                    </SelectItem>
                  ) : (
                    courierIntegrations.map((integration) => (
                      <SelectItem key={integration.id} value={integration.id}>
                        {integration.name || (integration.courier_type ? `${integration.courier_type.charAt(0).toUpperCase() + integration.courier_type.slice(1)} Account` : 'Courier Account')}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {courierIntegrations.length === 0 && (
                <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  No courier integrations configured.{' '}
                  <a href="/integrations" className="text-wt-gold hover:underline">Add integration</a>
                </p>
              )}
            </div>
            
            <div>
              <Label className="text-wt-text-primary">Pickup Date (Optional)</Label>
              <DateInput
                value={courierPickupDate}
                onChange={(e) => setCourierPickupDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
              />
              <p className="text-xs text-wt-text-muted mt-1">
                Leave empty for next available pickup slot
              </p>
            </div>
            
            {/* Package Dimensions */}
            <div className="border border-wt-border rounded-lg p-4">
              <Label className="text-wt-text-primary flex items-center gap-2 mb-3">
                <Package className="h-4 w-4 text-wt-gold" />
                Default Package Dimensions
              </Label>
              
              {/* Quick package selection */}
              {availablePackages.length > 0 && (
                <div className="mb-3">
                  <Label className="text-xs text-wt-text-muted mb-1 block">Quick Select Package</Label>
                  <Select value={selectedPackageId || "__manual__"} onValueChange={(val) => val === "__manual__" ? setSelectedPackageId('') : applyPackageDimensions(val)}>
                    <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                      <SelectValue placeholder="Choose a predefined package" />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-surface border-wt-border z-[100]">
                      <SelectItem value="__manual__">-- Manual Entry --</SelectItem>
                      {availablePackages.map((pkg) => (
                        <SelectItem key={pkg.id} value={pkg.id}>
                          {pkg.name} ({pkg.length}×{pkg.width}×{pkg.height} cm, {pkg.weight} {pkg.weight_unit || 'kg'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label className="text-xs text-wt-text-muted">Length (cm)</Label>
                  <Input
                    type="number"
                    value={packageDimensions.length}
                    onChange={(e) => { setPackageDimensions(prev => ({ ...prev, length: e.target.value })); setSelectedPackageId(''); }}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-wt-text-muted">Width (cm)</Label>
                  <Input
                    type="number"
                    value={packageDimensions.width}
                    onChange={(e) => { setPackageDimensions(prev => ({ ...prev, width: e.target.value })); setSelectedPackageId(''); }}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-wt-text-muted">Height (cm)</Label>
                  <Input
                    type="number"
                    value={packageDimensions.height}
                    onChange={(e) => { setPackageDimensions(prev => ({ ...prev, height: e.target.value })); setSelectedPackageId(''); }}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-wt-text-muted">Weight (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={packageDimensions.weight}
                    onChange={(e) => { setPackageDimensions(prev => ({ ...prev, weight: e.target.value })); setSelectedPackageId(''); }}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-wt-text-muted mt-2">
                These dimensions will be used for all selected orders
              </p>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-amber-400">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">Note</span>
              </div>
              <p className="text-xs text-amber-300 mt-1">
                Unpaid orders will be marked as COD. Orders already assigned to a courier will be skipped.
              </p>
            </div>
          </div>
            
          <div className="flex justify-end gap-3 pt-4 flex-shrink-0 border-t border-wt-border mt-4">
            <Button
              variant="ghost"
              onClick={() => setShowBulkCourierDialog(false)}
              className="text-wt-text-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkBookCourier}
              disabled={bulkActionLoading}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {bulkActionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Truck className="h-4 w-4 mr-2" />
              )}
              Book {selectedOrderIds.size} Orders
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create PO from Order Dialog */}
      <Dialog open={createPODialogOpen} onOpenChange={setCreatePODialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-po-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-wt-gold" />
              Create Purchase Order from Order
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-5 pt-4">
              {/* Top Section - Order Info and Open POs side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Order Info */}
                <div className="p-4 bg-wt-subtle rounded-lg">
                  {poMultiOrders.length > 1 ? (
                    <>
                      <p className="text-xs text-wt-text-muted uppercase tracking-wide">Orders ({poMultiOrders.length})</p>
                      <p className="text-sm text-wt-gold font-semibold break-words" data-testid="po-multi-order-numbers">
                        {poMultiOrders.map(o => o.order_number).join(', ')}
                      </p>
                      <p className="text-xs text-wt-text-muted mt-1">One PO will be created and linked to all selected orders</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-wt-text-muted uppercase tracking-wide">Order</p>
                      <p className="text-lg text-wt-gold font-semibold">{selectedOrder.order_number}</p>
                      <p className="text-sm text-wt-text-primary mt-1">{selectedOrder.customer_name}</p>
                    </>
                  )}
                </div>

                {/* Right Column - Vendor Open POs or Linked POs */}
                {poFormData.vendor_id && vendorOpenPOs.length > 0 ? (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-xs text-green-400 font-medium uppercase tracking-wide mb-3">
                      Open POs from Vendor ({vendorOpenPOs.length})
                    </p>
                    <p className="text-xs text-wt-text-muted mb-3">
                      Select a PO to add items instead of creating new
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {vendorOpenPOs.map(po => (
                        <div 
                          key={po.id} 
                          onClick={() => {
                            setSelectedExistingPO(po);
                            setAddToExistingPO(true);
                          }}
                          className={`p-3 rounded-lg cursor-pointer transition-all ${
                            selectedExistingPO?.id === po.id 
                              ? 'bg-green-500/20 border-2 border-green-500' 
                              : 'bg-wt-subtle border border-transparent hover:border-green-500/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="existing-po"
                                checked={selectedExistingPO?.id === po.id}
                                onChange={() => {
                                  setSelectedExistingPO(po);
                                  setAddToExistingPO(true);
                                }}
                                className="w-4 h-4 text-green-500 focus:ring-green-500 border-green-500"
                              />
                              <div>
                                <p className="text-wt-text-primary font-medium">{po.po_number}</p>
                                <p className="text-xs text-wt-text-muted">
                                  {po.items?.length || 0} items
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge className={`text-xs ${
                                po.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                po.status === 'ordered' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {po.status}
                              </Badge>
                              <p className="text-xs text-wt-gold mt-1">
                                {formatCurrency(po.total_amount || 0)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {selectedExistingPO && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="mt-2 text-wt-text-muted"
                        onClick={() => {
                          setSelectedExistingPO(null);
                          setAddToExistingPO(false);
                        }}
                      >
                        Clear selection (create new PO)
                      </Button>
                    )}
                  </div>
                ) : linkedPOs.length > 0 ? (
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-xs text-blue-400 font-medium uppercase tracking-wide mb-2">Existing POs for this Order</p>
                    <div className="space-y-1">
                      {linkedPOs.map(po => (
                        <div key={po.id} className="flex items-center justify-between text-sm">
                          <span className="text-wt-text-primary">{po.po_number}</span>
                          <Badge className={`text-xs ${po.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                            {po.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : poFormData.vendor_id ? (
                  <div className="p-4 bg-wt-subtle rounded-lg border border-wt-border">
                    <p className="text-xs text-wt-text-muted uppercase tracking-wide mb-2">Vendor Open POs</p>
                    <p className="text-sm text-wt-text-secondary">No open POs from this vendor</p>
                    <p className="text-xs text-wt-text-muted mt-1">A new PO will be created</p>
                  </div>
                ) : (
                  <div className="p-4 bg-wt-subtle rounded-lg border border-dashed border-wt-border">
                    <p className="text-xs text-wt-text-muted uppercase tracking-wide mb-2">Vendor Open POs</p>
                    <p className="text-sm text-wt-text-secondary">Select a vendor to see open POs</p>
                  </div>
                )}
              </div>

              {/* Form Fields Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Vendor Selection */}
                <div>
                  <Label className="text-wt-text-primary text-sm">Select Vendor *</Label>
                  <Select 
                    value={poFormData.vendor_id} 
                    onValueChange={handleVendorChange}
                  >
                    <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                      <SelectValue placeholder="Select a vendor" />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border">
                      {vendors.map(vendor => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-wt-gold" />
                            {vendor.vendor_name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* PO Type - Only show when creating new PO */}
                {!addToExistingPO && (
                  <div>
                    <Label className="text-wt-text-primary text-sm">PO Type</Label>
                    <Select 
                      value={poFormData.po_type} 
                      onValueChange={(v) => setPOFormData({...poFormData, po_type: v})}
                    >
                      <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-paper border-wt-border">
                        <SelectItem value="made_to_order">Made to Order</SelectItem>
                        <SelectItem value="stock_refill">Stock Refill</SelectItem>
                        <SelectItem value="replacement">Replacement</SelectItem>
                        <SelectItem value="raw_material">Raw Material</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Expected Delivery - Only show when creating new PO */}
                {!addToExistingPO && (
                  <div>
                    <Label className="text-wt-text-primary text-sm">Expected Delivery</Label>
                    <DateInput
                      value={poFormData.expected_delivery_date}
                      onChange={(e) => setPOFormData({...poFormData, expected_delivery_date: e.target.value})}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary [color-scheme:dark]"
                    />
                  </div>
                )}

                {/* Fulfillment Method - Only show when creating new PO */}
                {!addToExistingPO && (
                  <div>
                    <Label className="text-wt-text-primary text-sm">Fulfillment Method</Label>
                    <Select
                      value={poFormData.fulfillment_method || 'vendor_delivers'}
                      onValueChange={(v) => setPOFormData({...poFormData, fulfillment_method: v})}
                    >
                      <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border">
                        <SelectItem value="vendor_delivers">
                          <span className="flex items-center gap-2">
                            Ships to Warehouse (Vendor Delivers)
                          </span>
                        </SelectItem>
                        <SelectItem value="wooden_twist_pickup">
                          <span className="flex items-center gap-2">
                            Pickup from Vendor
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Tax Settings */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="tax_inclusive"
                      checked={poFormData.tax_inclusive}
                      onCheckedChange={(checked) => setPOFormData({...poFormData, tax_inclusive: checked})}
                    />
                    <Label htmlFor="tax_inclusive" className="text-wt-text-primary text-sm cursor-pointer">
                      Tax Inclusive
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-wt-text-secondary text-sm">Tax Rate:</Label>
                    <Select
                      value={String(poFormData.tax_rate || 5)}
                      onValueChange={(value) => setPOFormData({...poFormData, tax_rate: parseInt(value)})}
                    >
                      <SelectTrigger className="w-24 border-wt-border bg-wt-surface text-wt-text-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border">
                        <SelectItem value="0" className="text-wt-text-primary">0%</SelectItem>
                        <SelectItem value="5" className="text-wt-text-primary">5%</SelectItem>
                        <SelectItem value="12" className="text-wt-text-primary">12%</SelectItem>
                        <SelectItem value="18" className="text-wt-text-primary">18%</SelectItem>
                        <SelectItem value="28" className="text-wt-text-primary">28%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Select Items */}
              <div>
                <Label className="text-wt-text-primary text-sm mb-2 block">Select Items for PO</Label>
                {loadingVendorOptions && (
                  <p className="text-xs text-wt-text-muted mb-2 flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading vendor cost comparison…
                  </p>
                )}
                <div className="space-y-2 max-h-72 overflow-y-auto border border-wt-border rounded-lg p-3 bg-wt-bg/30">
                  {poFormData.selected_items.map((item, index) => (
                    <div key={index} className={`p-3 rounded-lg transition-all ${item.selected ? 'bg-wt-gold/10 border border-wt-gold/30' : 'bg-wt-subtle border border-transparent'}`}>
                      <div className="flex items-center gap-4">
                        <Checkbox
                          checked={item.selected}
                          onCheckedChange={() => toggleItemSelection(index)}
                          className="data-[state=checked]:bg-wt-gold data-[state=checked]:border-wt-gold"
                        />
                        {/* Product Image */}
                        {item.image_url ? (
                          <ImageLightbox 
                            src={item.image_url} 
                            alt={item.product_title}
                            className="w-16 h-16 flex-shrink-0"
                            thumbnailClassName="w-16 h-16 object-cover rounded-lg border border-wt-border"
                          />
                        ) : (
                          <div className="w-16 h-16 flex-shrink-0 bg-wt-bg rounded-lg border border-wt-border flex items-center justify-center">
                            <Package className="w-6 h-6 text-wt-text-muted" />
                          </div>
                        )}
                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-wt-text-primary font-medium text-sm line-clamp-2">{item.product_title}</p>
                          {item.variant_title && (
                            <p className="text-xs text-wt-text-muted mt-0.5">{item.variant_title}</p>
                          )}
                          <p className="text-xs text-wt-gold font-mono mt-1">SKU: {item.sku || 'N/A'}</p>
                          {/* Quick vendor-compare toggle. Shows count of
                              alternative vendors and current warehouse stock
                              so the buyer can decide at a glance whether
                              they even need to buy. */}
                          {item.sku && (
                            <div className="mt-1.5 flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => setExpandedVendorRows(prev => ({ ...prev, [item.sku]: !prev[item.sku] }))}
                                className="text-xs font-medium text-wt-gold hover:underline flex items-center gap-1"
                                data-testid={`toggle-vendor-compare-${item.sku}`}
                              >
                                <Building2 className="h-3 w-3" />
                                {expandedVendorRows[item.sku] ? 'Hide' : 'Compare'} vendors
                                {' '}({(vendorOptionsBySku[item.sku] || []).length})
                              </button>
                              <span className="text-xs text-wt-text-muted">
                                Stock: <span className={`font-semibold ${(stockBySku[item.sku] || 0) > 0 ? 'text-green-500' : 'text-red-500'}`}>{stockBySku[item.sku] ?? 0}</span>
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Quantity & Cost Inputs */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="w-20">
                            <p className="text-xs text-wt-text-muted mb-1">Qty</p>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItemQuantity(index, e.target.value)}
                              className="bg-wt-bg border-wt-border text-wt-text-primary text-center text-sm h-9"
                              disabled={!item.selected}
                            />
                          </div>
                          <div className="w-28">
                            <p className="text-xs text-wt-text-muted mb-1">Unit Cost</p>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_cost}
                              onChange={(e) => updateItemCost(index, e.target.value)}
                              className="bg-wt-bg border-wt-border text-wt-text-primary text-center text-sm h-9"
                              placeholder="0.00"
                              disabled={!item.selected}
                            />
                          </div>
                        </div>
                      </div>
                      {/* Vendor cost comparison table — expandable per item.
                          Picking a vendor row here drives the entire PO's
                          vendor selection (single-vendor PO model) and
                          updates this item's unit cost immediately. */}
                      {item.sku && expandedVendorRows[item.sku] && (
                        <div className="mt-3 ml-10 border border-wt-border rounded-md bg-wt-bg/50 overflow-hidden" data-testid={`vendor-compare-${item.sku}`}>
                          {(vendorOptionsBySku[item.sku] || []).length === 0 ? (
                            <div className="p-3 text-xs text-wt-text-muted text-center">
                              No vendor mappings found for this SKU yet. Add vendors from the product catalog.
                            </div>
                          ) : (
                            <table className="w-full text-xs">
                              <thead className="bg-wt-subtle">
                                <tr className="text-wt-text-muted">
                                  <th className="text-left px-3 py-2 font-medium">Vendor</th>
                                  <th className="text-right px-3 py-2 font-medium">Unit Cost</th>
                                  <th className="text-right px-3 py-2 font-medium">Last PO</th>
                                  <th className="text-right px-3 py-2 font-medium">Lead</th>
                                  <th className="text-right px-3 py-2 font-medium">MOQ</th>
                                  <th className="text-right px-3 py-2 font-medium">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(vendorOptionsBySku[item.sku] || []).map((vp, vi) => {
                                  const isLowest = vi === 0; // sorted asc by cost in backend
                                  const isSelectedVendor = poFormData.vendor_id === vp.vendor_id;
                                  return (
                                    <tr
                                      key={vp.vendor_id || vi}
                                      className={`border-t border-wt-border ${isSelectedVendor ? 'bg-wt-gold/10' : ''}`}
                                    >
                                      <td className="px-3 py-2 text-wt-text-primary">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{vp.vendor_name || 'Unnamed'}</span>
                                          {vp.is_primary && (
                                            <Badge className="text-[10px] px-1.5 py-0 bg-blue-500/20 text-blue-500">PRIMARY</Badge>
                                          )}
                                          {isLowest && (vendorOptionsBySku[item.sku] || []).length > 1 && (
                                            <Badge className="text-[10px] px-1.5 py-0 bg-green-500/20 text-green-500">LOWEST</Badge>
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-3 py-2 text-right text-wt-text-primary font-semibold">
                                        {formatCurrency(vp.cost_price || 0)}
                                      </td>
                                      <td className="px-3 py-2 text-right text-wt-text-muted">
                                        {vp.last_cost ? (
                                          <span title={vp.last_po_number || ''}>
                                            {formatCurrency(vp.last_cost)}
                                            {vp.last_po_number && (
                                              <span className="block text-[10px]">{vp.last_po_number}</span>
                                            )}
                                          </span>
                                        ) : '—'}
                                      </td>
                                      <td className="px-3 py-2 text-right text-wt-text-muted">
                                        {vp.lead_time_days ? `${vp.lead_time_days}d` : '—'}
                                      </td>
                                      <td className="px-3 py-2 text-right text-wt-text-muted">
                                        {vp.moq || 1}
                                      </td>
                                      <td className="px-3 py-2 text-right">
                                        <Button
                                          size="sm"
                                          variant={isSelectedVendor ? 'default' : 'outline'}
                                          className={`h-7 text-xs ${isSelectedVendor ? 'bg-wt-gold text-black hover:bg-wt-gold-hover' : 'border-wt-border text-wt-text-primary hover:bg-wt-gold/10'}`}
                                          onClick={() => handleVendorChange(vp.vendor_id)}
                                          data-testid={`pick-vendor-${item.sku}-${vp.vendor_id}`}
                                        >
                                          {isSelectedVendor ? 'Selected' : 'Pick'}
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Vendor Notes */}
              <div>
                <Label className="text-wt-text-primary text-sm">Notes for Vendor</Label>
                <Textarea
                  value={poFormData.vendor_notes}
                  onChange={(e) => setPOFormData({...poFormData, vendor_notes: e.target.value})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  rows={2}
                  placeholder="Special instructions for the vendor..."
                />
              </div>

              {/* Summary & Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-wt-border">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-xs text-wt-text-muted">Selected Items</p>
                    <p className="text-lg font-semibold text-wt-text-primary">
                      {poFormData.selected_items.filter(i => i.selected).length} <span className="text-sm font-normal text-wt-text-muted">of {poFormData.selected_items.length}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-wt-text-muted">Estimated Total</p>
                    <p className="text-lg font-semibold text-wt-gold">
                      {formatCurrency(
                        poFormData.selected_items
                          .filter(i => i.selected)
                          .reduce((sum, i) => sum + (i.quantity * (parseFloat(i.unit_cost) || 0)), 0)
                      )}
                    </p>
                  </div>
                  {addToExistingPO && selectedExistingPO && (
                    <div>
                      <p className="text-xs text-green-400">Adding to</p>
                      <p className="text-sm font-semibold text-green-400">{selectedExistingPO.po_number}</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button 
                    variant="ghost" 
                    onClick={() => setCreatePODialogOpen(false)} 
                    className="text-wt-text-secondary"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreatePO}
                    disabled={creatingPO || !poFormData.vendor_id || (addToExistingPO && !selectedExistingPO)}
                    className={`px-6 ${addToExistingPO ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-wt-gold hover:bg-wt-gold-hover text-black'}`}
                    data-testid="confirm-create-po"
                  >
                    {creatingPO ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {addToExistingPO ? 'Adding...' : 'Creating...'}</>
                    ) : addToExistingPO ? (
                      <><Plus className="h-4 w-4 mr-2" /> Add to {selectedExistingPO?.po_number || 'PO'}</>
                    ) : (
                      <><Plus className="h-4 w-4 mr-2" /> Create New PO</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Booking Results Dialog */}
      <Dialog open={bookingResultsOpen} onOpenChange={setBookingResultsOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Courier Booking Complete
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex items-center justify-between p-3 bg-wt-bg rounded-lg">
              <span className="text-wt-text-muted">
                {bookingResults.filter(r => r.success).length} of {bookingResults.length} orders booked successfully
              </span>
              {bookingResults.some(r => r.success) && (
                <Button
                  size="sm"
                  onClick={downloadAllLabels}
                  className="bg-wt-gold text-wt-bg hover:bg-wt-gold/90"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download All Labels
                </Button>
              )}
            </div>
            
            {/* Results List */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {bookingResults.map((result, idx) => (
                <div 
                  key={idx} 
                  className={`p-3 rounded-lg border ${
                    result.success 
                      ? 'border-green-500/30 bg-green-500/10' 
                      : 'border-red-500/30 bg-red-500/10'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {result.success ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                      <span className="font-medium text-wt-text-primary">
                        {result.order_number}
                      </span>
                    </div>
                    {result.success && result.awb && (
                      <Badge variant="outline" className="bg-wt-bg text-wt-text-primary border-wt-border">
                        AWB: {result.awb}
                      </Badge>
                    )}
                  </div>
                  
                  {result.success && result.shipment_id && (
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadShippingLabel(result)}
                        disabled={downloadingLabel === result.order_id}
                        className="border-wt-border text-wt-text-primary"
                      >
                        {downloadingLabel === result.order_id ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <Download className="h-3 w-3 mr-1" />
                        )}
                        Download Label
                      </Button>
                    </div>
                  )}
                  
                  {!result.success && result.error && (
                    <p className="mt-1 text-sm text-red-400">{result.error}</p>
                  )}
                </div>
              ))}
            </div>
            
            {/* Close Button */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={() => {
                  setBookingResultsOpen(false);
                  setBookingResults([]);
                }}
                className="bg-wt-gold text-wt-bg hover:bg-wt-gold/90"
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </PullToRefresh>
    </>
  );
};

export default OrdersPage;
